import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LabelList
} from 'recharts';
import { getRecords, getQuestions, saveQuestions, getPostTestQuestions, savePostTestQuestions, getAdmins, saveAdmin, getInstructorMap, saveInstructorMap, deleteRecord, subscribeQuestions, subscribePostTestQuestions, subscribeInstructorMap, getMandatoryTrainingRecords, subscribeMandatoryTrainingRecords, deleteMandatoryTrainingRecord, subscribeMandatorySettings, saveMandatorySettings, syncAllQuestionsToFirebase, DEFAULT_MANDATORY_SETTINGS } from '../services/storageService';
import { InductionRecord, Question, UserRole, PostTestQuestion, AdminAccount, InstructorMap, MandatoryTrainingRecord, MandatorySettings } from '../types';
import { AREAS, SAFETY_INDUCTION_PDF_URL } from '../constants';
import { MANDATORY_MODULES } from '../constantsMandatory';
import { Trash2, Plus, CreditCard as Edit2, LogOut, Download, Filter, XCircle, Shield, Eye, RefreshCw, Users, UserPlus, Search, MapPin, User, Save, Clock, AlertTriangle, AlertCircle, Calendar, Menu, X, CheckCircle, Loader2, GraduationCap, BookOpen, Award, Sliders, ToggleLeft, ToggleRight, Settings, FileText, Upload, Link, HelpCircle, FileUp, FileCheck, Pencil, ExternalLink } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';
import { generateInductionPDF, generateMandatoryTrainingPDF, getInductionPDFDataUri, getMandatoryTrainingPDFDataUri, generateMTMatrixPDF } from '../services/pdfService';
import { PdfViewer } from './PdfViewer';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

export const getInductionExpiryStatus = (inductionDateStr: string) => {
  if (!inductionDateStr) return { daysLeft: 0, status: 'expired' as const, expiryDateStr: '' };
  
  const inductionDate = new Date(inductionDateStr);
  const expiryDate = new Date(inductionDate);
  expiryDate.setFullYear(expiryDate.getFullYear() + 1); // 1 Year validity
  
  const today = new Date();
  
  // Set both to midnight to keep full days calculation consistent
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const expiryMidnight = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
  
  const msDiff = expiryMidnight.getTime() - todayMidnight.getTime();
  const daysLeft = Math.ceil(msDiff / (1000 * 60 * 60 * 24));
  
  let status: 'valid' | 'expiring_soon' | 'expired' = 'valid';
  if (daysLeft < 0) {
    status = 'expired';
  } else if (daysLeft <= 30) {
    status = 'expiring_soon';
  }
  
  return {
    daysLeft,
    status,
    expiryDateStr: expiryDate.toISOString().split('T')[0]
  };
};

interface AdminDashboardProps {
  onLogout: () => void;
  role: UserRole;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout, role }) => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'history' | 'mandatory_history' | 'mandatory_settings' | 'questions' | 'posttest' | 'admins' | 'instructors' | 'expiring'>('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [records, setRecords] = useState<InductionRecord[]>([]);
  const [mandatoryRecords, setMandatoryRecords] = useState<MandatoryTrainingRecord[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [postTestQuestions, setPostTestQuestions] = useState<PostTestQuestion[]>([]);
  const [loading, setLoading] = useState(true);

  // Mandatory Training Settings State
  const [mandatorySettings, setMandatorySettings] = useState<MandatorySettings>(DEFAULT_MANDATORY_SETTINGS);
  const [mandatorySettingsSaving, setMandatorySettingsSaving] = useState<boolean>(false);
  const [mandatorySettingsSuccess, setMandatorySettingsSuccess] = useState<string>('');
  const [mandatorySettingsError, setMandatorySettingsError] = useState<string>('');
  const [newMTSettingWorkArea, setNewMTSettingWorkArea] = useState<string>('');

  // Mandatory Question & PDF Management State
  const [mtSelectedQuestionModule, setMtSelectedQuestionModule] = useState<string>('jsa');
  const [mtQuestionModalOpen, setMtQuestionModalOpen] = useState<boolean>(false);
  const [mtEditingQuestionIndex, setMtEditingQuestionIndex] = useState<number | null>(null);
  const [mtQuestionForm, setMtQuestionForm] = useState<{
    id?: string;
    text: string;
    textEn: string;
    options: string[];
    correctOptionIndex: number;
    points: number;
  }>({
    text: '',
    textEn: '',
    options: ['', '', '', ''],
    correctOptionIndex: 0,
    points: 20
  });
  const [mtPdfPreviewUrl, setMtPdfPreviewUrl] = useState<string | null>(null);
  const [mtPdfPreviewModuleTitle, setMtPdfPreviewModuleTitle] = useState<string>('');
  const [isSyncingQuestions, setIsSyncingQuestions] = useState(false);
  const [syncSuccessMessage, setSyncSuccessMessage] = useState<string | null>(null);

  // Status Pop-up Modal State for Saving Operations
  const [saveStatusModal, setSaveStatusModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
    errorCode?: string;
  }>({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });

  const showSaveSuccess = (title: string, message: string) => {
    setSaveStatusModal({
      isOpen: true,
      type: 'success',
      title,
      message
    });
  };

  const showSaveError = (title: string, err: any) => {
    const errorCode = err?.code || err?.name || 'FIREBASE_ERROR';
    const errorMessage = err?.message || String(err);
    setSaveStatusModal({
      isOpen: true,
      type: 'error',
      title,
      message: errorMessage,
      errorCode
    });
  };

  // Certificate Format Preview Modal State
  const [certPreviewModalOpen, setCertPreviewModalOpen] = useState<boolean>(false);
  const [certPreviewType, setCertPreviewType] = useState<'INDUCTION' | 'MANDATORY'>('INDUCTION');
  const [certPreviewDataUri, setCertPreviewDataUri] = useState<string>('');
  const [certPreviewLoading, setCertPreviewLoading] = useState<boolean>(false);
  const [certPreviewRecord, setCertPreviewRecord] = useState<InductionRecord | MandatoryTrainingRecord | null>(null);

  const openCertificatePreview = async (
    type: 'INDUCTION' | 'MANDATORY',
    record?: InductionRecord | MandatoryTrainingRecord
  ) => {
    setCertPreviewModalOpen(true);
    setCertPreviewType(type);
    setCertPreviewLoading(true);
    setCertPreviewDataUri('');

    try {
      let targetRecord: InductionRecord | MandatoryTrainingRecord;
      if (record) {
        targetRecord = record;
      } else if (type === 'INDUCTION') {
        targetRecord = {
          id: 'SAMPLE-IND-2026',
          fullName: 'Budi Santoso (Sample)',
          company: 'PT United Tractors Tbk',
          jobTitle: 'Senior Field Engineer',
          area: 'Yard Sukapura',
          instructor: 'Agus Setiawan (EHS)',
          inductionDate: new Date().toISOString().split('T')[0],
          postTestScore: 95,
          postTestPassed: true,
          answers: {},
          visitorType: 'MEKANIK_VENDOR'
        };
      } else {
        targetRecord = {
          id: 'SAMPLE-MT-2026',
          fullName: 'Eko Prasetyo (Sample)',
          nik: '317101987654321',
          company: 'PT United Tractors Tbk',
          position: 'Technician Leader',
          workArea: 'Yard Sukapura',
          moduleId: 'jsa',
          moduleTitle: 'Job Safety Analysis (JSA)',
          score: 100,
          passed: true,
          completionDate: new Date().toISOString().split('T')[0],
          answers: {}
        };
      }

      setCertPreviewRecord(targetRecord);

      let uri = '';
      if (type === 'INDUCTION') {
        uri = await getInductionPDFDataUri(targetRecord as InductionRecord);
      } else {
        uri = await getMandatoryTrainingPDFDataUri(targetRecord as MandatoryTrainingRecord);
      }
      setCertPreviewDataUri(uri);
    } catch (err) {
      console.error('Failed to generate certificate preview Data URI:', err);
    } finally {
      setCertPreviewLoading(false);
    }
  };

  // Mandatory Training Filters
  const [mtFilterModule, setMtFilterModule] = useState<string>('all');
  const [mtFilterStatus, setMtFilterStatus] = useState<string>('all');
  const [mtFilterArea, setMtFilterArea] = useState<string>('all');
  const [mtFilterSearch, setMtFilterSearch] = useState<string>('');
  const [mtFilterStartDate, setMtFilterStartDate] = useState<string>('');
  const [mtFilterEndDate, setMtFilterEndDate] = useState<string>('');
  const [mtPersonSearch, setMtPersonSearch] = useState<string>('');
  const [mtPersonSortBy, setMtPersonSortBy] = useState<'passed' | 'unpassed' | 'score' | 'name'>('passed');
  const [mtPersonStatusFilter, setMtPersonStatusFilter] = useState<string>('all');
  const [mtPersonAreaFilter, setMtPersonAreaFilter] = useState<string>('all');
  const [showAllPersonMatrix, setShowAllPersonMatrix] = useState<boolean>(false);
  const [moduleBreakdownModal, setModuleBreakdownModal] = useState<{ moduleId: string; moduleTitle: string; moduleBadge: string } | null>(null);
  const [deleteConfirmMTObj, setDeleteConfirmMTObj] = useState<{ id: string; fullName: string; moduleTitle: string } | null>(null);
  const [deleteConfirmMTLoading, setDeleteConfirmMTLoading] = useState<boolean>(false);

  // Available unique work areas for Safety Mandatory Training
  const mtAvailableAreas = React.useMemo(() => {
    const set = new Set<string>();
    if (mandatorySettings?.allowedWorkAreas) {
      mandatorySettings.allowedWorkAreas.forEach(a => { if (a && a.trim()) set.add(a.trim()); });
    }
    mandatoryRecords.forEach(r => {
      if (r.workArea && r.workArea.trim()) set.add(r.workArea.trim());
    });
    ['Yard Sukapura', 'Jababeka', 'Cakung'].forEach(a => set.add(a));
    return Array.from(set).sort();
  }, [mandatorySettings, mandatoryRecords]);
  
  // Admins state
  const [adminsList, setAdminsList] = useState<AdminAccount[]>([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [newAdminRole, setNewAdminRole] = useState<UserRole>('EDITOR');
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');

  // Instructors Map State
  const [instructorMap, setInstructorMap] = useState<InstructorMap>({});
  const [areasList, setAreasList] = useState<string[]>([]);
  const [selectedConfigArea, setSelectedConfigArea] = useState<string>('');
  const [newAreaInput, setNewAreaInput] = useState<string>('');
  const [newInstructorInput, setNewInstructorInput] = useState<string>('');
  const [instructorMapSaving, setInstructorMapSaving] = useState<boolean>(false);
  const [instructorConfigSuccess, setInstructorConfigSuccess] = useState<string>('');
  const [instructorConfigError, setInstructorConfigError] = useState<string>('');
  const [editingInstructorIndex, setEditingInstructorIndex] = useState<number | null>(null);
  const [editingInstructorName, setEditingInstructorName] = useState<string>('');

  // Filters
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterMinScore, setFilterMinScore] = useState<string>('');
  const [filterMaxScore, setFilterMaxScore] = useState<string>('');

  // Expiry List Filters
  const [expirFilterSelect, setExpirFilterSelect] = useState<'all' | 'soon' | 'expired'>('soon');
  const [expirSearchQuery, setExpirSearchQuery] = useState('');
  const [copyFeedbackId, setCopyFeedbackId] = useState<string>('');

  // Delete record modal confirmation state
  const [deleteConfirmRecordObj, setDeleteConfirmRecordObj] = useState<{ id: string; fullName: string } | null>(null);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  // Participant details modal state
  const [selectedRecordForDetails, setSelectedRecordForDetails] = useState<InductionRecord | null>(null);

  // Question Editing State
  const [editingQ, setEditingQ] = useState<Question | null>(null);
  const [newQText, setNewQText] = useState('');
  const [newQTextEn, setNewQTextEn] = useState('');

  // Post-Test Editing State
  const [editingPT, setEditingPT] = useState<PostTestQuestion | null>(null);
  const [newPTText, setNewPTText] = useState('');
  const [newPTTextEn, setNewPTTextEn] = useState('');
  const [newPTOptions, setNewPTOptions] = useState<string[]>(['', '', '', '']);
  const [newPTOptionsEn, setNewPTOptionsEn] = useState<string[]>(['', '', '', '']);
  const [newPTCorrectIndex, setNewPTCorrectIndex] = useState<number>(0);
  const [newPTPoints, setNewPTPoints] = useState<number>(10);
  const [newPTImageUrl, setNewPTImageUrl] = useState<string>('');
  const [newPTOptionImages, setNewPTOptionImages] = useState<string[]>(['', '', '', '']);

  const fetchData = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const [fetchedRecords, fetchedAdmins, fetchedMT] = await Promise.all([
        getRecords(forceRefresh),
        getAdmins(forceRefresh),
        getMandatoryTrainingRecords(forceRefresh)
      ]);
      setRecords(fetchedRecords);
      setAdminsList(fetchedAdmins);
      setMandatoryRecords(fetchedMT);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 1. Subscribe to questions list
    const unsubQuestions = subscribeQuestions((q) => {
      setQuestions(q);
    });

    // 2. Subscribe to post-test questions
    const unsubPostTest = subscribePostTestQuestions((pt) => {
      setPostTestQuestions(pt);
    });

    // 3. Subscribe to instructor configuration map
    const unsubInstructorMap = subscribeInstructorMap((instructorMap) => {
      if (instructorMap) {
        setInstructorMap(instructorMap);
        const areas = Object.keys(instructorMap);
        setAreasList(areas);
        setSelectedConfigArea(prev => prev || (areas.length > 0 ? areas[0] : ''));
      }
    });

    // 4. Subscribe to mandatory training records
    const unsubMT = subscribeMandatoryTrainingRecords((mtRecs) => {
      setMandatoryRecords(mtRecs);
    });

    // 5. Subscribe to mandatory training settings
    const unsubMTSettings = subscribeMandatorySettings((st) => {
      if (st) {
        setMandatorySettings(st);
      }
    });

    // 6. Fetch initial records and admins list
    fetchData();

    return () => {
      unsubQuestions();
      unsubPostTest();
      unsubInstructorMap();
      unsubMT();
      unsubMTSettings();
    };
  }, []);

  // Dashboard Specific Date Filters
  const [dashFilterSelect, setDashFilterSelect] = useState<'all' | 'today' | 'current_month' | 'custom'>('all');
  const [dashStartDate, setDashStartDate] = useState('');
  const [dashEndDate, setDashEndDate] = useState('');

  const getFilteredDashboardRecords = () => {
    const todayStrUTC = new Date().toISOString().split('T')[0];
    const todayStrLocal = new Date().toLocaleDateString('en-CA');
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');

    return records.filter(record => {
      let matches = true;
      if (dashFilterSelect === 'today') {
        matches = record.inductionDate === todayStrUTC || record.inductionDate === todayStrLocal;
      } else if (dashFilterSelect === 'current_month') {
        matches = record.inductionDate.startsWith(`${year}-${month}`);
      } else if (dashFilterSelect === 'custom') {
        if (dashStartDate) {
          matches = matches && record.inductionDate >= dashStartDate;
        }
        if (dashEndDate) {
          matches = matches && record.inductionDate <= dashEndDate;
        }
      }
      return matches;
    });
  };

  // --- Statistics Logic ---
  const getStats = (recordsForStats: InductionRecord[]) => {
    const total = recordsForStats.length;
    
    // Group by Area
    const areaStats = recordsForStats.reduce((acc, curr) => {
      acc[curr.area] = (acc[curr.area] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const areaData = Object.keys(areaStats).map(key => ({ name: key, value: areaStats[key] }));

    // Group by Date
    const dailyDataMap: Record<string, number> = {};
    recordsForStats.forEach(r => {
      const date = r.inductionDate; // YYYY-MM-DD
      dailyDataMap[date] = (dailyDataMap[date] || 0) + 1;
    });
    const dailyData = Object.keys(dailyDataMap).sort().slice(-7).map(date => ({
      name: date,
      count: dailyDataMap[date]
    }));

    // Group by Date for Tamu vs Vendor
    const dailyDetailMap: Record<string, { Tamu: number; Vendor: number }> = {};
    recordsForStats.forEach(r => {
      const date = r.inductionDate; // YYYY-MM-DD
      if (!dailyDetailMap[date]) {
        dailyDetailMap[date] = { Tamu: 0, Vendor: 0 };
      }
      if (r.visitorType === 'TAMU') {
        dailyDetailMap[date].Tamu += 1;
      } else {
        dailyDetailMap[date].Vendor += 1;
      }
    });
    
    const dailyTamuVendorData = Object.keys(dailyDetailMap).sort().slice(-7).map(date => ({
      name: date,
      'Kunjungan Tamu / Guest': dailyDetailMap[date].Tamu,
      'Mekanik / Vendor / Pekerja Risiko Tinggi': dailyDetailMap[date].Vendor,
      'Total': dailyDetailMap[date].Tamu + dailyDetailMap[date].Vendor
    }));

    // Visitor Type Counts
    const tamuCount = recordsForStats.filter(r => r.visitorType === 'TAMU').length;
    const vendorCount = recordsForStats.filter(r => r.visitorType !== 'TAMU').length;
    const visitorTypeData = [
      { name: 'Kunjungan Tamu / Guest', value: tamuCount },
      { name: 'Mekanik / Vendor / Pekerja Risiko Tinggi', value: vendorCount }
    ];

    // Post Test Metrics
    const recordsWithPostTest = recordsForStats.filter(r => r.postTestScore !== undefined);
    const passedCount = recordsWithPostTest.filter(r => r.postTestPassed === true).length;
    const failedCount = recordsWithPostTest.filter(r => r.postTestPassed === false).length;
    const avgScore = recordsWithPostTest.length > 0
      ? Math.round(recordsWithPostTest.reduce((acc, curr) => acc + (curr.postTestScore || 0), 0) / recordsWithPostTest.length)
      : 0;

    return { total, areaData, dailyData, dailyTamuVendorData, visitorTypeData, tamuCount, vendorCount, passedCount, failedCount, avgScore };
  };

  const dashboardRecords = getFilteredDashboardRecords();
  const stats = getStats(dashboardRecords);

  // Dashboard Program Mode Switcher (All Programs / Safety Mandatory / Safety Induction)
  const [dashProgramView, setDashProgramView] = useState<'all' | 'mandatory' | 'induction'>('all');

  // Dashboard Safety Mandatory Date Filtering & Stats
  const getFilteredDashboardMTRecords = () => {
    const todayStrUTC = new Date().toISOString().split('T')[0];
    const todayStrLocal = new Date().toLocaleDateString('en-CA');
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');

    return mandatoryRecords.filter(r => {
      const recDate = r.completionDate || (r.timestamp ? new Date(r.timestamp).toLocaleDateString('en-CA') : '');
      let matches = true;
      if (dashFilterSelect === 'today') {
        matches = recDate === todayStrUTC || recDate === todayStrLocal;
      } else if (dashFilterSelect === 'current_month') {
        matches = recDate.startsWith(`${year}-${month}`);
      } else if (dashFilterSelect === 'custom') {
        if (dashStartDate) {
          matches = matches && (recDate ? recDate >= dashStartDate : true);
        }
        if (dashEndDate) {
          matches = matches && (recDate ? recDate <= dashEndDate : true);
        }
      }
      return matches;
    });
  };

  const getMTStats = (recordsForStats: MandatoryTrainingRecord[]) => {
    const total = recordsForStats.length;
    const passedCount = recordsForStats.filter(r => r.passed === true).length;
    const failedCount = recordsForStats.filter(r => r.passed === false).length;
    const avgScore = total > 0 ? Math.round(recordsForStats.reduce((acc, curr) => acc + (curr.score || 0), 0) / total) : 0;
    const passRate = total > 0 ? Math.round((passedCount / total) * 100) : 0;

    // Unique participants
    const uniquePersonsMap = new Map<string, { name: string; nik: string; passedModules: Set<string>; totalAttempts: number }>();
    recordsForStats.forEach(r => {
      const key = (r.nik && r.nik.trim()) ? r.nik.trim() : (r.fullName || '').trim().toLowerCase();
      if (!uniquePersonsMap.has(key)) {
        uniquePersonsMap.set(key, {
          name: r.fullName,
          nik: r.nik || '-',
          passedModules: new Set(),
          totalAttempts: 0
        });
      }
      const p = uniquePersonsMap.get(key)!;
      p.totalAttempts += 1;
      if (r.passed) {
        p.passedModules.add(r.moduleId);
      }
    });

    const uniquePersonsCount = uniquePersonsMap.size;
    const perfectCount = Array.from(uniquePersonsMap.values()).filter(p => p.passedModules.size >= MANDATORY_MODULES.length).length;

    // Module breakdown for 7 modules
    const moduleStats = MANDATORY_MODULES.map(m => {
      const mRecs = recordsForStats.filter(r => r.moduleId === m.id);
      const mTotal = mRecs.length;
      const mPassed = mRecs.filter(r => r.passed).length;
      const mFailed = mTotal - mPassed;
      const mPassRate = mTotal > 0 ? Math.round((mPassed / mTotal) * 100) : 0;
      const mAvgScore = mTotal > 0 ? Math.round(mRecs.reduce((acc, curr) => acc + (curr.score || 0), 0) / mTotal) : 0;
      return {
        id: m.id,
        badge: m.badge,
        title: m.title,
        total: mTotal,
        passed: mPassed,
        failed: mFailed,
        passRate: mPassRate,
        avgScore: mAvgScore
      };
    });

    // Area breakdown for MT
    const areaStatsMap: Record<string, number> = {};
    recordsForStats.forEach(r => {
      const area = (r.workArea && r.workArea.trim()) ? r.workArea.trim() : 'Lainnya / Umum';
      areaStatsMap[area] = (areaStatsMap[area] || 0) + 1;
    });
    const areaData = Object.keys(areaStatsMap).map(key => ({ name: key, value: areaStatsMap[key] })).sort((a,b) => b.value - a.value);

    // Daily MT trend (last 7 days active)
    const dailyMTMap: Record<string, { passed: number; failed: number; total: number }> = {};
    recordsForStats.forEach(r => {
      const recDate = r.completionDate || (r.timestamp ? new Date(r.timestamp).toLocaleDateString('en-CA') : '');
      if (recDate) {
        if (!dailyMTMap[recDate]) dailyMTMap[recDate] = { passed: 0, failed: 0, total: 0 };
        dailyMTMap[recDate].total += 1;
        if (r.passed) dailyMTMap[recDate].passed += 1;
        else dailyMTMap[recDate].failed += 1;
      }
    });
    const dailyMTData = Object.keys(dailyMTMap).sort().slice(-7).map(date => ({
      name: date,
      'Lulus': dailyMTMap[date].passed,
      'Belum Lulus': dailyMTMap[date].failed,
      'Total': dailyMTMap[date].total
    }));

    // Today specific MT counts
    const todayStrUTC = new Date().toISOString().split('T')[0];
    const todayStrLocal = new Date().toLocaleDateString('en-CA');
    const todayMTRecords = mandatoryRecords.filter(r => {
      const d = r.completionDate || (r.timestamp ? new Date(r.timestamp).toLocaleDateString('en-CA') : '');
      return d === todayStrUTC || d === todayStrLocal;
    });
    const todayPersonsCount = new Set(todayMTRecords.map(r => (r.nik && r.nik.trim()) ? r.nik.trim() : r.fullName.trim().toLowerCase())).size;

    // Recent 5 MT submissions sorted by timestamp or date
    const recentMTRecords = [...recordsForStats].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)).slice(0, 5);

    return {
      total,
      passedCount,
      failedCount,
      avgScore,
      passRate,
      uniquePersonsCount,
      perfectCount,
      moduleStats,
      areaData,
      dailyMTData,
      todayRecordsCount: todayMTRecords.length,
      todayPersonsCount,
      recentMTRecords
    };
  };

  const dashboardMTRecords = getFilteredDashboardMTRecords();
  const mtStats = getMTStats(dashboardMTRecords);

  // Expiration calculation logic
  const processedRecordsWithExpiry = records.map(record => {
    const expiryInfo = getInductionExpiryStatus(record.inductionDate);
    return {
      ...record,
      expiryInfo
    };
  });

  const expiringSoonRecords = processedRecordsWithExpiry.filter(item => item.expiryInfo.status === 'expiring_soon');
  const expiredRecords = processedRecordsWithExpiry.filter(item => item.expiryInfo.status === 'expired');
  const totalExpiringSoon = expiringSoonRecords.length;
  const totalExpired = expiredRecords.length;

  // --- Filter Logic ---
  const getFilteredRecords = () => {
    return records.filter(record => {
      const matchesArea = filterArea ? record.area === filterArea : true;
      
      let matchesDate = true;
      if (filterStartDate) {
        matchesDate = matchesDate && record.inductionDate >= filterStartDate;
      }
      if (filterEndDate) {
        matchesDate = matchesDate && record.inductionDate <= filterEndDate;
      }

      let matchesSearch = true;
      if (filterSearch) {
        const query = filterSearch.toLowerCase();
        matchesSearch = (
          record.fullName.toLowerCase().includes(query) ||
          (record.jobTitle || '').toLowerCase().includes(query) ||
          (record.company || '').toLowerCase().includes(query) ||
          (record.area || '').toLowerCase().includes(query) ||
          (record.instructor || '').toLowerCase().includes(query)
        );
      }

      let matchesScore = true;
      if (filterMinScore !== '' || filterMaxScore !== '') {
        const score = record.postTestScore;
        if (score === undefined) {
          matchesScore = false;
        } else {
          if (filterMinScore !== '') {
            matchesScore = matchesScore && score >= Number(filterMinScore);
          }
          if (filterMaxScore !== '') {
            matchesScore = matchesScore && score <= Number(filterMaxScore);
          }
        }
      }
      
      return matchesArea && matchesDate && matchesSearch && matchesScore;
    });
  };

  const filteredRecords = getFilteredRecords();

  // --- Question Management ---
  const handleAddQuestion = async () => {
    if (role !== 'EDITOR') return;
    if (!newQText.trim()) return;
    const newQ = { id: uuidv4(), text: newQText, textEn: newQTextEn };
    const updated = [...questions, newQ];
    setQuestions(updated);
    await saveQuestions(updated);
    setNewQText('');
    setNewQTextEn('');
  };

  const handleDeleteQuestion = async (id: string) => {
    if (role !== 'EDITOR') return;
    if (!confirm('Are you sure you want to delete this question?')) return;
    const updated = questions.filter(q => q.id !== id);
    setQuestions(updated);
    await saveQuestions(updated);
  };

  const handleEditQuestion = (q: Question) => {
    if (role !== 'EDITOR') return;
    setEditingQ(q);
    setNewQText(q.text);
    setNewQTextEn(q.textEn || '');
  };

  const handleUpdateQuestion = async () => {
    if (role !== 'EDITOR') return;
    if (!editingQ || !newQText.trim()) return;
    const updated = questions.map(q => q.id === editingQ.id ? { ...q, text: newQText, textEn: newQTextEn } : q);
    setQuestions(updated);
    await saveQuestions(updated);
    setEditingQ(null);
    setNewQText('');
    setNewQTextEn('');
  };

  // Helper to convert direct image file uploads to compact Base64
  const processImageFile = (e: React.ChangeEvent<HTMLInputElement>, callback: (base64: string) => void) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert("Gambar terlalu besar! Maksimum 2 MB.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          callback(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // --- Post-Test Question Management ---
  const handleAddPostTestQuestion = async () => {
    if (role !== 'EDITOR') return;
    if (!newPTText.trim()) return;

    if (newPTOptions.some(opt => !opt.trim())) {
      alert("Mohon isi semua (4) opsi pilihan jawaban.");
      return;
    }

    const newPT: PostTestQuestion = {
      id: uuidv4(),
      text: newPTText,
      options: [...newPTOptions],
      correctOptionIndex: newPTCorrectIndex,
      points: Number(newPTPoints) || 0
    };
    if (newPTTextEn.trim()) newPT.textEn = newPTTextEn.trim();
    if (newPTOptionsEn.some(o => !!o.trim())) newPT.optionsEn = [...newPTOptionsEn];
    if (newPTImageUrl?.trim()) newPT.imageUrl = newPTImageUrl.trim();
    if (newPTOptionImages.some(img => !!img)) newPT.optionImages = [...newPTOptionImages];

    const updated = [...postTestQuestions, newPT];
    setPostTestQuestions(updated);
    await savePostTestQuestions(updated);

    setNewPTText('');
    setNewPTTextEn('');
    setNewPTOptions(['', '', '', '']);
    setNewPTOptionsEn(['', '', '', '']);
    setNewPTCorrectIndex(0);
    setNewPTPoints(10);
    setNewPTImageUrl('');
    setNewPTOptionImages(['', '', '', '']);
  };

  const handleDeletePostTestQuestion = async (id: string) => {
    if (role !== 'EDITOR') return;
    if (!confirm('Apakah Anda yakin ingin menghapus pertanyaan post-test ini?')) return;
    const updated = postTestQuestions.filter(q => q.id !== id);
    setPostTestQuestions(updated);
    await savePostTestQuestions(updated);
  };

  const handleEditPostTestQuestion = (q: PostTestQuestion) => {
    if (role !== 'EDITOR') return;
    setEditingPT(q);
    setNewPTText(q.text);
    setNewPTTextEn(q.textEn || '');
    setNewPTOptions([...q.options]);
    setNewPTOptionsEn(q.optionsEn ? [...q.optionsEn] : ['', '', '', '']);
    setNewPTCorrectIndex(q.correctOptionIndex);
    setNewPTPoints(q.points);
    setNewPTImageUrl(q.imageUrl || '');
    setNewPTOptionImages(q.optionImages ? [...q.optionImages] : ['', '', '', '']);
    
    // Smoothly scroll the page directly to the top of the highlighted editing form in an elegant manner
    setTimeout(() => {
      const el = document.getElementById("posttest-form-container");
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        window.scrollTo({ top: 120, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleUpdatePostTestQuestion = async () => {
    if (role !== 'EDITOR') return;
    if (!editingPT || !newPTText.trim()) return;

    if (newPTOptions.some(opt => !opt.trim())) {
      alert("Mohon isi semua (4) opsi pilihan jawaban.");
      return;
    }

    const updated = postTestQuestions.map(q => {
      if (q.id === editingPT.id) {
        const item: PostTestQuestion = {
          ...q,
          text: newPTText,
          options: [...newPTOptions],
          correctOptionIndex: newPTCorrectIndex,
          points: Number(newPTPoints) || 0
        };
        if (newPTTextEn.trim()) item.textEn = newPTTextEn.trim();
        else delete item.textEn;
        if (newPTOptionsEn.some(o => !!o.trim())) item.optionsEn = [...newPTOptionsEn];
        else delete item.optionsEn;
        if (newPTImageUrl?.trim()) item.imageUrl = newPTImageUrl.trim();
        else delete item.imageUrl;
        if (newPTOptionImages.some(img => !!img)) item.optionImages = [...newPTOptionImages];
        else delete item.optionImages;
        return item;
      }
      return q;
    });

    setPostTestQuestions(updated);
    await savePostTestQuestions(updated);

    setEditingPT(null);
    setNewPTText('');
    setNewPTTextEn('');
    setNewPTOptions(['', '', '', '']);
    setNewPTOptionsEn(['', '', '', '']);
    setNewPTCorrectIndex(0);
    setNewPTPoints(10);
    setNewPTImageUrl('');
    setNewPTOptionImages(['', '', '', '']);
  };

  const handleRegisterAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (role !== 'EDITOR') {
      setAdminError("Hanya akun EDITOR yang dapat mendaftarkan admin baru.");
      return;
    }
    if (!newAdminEmail.trim() || !newAdminPassword.trim()) {
      setAdminError("Email dan password harus diisi.");
      return;
    }
    if (newAdminPassword.length < 6) {
      setAdminError("Password minimal harus 6 karakter.");
      return;
    }

    setAdminSaving(true);
    setAdminError('');
    setAdminSuccess('');

    try {
      const { firebaseConfig } = await import('../services/firebase');
      const { initializeApp } = await import('firebase/app');
      const { getAuth, createUserWithEmailAndPassword } = await import('firebase/auth');

      const secondaryApp = initializeApp(firebaseConfig, "SecondaryAdminApp");
      const secondaryAuth = getAuth(secondaryApp);

      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, newAdminEmail, newAdminPassword);
      const newUser = userCredential.user;

      // Store meta record FIRST, then clean up secondary app
      const newAdmin: AdminAccount = {
        id: newUser.uid,
        email: newAdminEmail,
        role: newAdminRole,
        createdAt: Date.now()
      };

      await saveAdmin(newAdmin);

      // Now safe to clean up secondary app
      try { await secondaryApp.delete(); } catch (_) { /* non-critical */ }

      setAdminSuccess(`Sukses mendaftarkan admin baru: ${newAdminEmail} (${newAdminRole})`);
      setNewAdminEmail('');
      setNewAdminPassword('');
      setNewAdminRole('EDITOR');

      // Refresh list
      const updatedAdmins = await getAdmins();
      setAdminsList(updatedAdmins);
    } catch (err: any) {
      console.error("Register Admin Error:", err);
      let msg = "Gagal mendaftarkan admin baru. Silakan coba lagi.";
      if (err.code === 'auth/email-already-in-use' || err.message?.includes('email-already-in-use')) {
        msg = "Email sudah terdaftar.";
      } else if (err.code === 'auth/invalid-email' || err.message?.includes('invalid-email')) {
        msg = "Format email tidak valid.";
      } else if (err.code === 'auth/weak-password') {
        msg = "Password terlalu lemah. Minimal 6 karakter.";
      } else if (err.message) {
        msg = err.message;
      }
      setAdminError(msg);
    } finally {
      setAdminSaving(false);
    }
  };

  const handleDeleteRecord = (recordId: string, recordName: string) => {
    if (role !== 'EDITOR') return;
    setDeleteConfirmRecordObj({ id: recordId, fullName: recordName });
  };

  const executeDeleteRecord = async () => {
    if (!deleteConfirmRecordObj || role !== 'EDITOR') return;
    setDeleteConfirmLoading(true);
    try {
      await deleteRecord(deleteConfirmRecordObj.id);
      setRecords(prev => prev.filter(r => r.id !== deleteConfirmRecordObj.id));
      setDeleteConfirmRecordObj(null);
    } catch (error) {
      console.error("Error deleting record:", error);
      alert("Gagal menghapus data. Silakan coba lagi.");
    } finally {
      setDeleteConfirmLoading(false);
    }
  };

  const handleAddArea = (e: React.FormEvent) => {
    e.preventDefault();
    if (role !== 'EDITOR') {
      setInstructorConfigError("Hanya akun EDITOR yang dapat menambah area.");
      return;
    }
    const area = newAreaInput.trim();
    if (!area) return;
    if (instructorMap[area]) {
      setInstructorConfigError(`Area "${area}" sudah terdaftar.`);
      return;
    }
    const updatedMap = { ...instructorMap, [area]: [] };
    setInstructorMap(updatedMap);
    setAreasList(Object.keys(updatedMap));
    setSelectedConfigArea(area);
    setNewAreaInput('');
    setInstructorConfigSuccess(`Area "${area}" berhasil ditambahkan ke daftar sementara. Tekan "Simpan Perubahan/Save changes" untuk menyimpan permanen.`);
    setInstructorConfigError('');
  };

  const handleDeleteArea = (areaToDelete: string) => {
    if (role !== 'EDITOR') {
      setInstructorConfigError("Hanya akun EDITOR yang dapat menghapus area.");
      return;
    }
    if (window.confirm(`Apakah Anda yakin ingin menghapus area "${areaToDelete}" beserta semua instruktur di dalamnya?`)) {
      const updatedMap = { ...instructorMap };
      delete updatedMap[areaToDelete];
      setInstructorMap(updatedMap);
      const remainingAreas = Object.keys(updatedMap);
      setAreasList(remainingAreas);
      if (selectedConfigArea === areaToDelete) {
        setSelectedConfigArea(remainingAreas.length > 0 ? remainingAreas[0] : '');
      }
      setInstructorConfigSuccess(`Area "${areaToDelete}" berhasil dihapus dari daftar sementara. Tekan "Simpan Perubahan/Save changes" untuk menyimpan permanen.`);
      setInstructorConfigError('');
    }
  };

  const handleAddInstructor = (e: React.FormEvent) => {
    e.preventDefault();
    if (role !== 'EDITOR') {
      setInstructorConfigError("Hanya akun EDITOR yang dapat menambah pemateri.");
      return;
    }
    const name = newInstructorInput.trim();
    if (!name) return;
    if (!selectedConfigArea) {
      setInstructorConfigError("Silakan pilih atau tambahkan area terlebih dahulu.");
      return;
    }
    
    const existing = instructorMap[selectedConfigArea] || [];
    if (existing.includes(name)) {
      setInstructorConfigError(`Pemateri "${name}" sudah terdaftar di area "${selectedConfigArea}".`);
      return;
    }

    const updatedInstructors = [...existing, name];
    const updatedMap = { ...instructorMap, [selectedConfigArea]: updatedInstructors };
    setInstructorMap(updatedMap);
    setNewInstructorInput('');
    setInstructorConfigSuccess(`Pemateri "${name}" berhasil ditambahkan ke area terpilih. Tekan "Simpan Perubahan/Save changes" untuk menyimpan permanen.`);
    setInstructorConfigError('');
  };

  const handleDeleteInstructor = (idxToDelete: number) => {
    if (role !== 'EDITOR') {
      setInstructorConfigError("Hanya akun EDITOR yang dapat menghapus pemateri.");
      return;
    }
    const existing = instructorMap[selectedConfigArea] || [];
    const nameToRemove = existing[idxToDelete];
    
    const updatedInstructors = existing.filter((_, idx) => idx !== idxToDelete);
    const updatedMap = { ...instructorMap, [selectedConfigArea]: updatedInstructors };
    setInstructorMap(updatedMap);
    setInstructorConfigSuccess(`Pemateri "${nameToRemove}" berhasil dihapus dari daftar terpilih. Tekan "Simpan Perubahan/Save changes" untuk menyimpan permanen.`);
    setInstructorConfigError('');
  };

  const startEditingInstructor = (idx: number, currentName: string) => {
    if (role !== 'EDITOR') return;
    setEditingInstructorIndex(idx);
    setEditingInstructorName(currentName);
  };

  const handleUpdateInstructorName = () => {
    if (role !== 'EDITOR') return;
    if (editingInstructorIndex === null) return;
    const trimmed = editingInstructorName.trim();
    if (!trimmed) return;

    const existing = [...(instructorMap[selectedConfigArea] || [])];
    existing[editingInstructorIndex] = trimmed;

    const updatedMap = { ...instructorMap, [selectedConfigArea]: existing };
    setInstructorMap(updatedMap);
    setEditingInstructorIndex(null);
    setEditingInstructorName('');
    setInstructorConfigSuccess(`Nama pemateri berhasil diubah sementara. Tekan "Simpan Perubahan/Save changes" untuk menyimpan permanen.`);
    setInstructorConfigError('');
  };

  const handleSaveInstructorConfig = async () => {
    if (role !== 'EDITOR') {
      setInstructorConfigError("Hanya akun EDITOR yang dapat menyimpan konfigurasi.");
      return;
    }
    setInstructorMapSaving(true);
    setInstructorConfigError('');
    setInstructorConfigSuccess('');
    try {
      await saveInstructorMap(instructorMap);
      setInstructorConfigSuccess("Konfigurasi Area dan Pemateri berhasil disimpan ke database Firestore!");
    } catch (err: any) {
      console.error(err);
      setInstructorConfigError("Gagal menyimpan konfigurasi: " + (err.message || String(err)));
    } finally {
      setInstructorMapSaving(false);
    }
  };

  const exportToCSV = () => {
    const dataToExport = filteredRecords;
    const headers = ['ID', 'Name', 'Job Title', 'Company', 'Date', 'Area', 'Activity', 'Instructor', 'Post-Test Score', 'Status'];
    const rows = dataToExport.map(r => [
      r.id, 
      r.fullName, 
      r.jobTitle || '-', 
      r.company || '-', 
      r.inductionDate, 
      r.area, 
      r.activity, 
      r.instructor, 
      r.postTestScore !== undefined ? String(r.postTestScore) : '-', 
      r.postTestScore !== undefined ? (r.postTestPassed ? 'LULUS' : 'GAGAL') : 'LULUS'
    ]);
    
    // Safely parse CSV escape boundaries (wrapping with double-quotes, escaping quotes inside)
    const csvRows = [headers, ...rows].map(row => 
      row.map(value => {
        const strVal = String(value);
        const escaped = strVal.replace(/"/g, '""');
        if (escaped.includes(',') || escaped.includes('\n') || escaped.includes('"')) {
          return `"${escaped}"`;
        }
        return escaped;
      }).join(',')
    );

    // Write UTF-8 BOM header for standard Excel formatting
    const csvContent = "\uFEFF" + csvRows.join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `laporan_evaluasi_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterArea('');
    setFilterSearch('');
    setFilterMinScore('');
    setFilterMaxScore('');
  };

  // Mandatory Training helper functions
  const getFilteredMTRecords = () => {
    return mandatoryRecords.filter(r => {
      const matchesModule = mtFilterModule === 'all' || r.moduleId === mtFilterModule;
      const matchesStatus = mtFilterStatus === 'all' 
        ? true 
        : mtFilterStatus === 'passed' ? r.passed : !r.passed;
      
      const recordArea = r.workArea || 'Yard Sukapura';
      const matchesArea = mtFilterArea === 'all' || !mtFilterArea 
        ? true 
        : (recordArea.toLowerCase() === mtFilterArea.toLowerCase() || (r.workArea && r.workArea.toLowerCase() === mtFilterArea.toLowerCase()));

      let matchesDate = true;
      const recDate = r.completionDate || (r.timestamp ? new Date(r.timestamp).toLocaleDateString('en-CA') : '');
      if (mtFilterStartDate) matchesDate = matchesDate && (recDate ? recDate >= mtFilterStartDate : true);
      if (mtFilterEndDate) matchesDate = matchesDate && (recDate ? recDate <= mtFilterEndDate : true);

      let matchesSearch = true;
      if (mtFilterSearch) {
        const q = mtFilterSearch.toLowerCase();
        matchesSearch = (
          r.fullName.toLowerCase().includes(q) ||
          r.nik.toLowerCase().includes(q) ||
          (r.position && r.position.toLowerCase().includes(q)) ||
          (r.workArea && r.workArea.toLowerCase().includes(q)) ||
          r.company.toLowerCase().includes(q) ||
          r.moduleTitle.toLowerCase().includes(q)
        );
      }

      return matchesModule && matchesStatus && matchesArea && matchesDate && matchesSearch;
    });
  };

  const clearMTFilters = () => {
    setMtFilterModule('all');
    setMtFilterStatus('all');
    setMtFilterArea('all');
    setMtFilterSearch('');
    setMtFilterStartDate('');
    setMtFilterEndDate('');
  };

  const exportMTToCSV = () => {
    const dataToExport = getFilteredMTRecords();
    const headers = ['ID', 'Nama Pekerja', 'NRP / NIK', 'Posisi / Jabatan', 'Perusahaan/Dept', 'Area Kerja', 'Tanggal Selesai', 'Modul Training', 'Skor Evaluasi', 'Status'];
    const rows = dataToExport.map(r => [
      r.id,
      r.fullName,
      r.nik,
      r.position || '-',
      r.company,
      r.workArea || 'Yard Sukapura',
      r.completionDate,
      r.moduleTitle,
      String(r.score),
      r.passed ? 'LULUS' : 'BELUM LULUS'
    ]);
    
    const csvRows = [headers, ...rows].map(row => 
      row.map(value => {
        const strVal = String(value);
        const escaped = strVal.replace(/"/g, '""');
        if (escaped.includes(',') || escaped.includes('\n') || escaped.includes('"')) {
          return `"${escaped}"`;
        }
        return escaped;
      }).join(',')
    );

    const csvContent = "\uFEFF" + csvRows.join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `laporan_mandatory_training_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDeleteMTRecord = (id: string, fullName: string, moduleTitle: string) => {
    if (role !== 'EDITOR') return;
    setDeleteConfirmMTObj({ id, fullName, moduleTitle });
  };

  const executeDeleteMTRecord = async () => {
    if (!deleteConfirmMTObj || role !== 'EDITOR') return;
    setDeleteConfirmMTLoading(true);
    try {
      await deleteMandatoryTrainingRecord(deleteConfirmMTObj.id);
      setMandatoryRecords(prev => prev.filter(r => r.id !== deleteConfirmMTObj.id));
      setDeleteConfirmMTObj(null);
    } catch (err) {
      console.error("Error deleting MT record:", err);
      alert("Gagal menghapus data training.");
    } finally {
      setDeleteConfirmMTLoading(false);
    }
  };

  const handleSaveMandatorySettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (role !== 'EDITOR') {
      showSaveError("Akses Ditolak", { code: "PERMISSION_DENIED", message: "Hanya akun EDITOR yang dapat mengubah pengaturan." });
      return;
    }
    setMandatorySettingsSaving(true);
    setMandatorySettingsSuccess('');
    setMandatorySettingsError('');
    try {
      const cleanSettings = JSON.parse(JSON.stringify(mandatorySettings, (_k, v) => (v === undefined ? null : v)));
      await saveMandatorySettings(cleanSettings);
      setMandatorySettingsSuccess("Pengaturan Safety Mandatory berhasil disimpan!");
      setTimeout(() => setMandatorySettingsSuccess(''), 4000);
      showSaveSuccess(
        "Berhasil Menyimpan!",
        "Seluruh konfigurasi Safety Mandatory (Passing Grade, Penandatangan, Area Kerja, PDF Modul, dan Soal Evaluasi) berhasil disimpan ke database Firebase."
      );
    } catch (err: any) {
      console.error("Error saving mandatory settings:", err);
      setMandatorySettingsError("Gagal menyimpan pengaturan: " + (err?.message || 'Error'));
      showSaveError("Gagal Menyimpan Pengaturan", err);
    } finally {
      setMandatorySettingsSaving(false);
    }
  };

  const autoSaveMandatorySettings = async (updatedSettings: MandatorySettings) => {
    const cleanSettings = JSON.parse(JSON.stringify(updatedSettings, (_k, v) => (v === undefined ? null : v)));
    setMandatorySettings(cleanSettings);
    if (role === 'EDITOR') {
      try {
        await saveMandatorySettings(cleanSettings);
      } catch (err: any) {
        console.error("Error auto-saving mandatory settings to Firebase:", err);
      }
    }
  };

  const handleSaveSingleModulePdf = async (moduleId: string, moduleTitle: string) => {
    if (role !== 'EDITOR') {
      showSaveError("Akses Ditolak", { code: "PERMISSION_DENIED", message: "Hanya akun EDITOR yang memiliki izin untuk mengubah materi." });
      return;
    }
    try {
      const cleanSettings = JSON.parse(JSON.stringify(mandatorySettings, (_k, v) => (v === undefined ? null : v)));
      await saveMandatorySettings(cleanSettings);
      showSaveSuccess(
        "Berhasil Menyimpan!",
        `File/Link PDF materi slide modul "${moduleTitle}" berhasil disimpan ke database Firebase.`
      );
    } catch (err: any) {
      console.error("Error saving module PDF:", err);
      showSaveError("Gagal Menyimpan PDF Modul", err);
    }
  };

  const handleSaveAllModulePdfs = async () => {
    if (role !== 'EDITOR') {
      showSaveError("Akses Ditolak", { code: "PERMISSION_DENIED", message: "Hanya akun EDITOR yang memiliki izin untuk mengubah materi." });
      return;
    }
    try {
      const cleanSettings = JSON.parse(JSON.stringify(mandatorySettings, (_k, v) => (v === undefined ? null : v)));
      await saveMandatorySettings(cleanSettings);
      showSaveSuccess(
        "Berhasil Menyimpan!",
        "Seluruh berkas dan link PDF materi slide ke-7 modul berhasil disimpan ke database Firebase."
      );
    } catch (err: any) {
      console.error("Error saving all module PDFs:", err);
      showSaveError("Gagal Menyimpan Materi PDF", err);
    }
  };

  const handleAddMTWorkArea = () => {
    if (role !== 'EDITOR') return;
    const name = newMTSettingWorkArea.trim();
    if (!name) return;
    if (mandatorySettings.allowedWorkAreas.includes(name)) {
      setMandatorySettingsError(`Area "${name}" sudah terdaftar.`);
      return;
    }
    const updated = {
      ...mandatorySettings,
      allowedWorkAreas: [...mandatorySettings.allowedWorkAreas, name]
    };
    autoSaveMandatorySettings(updated);
    setNewMTSettingWorkArea('');
    setMandatorySettingsError('');
  };

  const handleDeleteMTWorkArea = (areaToDelete: string) => {
    if (role !== 'EDITOR') return;
    if (mandatorySettings.allowedWorkAreas.length <= 1) {
      alert("Minimal harus ada 1 area kerja.");
      return;
    }
    const updated = {
      ...mandatorySettings,
      allowedWorkAreas: mandatorySettings.allowedWorkAreas.filter(a => a !== areaToDelete)
    };
    autoSaveMandatorySettings(updated);
  };

  const handleToggleModuleActive = (moduleId: string) => {
    if (role !== 'EDITOR') return;
    const currentActive = mandatorySettings.activeModules[moduleId] ?? true;
    const updated = {
      ...mandatorySettings,
      activeModules: {
        ...mandatorySettings.activeModules,
        [moduleId]: !currentActive
      }
    };
    autoSaveMandatorySettings(updated);
  };

  // PDF Handlers for Mandatory Modules
  const handlePdfUrlChange = (moduleId: string, url: string) => {
    if (role !== 'EDITOR') return;
    setMandatorySettings(prev => ({
      ...prev,
      modulePdfs: {
        ...(prev.modulePdfs || {}),
        [moduleId]: url
      }
    }));
  };

  const handlePdfUrlBlurOrApply = (moduleId: string, url: string) => {
    if (role !== 'EDITOR') return;
    const updated = {
      ...mandatorySettings,
      modulePdfs: {
        ...(mandatorySettings.modulePdfs || {}),
        [moduleId]: url
      }
    };
    autoSaveMandatorySettings(updated);
  };

  const handlePdfFileUpload = (moduleId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (role !== 'EDITOR') return;
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert("Hanya file bertipe PDF yang diperbolehkan!");
      return;
    }

    // Limit size if needed e.g. 10MB
    if (file.size > 15 * 1024 * 1024) {
      alert("Ukuran file PDF maksimal 15 MB!");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      if (dataUrl) {
        if (dataUrl.length > 900_000) {
          alert("File PDF terlalu besar untuk disimpan permanen. Gunakan tautan URL PDF publik sebagai gantinya, atau kompres file di bawah ~600KB.");
        }
        const updated = {
          ...mandatorySettings,
          modulePdfs: {
            ...(mandatorySettings.modulePdfs || {}),
            [moduleId]: dataUrl
          }
        };
        autoSaveMandatorySettings(updated);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteModulePdf = (moduleId: string) => {
    if (role !== 'EDITOR') return;
    const updatedPdfs = { ...(mandatorySettings.modulePdfs || {}) };
    delete updatedPdfs[moduleId];
    const updated = {
      ...mandatorySettings,
      modulePdfs: updatedPdfs
    };
    autoSaveMandatorySettings(updated);
  };

  // Questions Helper & Handlers for Mandatory Training
  const getEffectiveMTQuestions = (moduleId: string): PostTestQuestion[] => {
    const custom = mandatorySettings.customQuestions?.[moduleId];
    if (custom && custom.length >= 10) {
      return custom;
    }
    const defaultMod = MANDATORY_MODULES.find(m => m.id === moduleId);
    return defaultMod ? defaultMod.questions : [];
  };

  const handleSyncAllQuestionsToFirebase = async () => {
    if (role !== 'EDITOR') return;
    setIsSyncingQuestions(true);
    setSyncSuccessMessage(null);
    try {
      const res = await syncAllQuestionsToFirebase();
      const msg = `Berhasil! Seluruh ${res.totalAllQuestions} soal pertanyaan telah tersimpan secara permanen di Firebase Firestore (${res.totalInductionQuestions} soal kehadiran, ${res.totalPostTestQuestions} post-test induction, dan ${res.totalMandatoryQuestions} soal untuk 7 modul safety mandatory).`;
      setSyncSuccessMessage(msg);
      showSaveSuccess("Tersimpan di Firebase", msg);
      setTimeout(() => setSyncSuccessMessage(null), 8000);
    } catch (err) {
      console.error("Gagal sinkronisasi soal ke Firebase:", err);
      alert("Gagal menyimpan seluruh soal ke Firebase. Periksa koneksi internet.");
    } finally {
      setIsSyncingQuestions(false);
    }
  };

  const handleOpenAddMTQuestionModal = () => {
    if (role !== 'EDITOR') return;
    setMtEditingQuestionIndex(null);
    setMtQuestionForm({
      text: '',
      textEn: '',
      options: ['', '', '', ''],
      correctOptionIndex: 0,
      points: 20
    });
    setMtQuestionModalOpen(true);
  };

  const handleOpenEditMTQuestionModal = (index: number, q: PostTestQuestion) => {
    if (role !== 'EDITOR') return;
    setMtEditingQuestionIndex(index);
    setMtQuestionForm({
      id: q.id,
      text: q.text,
      textEn: q.textEn || '',
      options: [...q.options],
      correctOptionIndex: q.correctOptionIndex,
      points: q.points || 20
    });
    setMtQuestionModalOpen(true);
  };

  const handleSaveMTQuestion = async () => {
    if (role !== 'EDITOR') return;
    if (!mtQuestionForm.text.trim()) {
      alert("Teks pertanyaan tidak boleh kosong!");
      return;
    }
    if (mtQuestionForm.options.some(opt => !opt.trim())) {
      alert("Semua 4 pilihan jawaban harus diisi!");
      return;
    }

    const currentList = getEffectiveMTQuestions(mtSelectedQuestionModule);
    let updatedList: PostTestQuestion[] = [];

    const newQuestion: PostTestQuestion = {
      id: mtQuestionForm.id || `mt_q_${mtSelectedQuestionModule}_${Date.now()}`,
      text: mtQuestionForm.text.trim(),
      options: mtQuestionForm.options.map(o => o.trim()),
      correctOptionIndex: mtQuestionForm.correctOptionIndex,
      points: Number(mtQuestionForm.points) || 20
    };
    if (mtQuestionForm.textEn?.trim()) {
      newQuestion.textEn = mtQuestionForm.textEn.trim();
    }

    if (mtEditingQuestionIndex !== null && mtEditingQuestionIndex >= 0) {
      updatedList = [...currentList];
      updatedList[mtEditingQuestionIndex] = newQuestion;
    } else {
      updatedList = [...currentList, newQuestion];
    }

    const updatedSettings: MandatorySettings = {
      ...mandatorySettings,
      customQuestions: {
        ...(mandatorySettings.customQuestions || {}),
        [mtSelectedQuestionModule]: updatedList
      }
    };

    setMtQuestionModalOpen(false);
    await autoSaveMandatorySettings(updatedSettings);
  };

  const handleDeleteMTQuestion = async (index: number) => {
    if (role !== 'EDITOR') return;
    if (!confirm("Apakah Anda yakin ingin menghapus soal evaluasi ini?")) return;

    const currentList = getEffectiveMTQuestions(mtSelectedQuestionModule);
    const updatedList = currentList.filter((_, idx) => idx !== index);

    const updatedSettings: MandatorySettings = {
      ...mandatorySettings,
      customQuestions: {
        ...(mandatorySettings.customQuestions || {}),
        [mtSelectedQuestionModule]: updatedList
      }
    };

    await autoSaveMandatorySettings(updatedSettings);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Admin Navbar */}
      <nav className="bg-gray-800 text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Left: Brand + Role */}
            <div className="flex items-center gap-3 shrink-0">
              <div className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold flex items-center gap-1 shrink-0 ${role === 'EDITOR' ? 'bg-blue-600' : 'bg-gray-600'}`}>
                {role === 'EDITOR' ? <Shield size={10} className="sm:w-3 sm:h-3" /> : <Eye size={10} className="sm:w-3 sm:h-3" />}
                {role}
              </div>
            </div>

            {/* Center: Desktop Tabs (scrollable) */}
            <div className="hidden lg:flex flex-1 mx-4 overflow-x-auto scrollbar-hide items-center gap-1">
              <button 
                onClick={() => setActiveTab('dashboard')}
                className={`px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'dashboard' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
              >
                Dashboard
              </button>
              <button 
                onClick={() => setActiveTab('history')}
                className={`px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'history' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
              >
                History
              </button>
              <button 
                onClick={() => setActiveTab('mandatory_history')}
                className={`px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'mandatory_history' ? 'bg-amber-600 text-white font-bold' : 'text-amber-300 hover:bg-gray-700 hover:text-white'}`}
              >
                Mandatory History
              </button>
              <button 
                onClick={() => setActiveTab('mandatory_settings')}
                className={`px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'mandatory_settings' ? 'bg-amber-600 text-white font-bold' : 'text-amber-200 hover:bg-gray-700 hover:text-white'}`}
              >
                Mandatory Settings
              </button>
              <button 
                onClick={() => setActiveTab('questions')}
                className={`px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'questions' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
              >
                Questions
              </button>
              <button 
                onClick={() => setActiveTab('posttest')}
                className={`px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'posttest' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
              >
                Post-Test
              </button>
              <button 
                onClick={() => setActiveTab('admins')}
                className={`px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'admins' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
              >
                Admins
              </button>
              <button 
                onClick={() => setActiveTab('instructors')}
                className={`px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'instructors' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
              >
                Instructors
              </button>
              <button 
                onClick={() => setActiveTab('expiring')}
                className={`px-3 py-1.5 rounded-md text-xs xl:text-sm font-semibold flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'expiring' ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
              >
                Masa Berlaku
                {totalExpiringSoon > 0 && (
                  <span className="bg-amber-500 text-white font-bold text-[10px] px-1.5 py-0.5 rounded-full animate-pulse">
                    {totalExpiringSoon}
                  </span>
                )}
              </button>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => openCertificatePreview('INDUCTION')}
                className="hidden sm:flex items-center gap-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer"
                title="Preview Format Sertifikat PDF"
              >
                <Award size={15} />
                <span className="hidden xl:inline">Sertifikat</span>
              </button>

              <button 
                onClick={() => fetchData(true)} 
                className="text-gray-300 hover:text-white p-1.5 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer" 
                title="Refresh Data dari Server"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin text-amber-400' : ''} />
              </button>
              
              {/* Desktop Logout Button */}
              <button 
                onClick={onLogout}
                className="hidden lg:flex items-center gap-1.5 text-red-400 hover:text-white hover:bg-red-950/40 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer"
              >
                <LogOut size={15} /> <span className="hidden xl:inline">Logout</span>
              </button>

              {/* Mobile Menu Toggle Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 focus:outline-none transition-colors cursor-pointer"
                aria-label="Toggle Menu"
              >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-gray-900 border-t border-gray-750 px-4 pt-2 pb-4 space-y-1 shadow-inner animate-fade-in">
            <button 
              onClick={() => { setActiveTab('dashboard'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-bold flex items-center transition-all cursor-pointer ${
                activeTab === 'dashboard' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <span>Dashboard</span>
            </button>
            
            <button 
              onClick={() => { setActiveTab('history'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-bold flex items-center transition-all cursor-pointer ${
                activeTab === 'history' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <span>History Induksi Safety</span>
            </button>
            
            <button 
              onClick={() => { setActiveTab('mandatory_history'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-bold flex items-center justify-between transition-all cursor-pointer ${
                activeTab === 'mandatory_history' ? 'bg-amber-600 text-white' : 'text-amber-300 hover:bg-gray-800'
              }`}
            >
              <div className="flex items-center gap-2">
                <Award size={16} />
                <span>History Mandatory Training (7 Modul)</span>
              </div>
            </button>

            <button 
              onClick={() => { setActiveTab('mandatory_settings'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-bold flex items-center justify-between transition-all cursor-pointer ${
                activeTab === 'mandatory_settings' ? 'bg-amber-600 text-white' : 'text-amber-200 hover:bg-gray-800'
              }`}
            >
              <div className="flex items-center gap-2">
                <Sliders size={16} />
                <span>Pengaturan Safety Mandatory</span>
              </div>
            </button>
            
            <button 
              onClick={() => { setActiveTab('questions'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-bold flex items-center transition-all cursor-pointer ${
                activeTab === 'questions' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <span>Manage Questions</span>
            </button>
            
            <button 
              onClick={() => { setActiveTab('posttest'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-bold flex items-center transition-all cursor-pointer ${
                activeTab === 'posttest' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <span>Manage Post-Test</span>
            </button>
            
            <button 
              onClick={() => { setActiveTab('admins'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-bold flex items-center transition-all cursor-pointer ${
                activeTab === 'admins' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <span>Manage Admins</span>
            </button>
            
            <button 
              onClick={() => { setActiveTab('instructors'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-bold flex items-center transition-all cursor-pointer ${
                activeTab === 'instructors' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <span>Manage Instructors (Pemateri)</span>
            </button>
            
            <button 
              onClick={() => { setActiveTab('expiring'); setMobileMenuOpen(false); }}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-bold flex items-center transition-all cursor-pointer ${
                activeTab === 'expiring' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <div className="flex items-center gap-2">
                <span>Status Expired</span>
                {totalExpiringSoon > 0 && (
                  <span className="bg-amber-500 text-white font-bold text-[10px] px-1.5 py-0.5 rounded-full animate-pulse">
                    {totalExpiringSoon}
                  </span>
                )}
              </div>
            </button>

            {/* Mobile Logout */}
            <div className="pt-3 border-t border-gray-800 mt-2">
              <button 
                onClick={() => { setMobileMenuOpen(false); onLogout(); }}
                className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer shadow"
              >
                <LogOut size={16} /> Keluar / Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      <main className="flex-grow max-w-7xl w-full mx-auto py-6 sm:px-6 lg:px-8">
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <RefreshCw className="animate-spin text-gray-500 w-8 h-8" />
            <span className="ml-2 text-gray-500">Loading data from Firebase...</span>
          </div>
        ) : (
          <>
            {/* DASHBOARD TAB */}
            {activeTab === 'dashboard' && (
              <div className="space-y-6 animate-fade-in-up">
                {/* Filter Hari Ini / Bulan Berjalan / Custom Range */}
                <div className="bg-white p-5 rounded-lg shadow border border-gray-200">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                      <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wide flex items-center gap-1.5">
                        <Filter className="w-4 h-4 text-blue-600" />
                        Saring Statistik Berdasarkan Tanggal / Filter Statistics
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Saring data chart dan metrik kelulusan di bawah ini berdasarkan periode waktu pilihan Anda.
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                      <div className="inline-flex rounded-md shadow-sm" role="group">
                        <button
                          type="button"
                          onClick={() => {
                            setDashFilterSelect('all');
                            setDashStartDate('');
                            setDashEndDate('');
                          }}
                          className={`px-3.5 py-2 text-xs font-semibold rounded-l-lg border ${
                            dashFilterSelect === 'all'
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-305 hover:bg-gray-50'
                          } transition-all cursor-pointer`}
                        >
                          Semua Waktu
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDashFilterSelect('today');
                            setDashStartDate('');
                            setDashEndDate('');
                          }}
                          className={`px-3.5 py-2 text-xs font-semibold border-t border-b border-r ${
                            dashFilterSelect === 'today'
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-305 hover:bg-gray-50'
                          } transition-all cursor-pointer`}
                        >
                          Hari Ini
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDashFilterSelect('current_month');
                            setDashStartDate('');
                            setDashEndDate('');
                          }}
                          className={`px-3.5 py-2 text-xs font-semibold border-t border-b border-r ${
                            dashFilterSelect === 'current_month'
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-305 hover:bg-gray-50'
                          } transition-all cursor-pointer`}
                        >
                          Bulan Berjalan
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDashFilterSelect('custom');
                          }}
                          className={`px-3.5 py-2 text-xs font-semibold rounded-r-lg border-t border-b border-r ${
                            dashFilterSelect === 'custom'
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-305 hover:bg-gray-50'
                          } transition-all cursor-pointer`}
                        >
                          Custom Range / Kustom
                        </button>
                      </div>

                      {/* Custom Range Date Pickers */}
                      {dashFilterSelect === 'custom' && (
                        <div className="flex items-center gap-2 animate-fade-in">
                          <input
                            type="date"
                            value={dashStartDate}
                            onChange={(e) => setDashStartDate(e.target.value)}
                            className="border border-gray-300 rounded-lg px-2 py-1 bg-white text-xs text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                          />
                          <span className="text-xs text-gray-400 font-medium">s/d</span>
                          <input
                            type="date"
                            value={dashEndDate}
                            onChange={(e) => setDashEndDate(e.target.value)}
                            className="border border-gray-300 rounded-lg px-2 py-1 bg-white text-xs text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                          />
                        </div>
                      )}

                      {dashFilterSelect === 'today' && (
                        <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-100 px-2.5 py-1.5 rounded-lg animate-fade-in">
                          📅 {new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                      )}

                      {dashFilterSelect === 'current_month' && (
                        <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-100 px-2.5 py-1.5 rounded-lg animate-fade-in">
                          📅 Bulan Ini: {new Date().toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Program View Mode Selector */}
                <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                      <Sliders className="w-3.5 h-3.5 text-gray-400" />
                      Program:
                    </span>
                    <div className="inline-flex rounded-lg bg-gray-100 p-1" role="group">
                      <button
                        type="button"
                        onClick={() => setDashProgramView('all')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                          dashProgramView === 'all'
                            ? 'bg-white text-blue-700 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <span>Semua Program</span>
                        <span className="text-[10px] bg-gray-200/80 text-gray-700 px-1.5 py-0.5 rounded-full">
                          {dashboardRecords.length + dashboardMTRecords.length}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDashProgramView('mandatory')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                          dashProgramView === 'mandatory'
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'text-gray-600 hover:text-purple-700'
                        }`}
                      >
                        <Shield className="w-3.5 h-3.5" />
                        <span>Safety Mandatory ({dashboardMTRecords.length})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDashProgramView('induction')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                          dashProgramView === 'induction'
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-600 hover:text-blue-700'
                        }`}
                      >
                        <GraduationCap className="w-3.5 h-3.5" />
                        <span>Safety Induction ({dashboardRecords.length})</span>
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-500 self-end sm:self-auto">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="font-medium text-[11px]">Terhubung ke Database</span>
                  </div>
                </div>

                {/* ========================================================================= */}
                {/* SECTION: SAFETY MANDATORY TRAINING                                        */}
                {/* ========================================================================= */}
                {(dashProgramView === 'all' || dashProgramView === 'mandatory') && (
                  <div className="space-y-6 pt-2">
                    {/* MT Header Banner */}
                    <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900 text-white p-5 rounded-2xl shadow-md flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border border-purple-800/40">
                      <div className="flex items-center gap-3.5">
                        <div className="p-3 bg-purple-500/20 border border-purple-400/30 rounded-xl text-purple-300">
                          <Shield className="w-7 h-7" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-extrabold text-white tracking-wide">
                              Data & Statistik Safety Mandatory Training
                            </h3>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-500/30 text-purple-200 border border-purple-400/40">
                              7 Modul Wajib
                            </span>
                          </div>
                          <p className="text-xs text-purple-200/80 mt-1 max-w-2xl">
                            Monitoring penyelesaian 7 modul keselamatan wajib, pencapaian pekerja, tingkat kelulusan, dan evaluasi kepatuhan area.
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setActiveTab('mandatory_history')}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all shadow-md hover:shadow cursor-pointer self-stretch md:self-auto justify-center"
                      >
                        <span>Buka Riwayat & Matriks Progres</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* MT KPI Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      {/* Card 1: Total Sesi Modul */}
                      <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-100 border-l-4 border-l-purple-600 hover:shadow-md transition-all">
                        <div className="flex items-center justify-between">
                          <h4 className="text-gray-500 text-xs font-bold uppercase tracking-wider">Total Sesi Modul MT</h4>
                          <span className="p-2 rounded-lg bg-purple-50 text-purple-600">
                            <BookOpen className="w-4 h-4" />
                          </span>
                        </div>
                        <p className="text-3xl font-extrabold text-gray-900 mt-2">{mtStats.total}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          <span className="text-emerald-600 font-bold">{mtStats.passedCount} Lulus ({mtStats.passRate}%)</span>
                          <span className="text-gray-300">•</span>
                          <span className="text-rose-500 font-medium">{mtStats.failedCount} Gagal</span>
                        </div>
                      </div>

                      {/* Card 2: Total Pekerja Terdaftar */}
                      <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100 border-l-4 border-l-blue-600 hover:shadow-md transition-all">
                        <div className="flex items-center justify-between">
                          <h4 className="text-gray-500 text-xs font-bold uppercase tracking-wider">Pekerja Terdaftar (MT)</h4>
                          <span className="p-2 rounded-lg bg-blue-50 text-blue-600">
                            <Users className="w-4 h-4" />
                          </span>
                        </div>
                        <p className="text-3xl font-extrabold text-gray-900 mt-2">
                          {mtStats.uniquePersonsCount} <span className="text-sm font-normal text-gray-500">Orang</span>
                        </p>
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-blue-700 font-medium">
                          <CheckCircle className="w-3.5 h-3.5 text-blue-600" />
                          <span>{mtStats.perfectCount} orang lulus semua 7 modul</span>
                        </div>
                      </div>

                      {/* Card 3: Rata-rata Skor MT */}
                      <div className="bg-white p-6 rounded-xl shadow-sm border border-emerald-100 border-l-4 border-l-emerald-600 hover:shadow-md transition-all">
                        <div className="flex items-center justify-between">
                          <h4 className="text-gray-500 text-xs font-bold uppercase tracking-wider">Rata-rata Skor Modul</h4>
                          <span className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                            <Award className="w-4 h-4" />
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1.5 mt-2">
                          <p className="text-3xl font-extrabold text-gray-900">{mtStats.avgScore}</p>
                          <span className="text-sm font-medium text-gray-400">/ 100</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Standar Kelulusan Minimum: <strong className="text-gray-700">80</strong>
                        </p>
                      </div>

                      {/* Card 4: Training Hari Ini MT */}
                      <div className="bg-white p-6 rounded-xl shadow-sm border border-amber-100 border-l-4 border-l-amber-500 hover:shadow-md transition-all">
                        <div className="flex items-center justify-between">
                          <h4 className="text-gray-500 text-xs font-bold uppercase tracking-wider">Training MT Hari Ini</h4>
                          <span className="p-2 rounded-lg bg-amber-50 text-amber-600">
                            <Calendar className="w-4 h-4" />
                          </span>
                        </div>
                        <p className="text-3xl font-extrabold text-amber-600 mt-2">
                          {mtStats.todayPersonsCount} <span className="text-sm font-normal text-gray-500">Orang</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          {mtStats.todayRecordsCount} sesi modul dikerjakan hari ini
                        </p>
                      </div>
                    </div>

                    {/* MT Charts: Per Module Breakdown & Area Distribution */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Module Performance Bar Chart (Col 1 & 2) */}
                      <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                        <div className="border-b border-gray-100 pb-3 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div>
                            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                              <span className="w-2.5 h-5 bg-purple-600 rounded-full block"></span>
                              Pencapaian Peserta Per Modul (7 Modul Wajib)
                            </h4>
                            <p className="text-[11px] text-gray-500 mt-0.5">Jumlah sesi modul yang diselesaikan dan tingkat kelulusan pekerja</p>
                          </div>
                          <div className="flex items-center gap-3 text-xs font-semibold">
                            <span className="flex items-center gap-1.5 text-emerald-700">
                              <span className="w-3 h-3 bg-emerald-500 rounded"></span> Lulus
                            </span>
                            <span className="flex items-center gap-1.5 text-rose-700">
                              <span className="w-3 h-3 bg-rose-400 rounded"></span> Belum Lulus
                            </span>
                          </div>
                        </div>

                        <div className="h-[280px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart 
                              data={mtStats.moduleStats.map(m => ({
                                name: m.badge,
                                title: m.title,
                                Lulus: m.passed,
                                'Belum Lulus': m.failed,
                                Total: m.total
                              }))}
                              margin={{ top: 15, right: 10, left: -15, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                              <XAxis dataKey="name" fontSize={11} stroke="#6B7280" tickLine={false} />
                              <YAxis allowDecimals={false} stroke="#9CA3AF" fontSize={11} tickLine={false} />
                              <Tooltip 
                                formatter={(value: any, name: any) => [`${value} Sesi`, `${name}`]}
                                contentStyle={{ 
                                  backgroundColor: '#FFFFFF', 
                                  border: '1px solid #E5E7EB', 
                                  borderRadius: '8px', 
                                  fontSize: '12px' 
                                }}
                              />
                              <Bar dataKey="Lulus" fill="#10B981" radius={[4, 4, 0, 0]} stackId="a" />
                              <Bar dataKey="Belum Lulus" fill="#F43F5E" radius={[4, 4, 0, 0]} stackId="a" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>

                        {/* 7 Modules Quick Stat Badges */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 pt-4 border-t border-gray-100 mt-3">
                          {mtStats.moduleStats.map(m => (
                            <div key={m.id} className="p-2 rounded-lg bg-gray-50 border border-gray-200/70 text-center hover:bg-purple-50/40 transition-colors">
                              <span className="text-[10px] font-extrabold uppercase text-purple-700 block truncate" title={m.title}>
                                {m.badge}
                              </span>
                              <p className="text-xs font-black text-gray-800 mt-0.5">{m.total} <span className="text-[10px] font-normal text-gray-500">sesi</span></p>
                              <span className={`text-[10px] font-bold block mt-0.5 ${m.passRate >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                {m.passRate}% pass
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Area Distribution MT (Col 3) */}
                      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
                        <div>
                          <div className="border-b border-gray-100 pb-3 mb-4">
                            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                              <span className="w-2.5 h-5 bg-indigo-600 rounded-full block"></span>
                              Distribusi Peserta MT per Area
                            </h4>
                            <p className="text-[11px] text-gray-500 mt-0.5">Sebaran pengerjaan modul di setiap area kerja</p>
                          </div>

                          <div className="h-[250px]">
                            {mtStats.areaData.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart layout="vertical" data={mtStats.areaData.slice(0, 5)} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} vertical={true} />
                                  <XAxis type="number" allowDecimals={false} stroke="#9CA3AF" fontSize={11} tickLine={false} />
                                  <YAxis type="category" dataKey="name" stroke="#4B5563" fontSize={11} tickLine={false} width={100} />
                                  <Tooltip 
                                    formatter={(val: any) => [`${val} Sesi`, 'Penyelesaian']}
                                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px' }}
                                  />
                                  <Bar dataKey="value" fill="#6366F1" radius={[0, 6, 6, 0]} barSize={18}>
                                    <LabelList dataKey="value" position="right" style={{ fill: '#374151', fontSize: '11px', fontWeight: 'bold' }} />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-full text-xs text-gray-400 italic">
                                Belum ada data pengerjaan dalam rentang waktu ini
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-600">
                          <span>Total Unit / Area: <strong className="text-gray-900">{mtStats.areaData.length}</strong></span>
                          <button 
                            type="button" 
                            onClick={() => setActiveTab('mandatory_history')}
                            className="text-purple-600 font-bold hover:underline cursor-pointer"
                          >
                            Lihat detail area &rarr;
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* MT Trend & Recent MT Submissions */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Daily MT Trend (Col 1) */}
                      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                        <div className="border-b border-gray-100 pb-3 mb-4">
                          <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                            <span className="w-2.5 h-5 bg-teal-500 rounded-full block"></span>
                            Tren Harian Safety Mandatory (7 Hari)
                          </h4>
                          <p className="text-[11px] text-gray-500 mt-0.5">Frekuensi pengerjaan modul harian</p>
                        </div>

                        <div className="h-[220px]">
                          {mtStats.dailyMTData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={mtStats.dailyMTData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                                <XAxis dataKey="name" fontSize={10} stroke="#6B7280" tickLine={false} />
                                <YAxis allowDecimals={false} stroke="#9CA3AF" fontSize={10} tickLine={false} />
                                <Tooltip 
                                  contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '11px' }}
                                />
                                <Bar dataKey="Lulus" fill="#10B981" stackId="mtDaily" radius={[0, 0, 0, 0]} />
                                <Bar dataKey="Belum Lulus" fill="#F43F5E" stackId="mtDaily" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full text-xs text-gray-400 italic">
                              Belum ada aktivitas pengerjaan dalam rentang ini
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Recent MT Activity Table (Col 2 & 3) */}
                      <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
                        <div>
                          <div className="border-b border-gray-100 pb-3 mb-4 flex items-center justify-between">
                            <div>
                              <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                                <span className="w-2.5 h-5 bg-blue-600 rounded-full block"></span>
                                Pengerjaan Terbaru Safety Mandatory
                              </h4>
                              <p className="text-[11px] text-gray-500 mt-0.5">5 sesi modul terakhir yang diselesaikan oleh peserta</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setActiveTab('mandatory_history')}
                              className="text-xs font-bold text-purple-700 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 px-3 py-1.5 rounded-lg transition-all cursor-pointer"
                            >
                              Lihat Semua Data &rarr;
                            </button>
                          </div>

                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-xs">
                              <thead>
                                <tr className="bg-gray-50 text-gray-500 uppercase tracking-wider text-[10px]">
                                  <th className="py-2.5 px-3 text-left font-bold">Peserta & NIK</th>
                                  <th className="py-2.5 px-3 text-left font-bold">Area Kerja</th>
                                  <th className="py-2.5 px-3 text-left font-bold">Modul Keselamatan</th>
                                  <th className="py-2.5 px-3 text-center font-bold">Skor</th>
                                  <th className="py-2.5 px-3 text-center font-bold">Status</th>
                                  <th className="py-2.5 px-3 text-right font-bold">Tanggal</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {mtStats.recentMTRecords.length > 0 ? (
                                  mtStats.recentMTRecords.map((r) => {
                                    const modBadge = MANDATORY_MODULES.find(m => m.id === r.moduleId)?.badge || r.moduleId;
                                    return (
                                      <tr key={r.id} className="hover:bg-purple-50/30 transition-colors">
                                        <td className="py-2.5 px-3 font-semibold text-gray-900">
                                          <div>{r.fullName}</div>
                                          <div className="text-[10px] text-gray-400 font-normal">{r.nik || '-'}</div>
                                        </td>
                                        <td className="py-2.5 px-3 text-gray-600">
                                          {r.workArea || '-'}
                                        </td>
                                        <td className="py-2.5 px-3">
                                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800 border border-purple-200 inline-block">
                                            {modBadge}
                                          </span>
                                          <span className="text-[11px] text-gray-600 ml-1.5 hidden sm:inline">{r.moduleTitle}</span>
                                        </td>
                                        <td className="py-2.5 px-3 text-center font-extrabold text-gray-800">
                                          {r.score}
                                        </td>
                                        <td className="py-2.5 px-3 text-center">
                                          {r.passed ? (
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                                              LULUS
                                            </span>
                                          ) : (
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800">
                                              GAGAL
                                            </span>
                                          )}
                                        </td>
                                        <td className="py-2.5 px-3 text-right text-gray-500 text-[11px] whitespace-nowrap">
                                          {r.completionDate || (r.timestamp ? new Date(r.timestamp).toLocaleDateString('id-ID') : '-')}
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan={6} className="py-6 text-center text-gray-400 italic">
                                      Belum ada data pengerjaan safety mandatory.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ========================================================================= */}
                {/* SECTION: SAFETY INDUCTION                                                 */}
                {/* ========================================================================= */}
                {(dashProgramView === 'all' || dashProgramView === 'induction') && (
                  <div className="space-y-6 pt-4 border-t border-gray-200">
                    {/* Induction Header Banner */}
                    <div className="bg-gradient-to-r from-blue-900 via-slate-900 to-teal-900 text-white p-5 rounded-2xl shadow-md flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border border-blue-800/40">
                      <div className="flex items-center gap-3.5">
                        <div className="p-3 bg-blue-500/20 border border-blue-400/30 rounded-xl text-blue-300">
                          <GraduationCap className="w-7 h-7" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-extrabold text-white tracking-wide">
                              Data & Statistik Safety Induction
                            </h3>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-500/30 text-blue-200 border border-blue-400/40">
                              Tamu & Pekerja Risiko Tinggi
                            </span>
                          </div>
                          <p className="text-xs text-blue-200/80 mt-1 max-w-2xl">
                            Monitoring induksi keselamatan untuk tamu dan pekerja vendor/risiko tinggi, kepatuhan post-test, masa berlaku sertifikat, dan distribusi area.
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setActiveTab('history')}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition-all shadow-md hover:shadow cursor-pointer self-stretch md:self-auto justify-center"
                      >
                        <span>Buka Riwayat Induksi Lengkap</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* KPI Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
                        <h3 className="text-gray-500 text-sm font-medium uppercase">Total Inductions</h3>
                        <p className="text-3xl font-bold text-gray-800 mt-2">{stats.total}</p>
                      </div>
                      <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
                        <h3 className="text-gray-500 text-sm font-medium uppercase">Most Active Area</h3>
                        <p className="text-xl font-bold text-gray-800 mt-2 truncate" title={stats.areaData.sort((a,b) => b.value - a.value)[0]?.name || 'N/A'}>
                          {stats.areaData.sort((a,b) => b.value - a.value)[0]?.name || 'N/A'}
                        </p>
                      </div>
                      <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
                        <h3 className="text-gray-500 text-sm font-medium uppercase">Today's Inductions</h3>
                        <p className="text-3xl font-bold text-gray-800 mt-2">
                          {records.filter(r => r.inductionDate === new Date().toISOString().split('T')[0] || r.inductionDate === new Date().toLocaleDateString('en-CA')).length}
                        </p>
                      </div>
                      <button
                        onClick={() => setActiveTab('expiring')}
                        className="bg-white p-6 rounded-lg shadow border-l-4 border-amber-500 text-left hover:bg-amber-50/50 hover:shadow-md transition-all cursor-pointer block w-full focus:outline-none"
                      >
                        <h3 className="text-gray-500 text-xs font-medium uppercase flex items-center justify-between">
                          <span>Status Expiry / Expired</span>
                          {totalExpiringSoon > 0 && (
                            <span className="flex h-2 w-2 relative">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                            </span>
                          )}
                        </h3>
                        <div className="flex items-baseline justify-between mt-2">
                          <p className="text-3xl font-bold text-gray-800">
                            {totalExpiringSoon}
                          </p>
                          <span className="text-xs text-gray-500 font-medium bg-amber-100 px-2 py-0.5 rounded-full">
                            {totalExpired} Expired
                          </span>
                        </div>
                        <span className="text-[10px] text-amber-600 block mt-1 hover:underline">
                          Lihat list lengkap &rarr;
                        </span>
                      </button>
                    </div>

                    {/* Scored Post Test KPI Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white p-6 rounded-lg shadow border-l-4 border-yellow-500">
                        <h3 className="text-gray-500 text-sm font-medium uppercase text-yellow-600">Rata-rata Nilai Post-Test / Avg Score</h3>
                        <div className="flex items-baseline gap-2 mt-2">
                          <p className="text-3xl font-bold text-gray-800">{stats.avgScore}</p>
                          <span className="text-sm text-gray-500">/ 100</span>
                        </div>
                      </div>
                      <div className="bg-white p-6 rounded-lg shadow border-l-4 border-teal-500">
                        <h3 className="text-gray-500 text-sm font-medium uppercase text-teal-600">Kelulusan Post-Test / Safety Induction Pass Rate</h3>
                        <div className="flex items-center gap-4 mt-2">
                          <div>
                            <p className="text-2xl font-bold text-green-600">{stats.passedCount} <span className="text-xs text-gray-400 font-normal">Lulus</span></p>
                          </div>
                          <div className="border-l h-8 border-gray-200"></div>
                          <div>
                            <p className="text-2xl font-bold text-red-500">{stats.failedCount} <span className="text-xs text-gray-400 font-normal">Gagal</span></p>
                          </div>
                          <div className="border-l h-8 border-gray-200"></div>
                          <p className="text-sm text-gray-500">
                            Pass Rate: <span className="font-semibold text-gray-800">{stats.total > 0 ? Math.round((stats.passedCount / stats.total) * 100) : 0}%</span>
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Section Overview Baru / New Dashboard Overview Section */}
                    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm animate-fade-in-up">
                      <div className="border-b border-gray-100 pb-4 mb-6">
                        <h3 className="text-lg font-extrabold text-gray-900 flex items-center gap-2">
                          <span className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                            <Users className="w-5 h-5" />
                          </span>
                          <span>Ringkasan Induksi & Distribusi Pengunjung / Induction & Visitor Overview</span>
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Visualisasi real-time tren harian kelulusan induksi serta pembagian kategori pengunjung (Kunjungan Tamu vs Pekerja Risiko Tinggi).
                        </p>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Kolom 1 & 2: Daily Completions by Visitor Type */}
                        <div className="lg:col-span-2 bg-gray-50/50 p-5 rounded-xl border border-gray-100">
                          <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div>
                              <h4 className="text-sm font-bold text-gray-850">
                                Tren Penyelesaian Induksi Harian / Daily Induction Completions
                              </h4>
                              <p className="text-[11px] text-gray-500">Jumlah pendaftaran per hari (7 hari terakhir aktif)</p>
                            </div>
                            <div className="flex gap-4 text-xs font-semibold text-gray-600">
                              <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 bg-blue-500 rounded"></span>
                                <span>Tamu / Guest ({stats.tamuCount})</span>
                              </span>
                              <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 bg-amber-500 rounded"></span>
                                <span>Mekanik/Vendor ({stats.vendorCount})</span>
                              </span>
                            </div>
                          </div>

                          <div className="h-[280px]">
                            {stats.dailyTamuVendorData.length > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats.dailyTamuVendorData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                                  <XAxis dataKey="name" fontSize={11} stroke="#6B7280" tickLine={false} />
                                  <YAxis allowDecimals={false} stroke="#9CA3AF" fontSize={11} tickLine={false} />
                                  <Tooltip 
                                    contentStyle={{ 
                                      backgroundColor: '#FFFFFF', 
                                      border: '1px solid #E5E7EB', 
                                      borderRadius: '8px', 
                                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                      fontSize: '12px'
                                    }} 
                                  />
                                  <Bar dataKey="Kunjungan Tamu / Guest" stackId="a" fill="#3B82F6" radius={[0, 0, 0, 0]} />
                                  <Bar dataKey="Mekanik / Vendor / Pekerja Risiko Tinggi" stackId="a" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            ) : (
                              <div className="flex items-center justify-center h-full text-sm text-gray-400 italic">
                                Belum ada data pendaftaran dalam rentang waktu yang dipilih
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Kolom 3: Pie Chart - Visitor Type Distribution */}
                        <div className="bg-gray-50/50 p-5 rounded-xl border border-gray-100 flex flex-col justify-between">
                          <div>
                            <h4 className="text-sm font-bold text-gray-800 mb-1">
                              Proporsi Tipe Pengunjung / Visitor Distribution
                            </h4>
                            <p className="text-[11px] text-gray-500 mb-4">Persentase perbandingan tipe kunjungan</p>
                          </div>

                          <div className="h-[180px] relative flex items-center justify-center">
                            {stats.total > 0 ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={stats.visitorTypeData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={55}
                                    outerRadius={75}
                                    paddingAngle={3}
                                    dataKey="value"
                                  >
                                    {stats.visitorTypeData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={index === 0 ? '#3B82F6' : '#F59E0B'} />
                                    ))}
                                  </Pie>
                                  <Tooltip 
                                    formatter={(value) => [`${value} Orang`, 'Jumlah']}
                                    contentStyle={{ 
                                      backgroundColor: '#FFFFFF', 
                                      border: '1px solid #E5E7EB', 
                                      borderRadius: '8px',
                                      fontSize: '12px'
                                    }}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            ) : (
                              <span className="text-sm text-gray-400 italic">Tidak ada data</span>
                            )}
                            {stats.total > 0 && (
                              <div className="absolute flex flex-col items-center justify-center">
                                <span className="text-2xl font-extrabold text-gray-800">{stats.total}</span>
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Total</span>
                              </div>
                            )}
                          </div>

                          <div className="space-y-2 mt-4">
                            <div className="flex items-center justify-between text-xs border-b border-gray-100 pb-1.5">
                              <span className="flex items-center gap-2 text-gray-650">
                                <span className="w-2.5 h-2.5 bg-blue-500 rounded-full"></span>
                                <span>Kunjungan Tamu (Guest)</span>
                              </span>
                              <span className="font-bold text-gray-800">
                                {stats.tamuCount} ({stats.total > 0 ? Math.round((stats.tamuCount / stats.total) * 100) : 0}%)
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-xs pt-0.5">
                              <span className="flex items-center gap-2 text-gray-650">
                                <span className="w-2.5 h-2.5 bg-amber-500 rounded-full"></span>
                                <span>Mekanik / Vendor / Resiko Tinggi</span>
                              </span>
                              <span className="font-bold text-gray-800">
                                {stats.vendorCount} ({stats.total > 0 ? Math.round((stats.vendorCount / stats.total) * 100) : 0}%)
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-white p-6 rounded-xl border border-gray-150 shadow-sm hover:shadow-md transition-all">
                        <div className="border-b border-gray-100 pb-3 mb-4 flex items-center justify-between">
                          <div>
                            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                              <span className="w-2.5 h-5 bg-teal-500 rounded-full block"></span>
                              Inductions per Day (Last 7 Days)
                            </h3>
                            <p className="text-[11px] text-gray-500 mt-0.5">Frekuensi harian pendaftaran induksi peserta</p>
                          </div>
                        </div>
                        <div className="h-[280px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats.dailyData} margin={{ top: 20, right: 10, left: -10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={true} vertical={false} />
                              <XAxis dataKey="name" fontSize={11} stroke="#4B5563" tickLine={false} />
                              <YAxis allowDecimals={false} stroke="#9CA3AF" fontSize={11} tickLine={false} />
                              <Tooltip 
                                cursor={{ fill: '#F9FAFB' }}
                                contentStyle={{ 
                                  backgroundColor: '#FFFFFF', 
                                  border: '1px solid #E5E7EB', 
                                  borderRadius: '8px', 
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' 
                                }} 
                              />
                              <Bar dataKey="count" fill="#10B981" radius={[6, 6, 0, 0]} barSize={24} name="Peserta">
                                <LabelList dataKey="count" position="top" style={{ fill: '#111827', fontSize: '11px', fontWeight: 'bold' }} />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="bg-white p-6 rounded-xl border border-gray-150 shadow-sm hover:shadow-md transition-all">
                        <div className="border-b border-gray-100 pb-3 mb-4 flex items-center justify-between">
                          <div>
                            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                              <span className="w-2.5 h-5 bg-blue-600 rounded-full block"></span>
                              Distribusi Peserta per Area / Total Area Distribution
                            </h3>
                            <p className="text-[11px] text-gray-500 mt-0.5">Saringan jumlah peserta keselamatan di masing-masing wilayah kerja</p>
                          </div>
                        </div>
                        <div className="h-[280px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart 
                              layout="vertical" 
                              data={stats.areaData} 
                              margin={{ top: 10, right: 40, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} vertical={true} />
                              <XAxis type="number" allowDecimals={false} stroke="#9CA3AF" fontSize={11} tickLine={false} />
                              <YAxis 
                                type="category" 
                                dataKey="name" 
                                stroke="#4B5563" 
                                fontSize={11} 
                                tickLine={false} 
                                width={110}
                              />
                              <Tooltip 
                                cursor={{ fill: '#F9FAFB' }}
                                contentStyle={{ 
                                  backgroundColor: '#FFFFFF', 
                                  border: '1px solid #E5E7EB', 
                                  borderRadius: '8px', 
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' 
                                }} 
                                formatter={(value) => [`${value} Orang`, 'Total Peserta']} 
                              />
                              <Bar dataKey="value" fill="#3B82F6" radius={[0, 6, 6, 0]} barSize={20}>
                                <LabelList 
                                  dataKey="value" 
                                  position="right" 
                                  style={{ fill: '#1F2937', fontSize: '12px', fontWeight: 'bold' }} 
                                  formatter={(v: any) => `${v} Org`} 
                                />
                                {stats.areaData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* HISTORY TAB */}
            {activeTab === 'history' && (
              <div className="bg-white shadow rounded-lg overflow-hidden animate-fade-in-up">
                
                {/* Filter Section */}
                <div className="p-5 bg-gray-50 border-b border-gray-200">
                  <div className="space-y-4">
                    {/* First Row: Search Bar & Area Select */}
                    <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                      <div className="flex items-center gap-1.5 text-gray-650 mr-1">
                        <Filter size={18} />
                        <span className="font-semibold text-sm whitespace-nowrap">Filter & Cari:</span>
                      </div>
                      
                      {/* Search Bar Input */}
                      <div className="flex-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                          <Search size={16} />
                        </div>
                        <input 
                          type="text"
                          placeholder="Cari nama peserta, jabatan, perusahaan, atau instruktur..."
                          value={filterSearch}
                          onChange={(e) => setFilterSearch(e.target.value)}
                          className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm bg-white text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all shadow-sm"
                        />
                      </div>

                      {/* Area Select Dropdown */}
                      <div className="min-w-[180px]">
                        <select
                          value={filterArea}
                          onChange={(e) => setFilterArea(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none shadow-sm cursor-pointer"
                        >
                          <option value="">Semua Area (All Areas)</option>
                          {(areasList.length > 0 ? areasList : AREAS).map(area => (
                            <option key={area} value={area}>{area}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Second Row: Date Range & Score Range */}
                    <div className="flex flex-wrap items-center gap-y-3 gap-x-6 pt-3 border-t border-gray-200">
                      {/* Date Range Inputs */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-bold text-gray-650 uppercase tracking-tight">Rentang Tanggal (Date):</span>
                        <div className="flex items-center gap-1.5">
                          <input 
                            type="date" 
                            value={filterStartDate}
                            onChange={(e) => setFilterStartDate(e.target.value)}
                            className="border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white text-sm text-gray-850 focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm"
                          />
                          <span className="text-xs text-gray-400 font-medium">s/d</span>
                          <input 
                            type="date" 
                            value={filterEndDate}
                            onChange={(e) => setFilterEndDate(e.target.value)}
                            className="border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white text-sm text-gray-855 focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm"
                          />
                        </div>
                      </div>

                      {/* Score Range Inputs for Filtering Post-Test Score */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-655 uppercase tracking-tight">Nilai Post-Test (Score):</span>
                        <div className="flex items-center gap-1.5">
                          <input 
                            type="number" 
                            min="0"
                            max="100"
                            placeholder="Min"
                            value={filterMinScore}
                            onChange={(e) => setFilterMinScore(e.target.value)}
                            className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-16 bg-white text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm text-center font-semibold"
                          />
                          <span className="text-xs text-gray-400 font-bold">-</span>
                          <input 
                            type="number" 
                            min="0"
                            max="100"
                            placeholder="Max"
                            value={filterMaxScore}
                            onChange={(e) => setFilterMaxScore(e.target.value)}
                            className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-16 bg-white text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm text-center font-semibold"
                          />
                        </div>
                      </div>

                      {/* Clear Filters Button */}
                      {(filterStartDate || filterEndDate || filterArea || filterSearch || filterMinScore || filterMaxScore) && (
                        <button 
                          onClick={clearFilters}
                          className="bg-red-50 hover:bg-red-100 text-red-700 font-semibold text-xs border border-red-200 px-3 py-1.5 rounded-lg flex items-center gap-1 transition shadow-sm ml-auto cursor-pointer"
                        >
                          <XCircle size={14} /> Reset Filter / Reset
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white">
                  <h3 className="text-lg font-medium text-gray-900">
                    Participant History 
                    <span className="ml-2 text-sm text-gray-500 font-normal">
                      ({filteredRecords.length} records found)
                    </span>
                  </h3>
                  <button 
                    onClick={exportToCSV}
                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
                  >
                    <Download size={16} /> Export CSV
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status Pengunjung</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Area</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Instructor</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Post-Test Score</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sertifikat</th>
                        {role === 'EDITOR' && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredRecords.map((record) => (
                        <tr key={record.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.inductionDate}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => setSelectedRecordForDetails(record)}
                              className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer flex items-center gap-1.5 focus:outline-none text-left"
                            >
                              <Eye size={14} className="text-blue-500 flex-shrink-0" />
                              <span>{record.fullName}</span>
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {record.visitorType === 'TAMU' ? (
                              <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                                Tamu / Guest
                              </span>
                            ) : (
                              <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                                Mekanik / Vendor / Pekerja Risiko Tinggi
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.jobTitle || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.company || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.area}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.instructor}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                            {record.visitorType === 'TAMU' ? (
                              <span className="text-xs text-gray-400 font-semibold bg-gray-100 px-2 py-0.5 rounded">Dilewati / Skipped</span>
                            ) : record.postTestScore !== undefined ? (
                              `${record.postTestScore} / 100`
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {record.visitorType === 'TAMU' ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                Selesai / Guest
                              </span>
                            ) : record.postTestScore !== undefined && record.postTestPassed === false ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                Gagal / Failed
                              </span>
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                Lulus / Passed
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {record.visitorType === 'TAMU' || record.postTestPassed !== false ? (
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => openCertificatePreview('INDUCTION', record)}
                                  className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 px-2.5 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition shadow-2xs"
                                  title="Preview sertifikat di modal"
                                >
                                  <Eye size={13} /> Preview
                                </button>
                                <button
                                  onClick={() => generateInductionPDF(record)}
                                  className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 px-2.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition shadow-2xs"
                                  title="Download PDF sertifikat langsung"
                                >
                                  <Download size={13} /> Download
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 italic font-medium">Tidak Ada / Gagal</span>
                            )}
                          </td>
                          {role === 'EDITOR' && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => handleDeleteRecord(record.id, record.fullName)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded transition"
                                title="Hapus data"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                      {filteredRecords.length === 0 && (
                        <tr>
                          <td colSpan={10} className="px-6 py-4 text-center text-gray-500">No records found matching filters.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* MANDATORY TRAINING HISTORY TAB */}
            {activeTab === 'mandatory_history' && (() => {
              const filteredMT = getFilteredMTRecords();
              const totalMT = mandatoryRecords.length;
              const passedMT = mandatoryRecords.filter(r => r.passed).length;
              const passRateMT = totalMT > 0 ? Math.round((passedMT / totalMT) * 100) : 0;

              // Statistik Hari Ini (Today's Training Statistics)
              const todayStrUTC = new Date().toISOString().split('T')[0];
              const todayStrLocal = new Date().toLocaleDateString('en-CA');
              const isTodayRecord = (r: MandatoryTrainingRecord) => {
                if (r.completionDate && (r.completionDate === todayStrLocal || r.completionDate === todayStrUTC)) return true;
                if (r.timestamp) {
                  const d = new Date(r.timestamp);
                  const dLocal = d.toLocaleDateString('en-CA');
                  const dUTC = d.toISOString().split('T')[0];
                  return dLocal === todayStrLocal || dLocal === todayStrUTC || dUTC === todayStrLocal || dUTC === todayStrUTC;
                }
                return false;
              };

              const todayRecords = mandatoryRecords.filter(isTodayRecord);
              const todayRecordsCount = todayRecords.length;
              const todayUniquePersonsSet = new Set(
                todayRecords.map(r => (r.nik && r.nik.trim()) || (r.fullName && r.fullName.trim()) || r.id)
              );
              const todayPersonsCount = todayUniquePersonsSet.size;
              const todayPassedCount = todayRecords.filter(r => r.passed).length;
              const isFilteringToday = mtFilterStartDate === todayStrLocal && mtFilterEndDate === todayStrLocal;

              return (
                <div className="space-y-6 animate-fade-in-up">
                  
                  {/* Top Stats Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {/* Card 1: Total Training Hari Ini */}
                    <div 
                      onClick={() => {
                        if (isFilteringToday) {
                          setMtFilterStartDate('');
                          setMtFilterEndDate('');
                        } else {
                          setMtFilterStartDate(todayStrLocal);
                          setMtFilterEndDate(todayStrLocal);
                        }
                      }}
                      className={`p-5 rounded-2xl border shadow-xs flex items-center justify-between cursor-pointer transition-all duration-200 relative overflow-hidden group ${
                        isFilteringToday
                          ? 'bg-blue-600 text-white border-blue-600 ring-2 ring-blue-400'
                          : 'bg-white hover:bg-blue-50/50 border-blue-200 hover:border-blue-300'
                      }`}
                      title={isFilteringToday ? "Sedang menyaring hari ini - Klik untuk reset" : "Klik untuk saring data training hari ini"}
                    >
                      <div className="min-w-0 pr-2">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-xs font-bold uppercase tracking-wider truncate ${
                            isFilteringToday ? 'text-blue-100' : 'text-blue-700'
                          }`}>
                            Training Hari Ini
                          </p>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-extrabold ${
                            isFilteringToday 
                              ? 'bg-white text-blue-700' 
                              : 'bg-blue-100 text-blue-700 animate-pulse'
                          }`}>
                            Hari Ini
                          </span>
                        </div>
                        <h3 className={`text-2xl font-extrabold mt-1 ${
                          isFilteringToday ? 'text-white' : 'text-blue-900'
                        }`}>
                          {todayPersonsCount} <span className={`text-xs font-normal ${
                            isFilteringToday ? 'text-blue-100' : 'text-gray-500'
                          }`}>orang</span>
                        </h3>
                        <p className={`text-[11px] font-medium mt-0.5 truncate ${
                          isFilteringToday ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {todayRecordsCount} sesi modul ({todayPassedCount} lulus)
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-xs transition-transform group-hover:scale-105 ${
                        isFilteringToday
                          ? 'bg-white/20 text-white'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        <Calendar className="w-6 h-6" />
                      </div>
                    </div>

                    {/* Card 2: Total Penyelesaian */}
                    <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Penyelesaian</p>
                        <h3 className="text-2xl font-extrabold text-gray-900 mt-1">{totalMT} <span className="text-xs text-gray-500 font-normal">modul</span></h3>
                        <p className="text-[11px] text-gray-400 font-medium mt-0.5">Semua riwayat modul</p>
                      </div>
                      <div className="w-12 h-12 bg-amber-100 text-amber-700 rounded-2xl flex items-center justify-center shrink-0">
                        <Award className="w-6 h-6" />
                      </div>
                    </div>

                    {/* Card 3: Peserta Lulus */}
                    <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Peserta Lulus</p>
                        <h3 className="text-2xl font-extrabold text-emerald-600 mt-1">{passedMT} <span className="text-xs text-gray-500 font-normal">peserta</span></h3>
                        <p className="text-[11px] text-gray-400 font-medium mt-0.5">Memenuhi passing grade</p>
                      </div>
                      <div className="w-12 h-12 bg-emerald-100 text-emerald-700 rounded-2xl flex items-center justify-center shrink-0">
                        <CheckCircle className="w-6 h-6" />
                      </div>
                    </div>

                    {/* Card 4: Tingkat Kelulusan */}
                    <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Tingkat Kelulusan</p>
                        <h3 className="text-2xl font-extrabold text-blue-600 mt-1">{passRateMT}%</h3>
                        <p className="text-[11px] text-gray-400 font-medium mt-0.5">Rasio lulus vs total sesi</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-2xl flex items-center justify-center shrink-0">
                        <GraduationCap className="w-6 h-6" />
                      </div>
                    </div>

                    {/* Card 5: Modul Wajib */}
                    <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Modul Wajib</p>
                        <h3 className="text-2xl font-extrabold text-purple-600 mt-1">{MANDATORY_MODULES.length} <span className="text-xs text-gray-500 font-normal">modul</span></h3>
                        <p className="text-[11px] text-gray-400 font-medium mt-0.5">Standar 7 modul safety</p>
                      </div>
                      <div className="w-12 h-12 bg-purple-100 text-purple-700 rounded-2xl flex items-center justify-center shrink-0">
                        <BookOpen className="w-6 h-6" />
                      </div>
                    </div>
                  </div>

                  {/* Module Breakdown Progress */}
                  <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-xs space-y-4">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Ringkasan Pengerjaan Per Modul Wajib
                      </h4>
                      <span className="text-[10px] text-gray-400 font-semibold italic">Klik kartu modul untuk melihat detail nama peserta</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 gap-3">
                      {MANDATORY_MODULES.map(m => {
                        const passedRecs = mandatoryRecords.filter(r => r.moduleId === m.id && r.passed);
                        const failedRecs = mandatoryRecords.filter(r => r.moduleId === m.id && !r.passed);
                        const countPassed = passedRecs.length;
                        const countFailed = failedRecs.length;
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => setModuleBreakdownModal({ moduleId: m.id, moduleTitle: m.title, moduleBadge: m.badge })}
                            className="bg-slate-50 hover:bg-amber-50 border border-slate-200 hover:border-amber-300 rounded-xl p-3 space-y-1 text-left transition cursor-pointer group"
                            title="Klik untuk melihat daftar nama peserta"
                          >
                            <span className="text-[10px] font-extrabold text-amber-700 block uppercase truncate">{m.badge}</span>
                            <h5 className="text-xs font-bold text-gray-900 truncate group-hover:text-amber-800" title={m.title}>{m.title}</h5>
                            <div className="pt-1 flex flex-col gap-0.5">
                              <p className="text-xs font-extrabold text-emerald-700">{countPassed} <span className="text-[10px] text-gray-500 font-normal">Lulus</span></p>
                              {countFailed > 0 ? (
                                <p className="text-[10px] font-extrabold text-red-600">{countFailed} Tidak Lulus</p>
                              ) : (
                                <p className="text-[10px] text-gray-400 font-normal">0 Tidak Lulus</p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Grafik & Progress Pencapaian 7 Modul Wajib Per Orang */}
                  {(() => {
                    // Aggregate records by person (NIK / NRP or Full Name)
                    const personMap = new Map<string, {
                      key: string;
                      fullName: string;
                      nik: string;
                      company: string;
                      position: string;
                      workArea: string;
                      modulesMap: Record<string, { passed: boolean; score: number; completionDate: string }>;
                      allAttempts: MandatoryTrainingRecord[];
                    }>();

                    mandatoryRecords.forEach((rec) => {
                      const nikClean = rec.nik ? rec.nik.trim() : '';
                      const key = (nikClean !== '' && nikClean !== '-') ? nikClean : rec.fullName.trim().toLowerCase();
                      if (!key) return;

                      if (!personMap.has(key)) {
                        personMap.set(key, {
                          key,
                          fullName: rec.fullName,
                          nik: (rec.nik && rec.nik !== '-') ? rec.nik : '-',
                          company: (rec.company && rec.company !== '-') ? rec.company : '-',
                          position: (rec.position && rec.position !== '-') ? rec.position : '-',
                          workArea: (rec.workArea && rec.workArea !== '-') ? rec.workArea : '-',
                          modulesMap: {},
                          allAttempts: []
                        });
                      }

                      const person = personMap.get(key)!;
                      if (rec.fullName) person.fullName = rec.fullName;
                      if (rec.nik && rec.nik !== '-') person.nik = rec.nik;
                      if (rec.company && rec.company !== '-') person.company = rec.company;
                      if (rec.position && rec.position !== '-') person.position = rec.position;
                      if (rec.workArea && rec.workArea !== '-') person.workArea = rec.workArea;

                      person.allAttempts.push(rec);

                      const existingMod = person.modulesMap[rec.moduleId];
                      if (!existingMod || (rec.passed && !existingMod.passed) || (rec.passed === existingMod.passed && rec.score > existingMod.score)) {
                        person.modulesMap[rec.moduleId] = {
                          passed: rec.passed,
                          score: rec.score,
                          completionDate: rec.completionDate
                        };
                      }
                    });

                    let personList = Array.from(personMap.values()).map(p => {
                      const moduleEntries = Object.values(p.modulesMap);
                      const passedModules = moduleEntries.filter(m => m.passed);
                      const failedModules = moduleEntries.filter(m => !m.passed);
                      const totalScores = moduleEntries.reduce((sum, m) => sum + m.score, 0);
                      const avgScore = moduleEntries.length > 0 ? Math.round(totalScores / moduleEntries.length) : 0;
                      const passedCount = passedModules.length;
                      const failedCount = failedModules.length;
                      const unpassedCount = MANDATORY_MODULES.length - passedCount;
                      const hasFailedRecord = p.allAttempts.some(a => !a.passed) || failedCount > 0;

                      return {
                        ...p,
                        passedCount,
                        failedCount,
                        unpassedCount,
                        hasFailedRecord,
                        avgScore,
                        percentComplete: Math.round((passedCount / MANDATORY_MODULES.length) * 100)
                      };
                    });

                    // Stats summary counts
                    const totalPeserta = personList.length;
                    const perfectCount = personList.filter(p => p.passedCount === MANDATORY_MODULES.length).length;
                    const inProgressCount = personList.filter(p => p.passedCount > 0 && p.passedCount < MANDATORY_MODULES.length).length;
                    const unpassedPersonCount = personList.filter(p => p.passedCount === 0).length;
                    const failedRecordPersonCount = personList.filter(p => p.hasFailedRecord).length;

                    // Area Filter
                    if (mtPersonAreaFilter !== 'all') {
                      personList = personList.filter(p => {
                        const area = p.workArea || 'Yard Sukapura';
                        return area.toLowerCase() === mtPersonAreaFilter.toLowerCase();
                      });
                    }

                    // Status Filter
                    if (mtPersonStatusFilter === 'perfect') {
                      personList = personList.filter(p => p.passedCount === MANDATORY_MODULES.length);
                    } else if (mtPersonStatusFilter === 'progress') {
                      personList = personList.filter(p => p.passedCount > 0 && p.passedCount < MANDATORY_MODULES.length);
                    } else if (mtPersonStatusFilter === 'unpassed') {
                      personList = personList.filter(p => p.passedCount === 0);
                    } else if (mtPersonStatusFilter === 'failed') {
                      personList = personList.filter(p => p.hasFailedRecord || p.passedCount < MANDATORY_MODULES.length);
                    }

                    // Search filter
                    if (mtPersonSearch.trim()) {
                      const q = mtPersonSearch.toLowerCase().trim();
                      personList = personList.filter(p =>
                        p.fullName.toLowerCase().includes(q) ||
                        p.nik.toLowerCase().includes(q) ||
                        p.company.toLowerCase().includes(q) ||
                        p.position.toLowerCase().includes(q) ||
                        p.workArea.toLowerCase().includes(q)
                      );
                    }

                    // Sort
                    if (mtPersonSortBy === 'passed') {
                      personList.sort((a, b) => b.passedCount - a.passedCount || b.avgScore - a.avgScore);
                    } else if (mtPersonSortBy === 'unpassed') {
                      personList.sort((a, b) => b.unpassedCount - a.unpassedCount || a.passedCount - b.passedCount);
                    } else if (mtPersonSortBy === 'score') {
                      personList.sort((a, b) => b.avgScore - a.avgScore || b.passedCount - a.passedCount);
                    } else if (mtPersonSortBy === 'name') {
                      personList.sort((a, b) => a.fullName.localeCompare(b.fullName));
                    }

                    const exportMTMatrixToCSV = () => {
                      const moduleBadges = MANDATORY_MODULES.map(m => m.badge);
                      const headers = ['Nama Pekerja', 'NRP / NIK', 'Posisi / Jabatan', 'Perusahaan/Dept', 'Area Kerja', ...moduleBadges, 'Total Lulus', 'Status'];
                      const rows = personList.map(person => {
                        const moduleCells = MANDATORY_MODULES.map(m => {
                          const rec = mandatoryRecords.find(r => r.fullName === person.fullName && r.moduleId === m.id);
                          if (!rec) return '-';
                          return rec.passed ? 'LULUS' : 'TIDAK LULUS';
                        });
                        return [
                          person.fullName,
                          person.nik || '-',
                          person.position || '-',
                          person.company || '-',
                          person.workArea || 'Yard Sukapura',
                          ...moduleCells,
                          String(person.passedCount),
                          person.passedCount === MANDATORY_MODULES.length ? 'LULUS SEMUA' : 'BELUM LENGKAP'
                        ];
                      });

                      const csvRows = [headers, ...rows].map(row =>
                        row.map(value => {
                          const strVal = String(value);
                          const escaped = strVal.replace(/"/g, '""');
                          if (escaped.includes(',') || escaped.includes('\n') || escaped.includes('"')) {
                            return `"${escaped}"`;
                          }
                          return escaped;
                        }).join(',')
                      );

                      const csvContent = "\uFEFF" + csvRows.join("\n");
                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.setAttribute("href", url);
                      link.setAttribute("download", `matriks_7_safety_mandatory_${new Date().toISOString().slice(0,10)}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                    };

                    const exportMTMatrixToExcel = () => {
                      const moduleBadges = MANDATORY_MODULES.map(m => m.badge);
                      const headerRow = ['Nama Pekerja', 'NRP / NIK', 'Posisi / Jabatan', 'Perusahaan/Dept', 'Area Kerja', ...moduleBadges, 'Total Lulus', 'Status'];
                      const dataRows = personList.map(person => {
                        const moduleCells = MANDATORY_MODULES.map(m => {
                          const rec = mandatoryRecords.find(r => r.fullName === person.fullName && r.moduleId === m.id);
                          if (!rec) return '-';
                          return rec.passed ? 'LULUS' : 'TIDAK LULUS';
                        });
                        return [
                          person.fullName,
                          person.nik || '-',
                          person.position || '-',
                          person.company || '-',
                          person.workArea || 'Yard Sukapura',
                          ...moduleCells,
                          person.passedCount,
                          person.passedCount === MANDATORY_MODULES.length ? 'LULUS SEMUA' : 'BELUM LENGKAP'
                        ];
                      });

                      const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
                      ws['!cols'] = [
                        { wch: 28 }, { wch: 18 }, { wch: 20 }, { wch: 22 }, { wch: 18 },
                        ...moduleBadges.map(() => ({ wch: 14 })),
                        { wch: 12 }, { wch: 16 }
                      ];
                      const wb = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(wb, ws, 'Matriks 7 Safety Mandatory');
                      XLSX.writeFile(wb, `matriks_7_safety_mandatory_${new Date().toISOString().slice(0,10)}.xlsx`);
                    };

                    const exportMTMatrixToPDF = async () => {
                      await generateMTMatrixPDF({
                        personList,
                        modules: MANDATORY_MODULES,
                        selectedArea: mtPersonAreaFilter,
                        selectedStatus: mtPersonStatusFilter,
                        searchQuery: mtPersonSearch
                      });
                    };

                    // Prepare bar chart data (limit top 15 for chart readability if search is clear)
                    const chartData = personList.slice(0, 15).map(p => ({
                      displayName: p.fullName.length > 12 ? `${p.fullName.substring(0, 12)}...` : p.fullName,
                      fullName: p.fullName,
                      nik: p.nik,
                      lulusCount: p.passedCount,
                      target: MANDATORY_MODULES.length,
                      avgScore: p.avgScore,
                      percent: p.percentComplete
                    }));

                    return (
                      <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-xs space-y-6">
                        {/* Title & Controls */}
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-gray-100 pb-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <div className="p-2 bg-amber-100 text-amber-800 rounded-xl">
                                <Users className="w-5 h-5" />
                              </div>
                              <div>
                                <h3 className="font-extrabold text-gray-900 text-lg">
                                  Grafik & Progress Pencapaian {MANDATORY_MODULES.length} Modul Wajib Per Orang
                                </h3>
                                <p className="text-xs text-gray-500">
                                  Pencapaian penyelesaian {MANDATORY_MODULES.length} modul keselamatan kerja untuk masing-masing pekerja (termasuk status Lulus, Dalam Proses, dan Tidak Lulus).
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Stats Badges */}
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="px-3 py-1.5 rounded-xl bg-blue-50 border border-blue-200 text-blue-800 text-xs font-bold flex items-center gap-1.5 shadow-2xs">
                              <Calendar className="w-3.5 h-3.5 text-blue-600" />
                              <span>{todayPersonsCount} Training Hari Ini</span>
                            </div>
                            <div className="px-3 py-1.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-800 text-xs font-bold flex items-center gap-1.5">
                              <Users className="w-3.5 h-3.5 text-slate-600" />
                              <span>{totalPeserta} Total Peserta</span>
                            </div>
                            <div className="px-3 py-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex items-center gap-1.5">
                              <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                              <span>{perfectCount} Lulus Sempurna ({MANDATORY_MODULES.length}/{MANDATORY_MODULES.length})</span>
                            </div>
                            <div className="px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold flex items-center gap-1.5">
                              <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                              <span>{inProgressCount} Dalam Proses</span>
                            </div>
                            {failedRecordPersonCount > 0 && (
                              <div className="px-3 py-1.5 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-bold flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                                <span>{failedRecordPersonCount} Ada Catatan Tidak Lulus</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Search, Filter Status & Sort Bar */}
                        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
                          <div className="relative flex-1">
                            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                            <input
                              type="text"
                              value={mtPersonSearch}
                              onChange={(e) => setMtPersonSearch(e.target.value)}
                              placeholder="Cari Nama, NRP/NIK, atau Perusahaan..."
                              className="w-full pl-9 pr-3 py-1.5 bg-white border border-gray-300 rounded-lg text-xs font-semibold text-gray-800 outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>

                          <div className="flex flex-wrap items-center gap-2 shrink-0">
                            <span className="text-xs font-bold text-gray-600">Filter Area:</span>
                            <select
                              value={mtPersonAreaFilter}
                              onChange={(e) => setMtPersonAreaFilter(e.target.value)}
                              className="bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-800 outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer"
                            >
                              <option value="all">Semua Area ({totalPeserta})</option>
                              {mtAvailableAreas.map(area => (
                                <option key={area} value={area}>{area}</option>
                              ))}
                            </select>

                            <span className="text-xs font-bold text-gray-600">Filter Status:</span>
                            <select
                              value={mtPersonStatusFilter}
                              onChange={(e) => setMtPersonStatusFilter(e.target.value)}
                              className="bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-800 outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer"
                            >
                              <option value="all">Semua Status Peserta ({totalPeserta})</option>
                              <option value="perfect">Lulus Sempurna 7/7 ({perfectCount})</option>
                              <option value="progress">Dalam Proses (1-{MANDATORY_MODULES.length - 1} Modul) ({inProgressCount})</option>
                              <option value="unpassed">Belum Lulus 0 Modul ({unpassedPersonCount})</option>
                              <option value="failed">Tercatat Tidak Lulus / Perlu Remidi ({failedRecordPersonCount})</option>
                            </select>

                            <span className="text-xs font-bold text-gray-600 ml-2">Urutkan:</span>
                            <select
                              value={mtPersonSortBy}
                              onChange={(e) => setMtPersonSortBy(e.target.value as any)}
                              className="bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-800 outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer"
                            >
                              <option value="passed">Modul Terbanyak Lulus (7/7)</option>
                              <option value="unpassed">Modul Terbanyak Belum Lulus</option>
                              <option value="score">Rata-Rata Skor Post-Test</option>
                              <option value="name">Nama (A - Z)</option>
                            </select>
                          </div>
                        </div>

                        {/* Chart Component */}
                        {chartData.length > 0 ? (
                          <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-200/80 space-y-2">
                            <div className="flex items-center justify-between px-1">
                              <h4 className="text-xs font-extrabold text-gray-700 uppercase tracking-wider">
                                Grafik Jumlah Modul Lulus (Skala 0 - {MANDATORY_MODULES.length} Modul Wajib)
                              </h4>
                              <span className="text-[11px] text-gray-500 font-medium">
                                Menampilkan {chartData.length} dari {personList.length} peserta
                              </span>
                            </div>

                            <div className="h-64 sm:h-72 w-full pt-2">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} margin={{ top: 20, right: 20, left: -20, bottom: 25 }}>
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                  <XAxis
                                    dataKey="displayName"
                                    tick={{ fill: '#475569', fontSize: 11, fontWeight: 600 }}
                                    interval={0}
                                    angle={-25}
                                    textAnchor="end"
                                  />
                                  <YAxis domain={[0, MANDATORY_MODULES.length]} ticks={[0, 1, 2, 3, 4, 5, 6, 7]} tick={{ fill: '#475569', fontSize: 11 }} />
                                  <Tooltip
                                    cursor={{ fill: '#F1F5F9' }}
                                    content={({ active, payload }) => {
                                      if (active && payload && payload.length) {
                                        const d = payload[0].payload;
                                        return (
                                          <div className="bg-slate-900 text-white p-3 rounded-xl shadow-xl text-xs space-y-1.5 border border-slate-700">
                                            <div className="font-extrabold text-amber-400">{d.fullName}</div>
                                            <div className="text-[11px] text-slate-300">NRP/NIK: {d.nik}</div>
                                            <div className="pt-1 border-t border-slate-700 flex justify-between items-center gap-4">
                                              <span>Modul Lulus:</span>
                                              <strong className="text-emerald-400 font-extrabold">{d.lulusCount} / {MANDATORY_MODULES.length} Modul ({d.percent}%)</strong>
                                            </div>
                                            <div className="flex justify-between items-center gap-4">
                                              <span>Rata-Rata Skor:</span>
                                              <strong className="text-blue-300 font-extrabold">{d.avgScore} / 100</strong>
                                            </div>
                                          </div>
                                        );
                                      }
                                      return null;
                                    }}
                                  />
                                  <Bar dataKey="lulusCount" radius={[6, 6, 0, 0]} barSize={28}>
                                    <LabelList
                                      dataKey="lulusCount"
                                      position="top"
                                      formatter={(v: any) => `${v}/${MANDATORY_MODULES.length}`}
                                      style={{ fill: '#1E293B', fontSize: '11px', fontWeight: 'bold' }}
                                    />
                                    {chartData.map((entry, index) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={
                                          entry.lulusCount === MANDATORY_MODULES.length
                                            ? '#10B981'
                                            : entry.lulusCount > 0
                                            ? '#F59E0B'
                                            : '#EF4444'
                                        }
                                      />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 bg-slate-50 rounded-xl border border-slate-200 text-gray-500 text-xs font-semibold">
                            Belum ada data pengerjaan per orang yang sesuai pencarian atau filter status.
                          </div>
                        )}

                        {/* Matrix Breakdown per Person Table */}
                        <div className="space-y-3">
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 px-1">
                            <div className="flex items-center gap-2">
                              <h4 className="text-xs font-extrabold text-gray-700 uppercase tracking-wider">
                                Matriks Pencapaian Detail {MANDATORY_MODULES.length} Safety Mandatory Per Peserta
                              </h4>
                              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                {personList.length} Peserta
                              </span>
                            </div>

                            <div className="flex flex-wrap items-center gap-2 self-start lg:self-auto">
                              {/* Filter Area Langsung di Header Matriks */}
                              <div className="flex items-center gap-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-300 rounded-lg px-2.5 py-1 text-xs shadow-2xs transition">
                                <MapPin className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                <span className="font-bold text-gray-700 whitespace-nowrap">Filter Area:</span>
                                <select
                                  value={mtPersonAreaFilter}
                                  onChange={(e) => setMtPersonAreaFilter(e.target.value)}
                                  className="bg-transparent font-bold text-gray-800 outline-none cursor-pointer pr-1"
                                  title="Filter matriks berdasarkan area kerja"
                                >
                                  <option value="all">Semua Area ({totalPeserta})</option>
                                  {mtAvailableAreas.map(area => (
                                    <option key={area} value={area}>{area}</option>
                                  ))}
                                </select>
                              </div>

                              {/* Download PDF Matriks */}
                              <button
                                type="button"
                                onClick={exportMTMatrixToPDF}
                                disabled={personList.length === 0}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-300 text-xs font-extrabold transition cursor-pointer shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Download matriks pencapaian detail sebagai PDF (sesuai format tampilan)"
                              >
                                <FileText className="w-3.5 h-3.5 text-rose-600" />
                                <span>PDF</span>
                              </button>

                              <button
                                type="button"
                                onClick={exportMTMatrixToCSV}
                                disabled={personList.length === 0}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 text-xs font-extrabold transition cursor-pointer shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Download matriks pencapaian sebagai CSV"
                              >
                                <Download className="w-3.5 h-3.5" />
                                <span>CSV</span>
                              </button>
                              <button
                                type="button"
                                onClick={exportMTMatrixToExcel}
                                disabled={personList.length === 0}
                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-300 text-xs font-extrabold transition cursor-pointer shadow-2xs disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Download matriks pencapaian sebagai Excel"
                              >
                                <Download className="w-3.5 h-3.5" />
                                <span>Excel</span>
                              </button>
                              {personList.length > 5 && (
                                <button
                                  type="button"
                                  onClick={() => setShowAllPersonMatrix(!showAllPersonMatrix)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 text-xs font-extrabold transition cursor-pointer shadow-2xs"
                                >
                                  {showAllPersonMatrix ? (
                                    <>
                                      <span>Sembunyikan (Tampilkan 5 Teratas)</span>
                                    </>
                                  ) : (
                                    <>
                                      <span>Lihat Semua ({personList.length} Peserta) ↓</span>
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-2xs">
                            <table className="min-w-full divide-y divide-gray-200 text-xs">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="px-4 py-3 text-left font-extrabold text-gray-700 uppercase tracking-wider">Nama & NRP/NIK</th>
                                  <th className="px-4 py-3 text-left font-extrabold text-gray-700 uppercase tracking-wider">Posisi / Area</th>
                                  <th className="px-4 py-3 text-center font-extrabold text-gray-700 uppercase tracking-wider">Progress Lulus</th>
                                  <th className="px-4 py-3 text-center font-extrabold text-gray-700 uppercase tracking-wider">
                                    Status {MANDATORY_MODULES.length} Modul Keselamatan Wajib
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200 font-medium text-gray-800">
                                {(showAllPersonMatrix ? personList : personList.slice(0, 5)).map(person => (
                                  <tr key={person.key} className="hover:bg-amber-50/30 transition">
                                    {/* Name & NRP */}
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="font-bold text-gray-900">{person.fullName}</div>
                                      <div className="text-[10px] text-gray-500 font-mono">NRP: {person.nik}</div>
                                    </td>

                                    {/* Position & Area */}
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="font-semibold text-gray-800">{person.position}</div>
                                      <div className="text-[10px] text-gray-500">{person.company} • {person.workArea}</div>
                                    </td>

                                    {/* Overall Progress */}
                                    <td className="px-4 py-3 whitespace-nowrap text-center">
                                      <div className="inline-flex flex-col items-center min-w-[120px]">
                                        {person.passedCount === MANDATORY_MODULES.length ? (
                                          <span className="text-xs font-extrabold text-emerald-600">
                                            ✓ Lulus Sempurna ({person.passedCount}/{MANDATORY_MODULES.length})
                                          </span>
                                        ) : person.passedCount > 0 ? (
                                          <span className="text-xs font-extrabold text-amber-700">
                                            ⚡ Dalam Proses ({person.passedCount}/{MANDATORY_MODULES.length})
                                          </span>
                                        ) : (
                                          <span className="text-xs font-extrabold text-red-600">
                                            ⚠️ Belum Lulus (0/{MANDATORY_MODULES.length})
                                          </span>
                                        )}

                                        <div className="w-full bg-slate-200 h-2 rounded-full mt-1 overflow-hidden">
                                          <div
                                            className={`h-full transition-all rounded-full ${
                                              person.passedCount === MANDATORY_MODULES.length
                                                ? 'bg-emerald-500'
                                                : person.passedCount > 0
                                                ? 'bg-amber-500'
                                                : 'bg-red-500'
                                            }`}
                                            style={{ width: `${person.percentComplete}%` }}
                                          />
                                        </div>

                                        {person.hasFailedRecord && (
                                          <span className="text-[10px] font-bold text-red-600 mt-0.5">
                                            Tercatat Tidak Lulus ({person.failedCount} Modul)
                                          </span>
                                        )}
                                      </div>
                                    </td>

                                    {/* 7 Mandatory Modules Badges */}
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="flex items-center justify-center gap-1.5">
                                        {MANDATORY_MODULES.map(mod => {
                                          const modRecord = person.modulesMap[mod.id];
                                          const isPassed = modRecord?.passed;
                                          const isAttempted = !!modRecord;
                                          const score = modRecord?.score;

                                          return (
                                            <div
                                              key={mod.id}
                                              className={`px-2 py-1 rounded-lg text-[10px] font-bold border flex flex-col items-center justify-center min-w-[62px] ${
                                                isPassed
                                                  ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                                                  : isAttempted
                                                  ? 'bg-red-50 border-red-300 text-red-800'
                                                  : 'bg-slate-100 border-slate-200 text-slate-400'
                                              }`}
                                              title={`${mod.title}: ${isPassed ? `LULUS (Skor: ${score})` : isAttempted ? `TIDAK LULUS (Skor: ${score})` : 'Belum Dikerjakan'}`}
                                            >
                                              <span className="text-[9px] uppercase tracking-tighter truncate max-w-[58px]">
                                                {mod.badge}
                                              </span>
                                              <span className="font-extrabold text-[11px] mt-0.5">
                                                {isPassed ? `✓ ${score}` : isAttempted ? `✗ ${score}` : '-'}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </td>
                                  </tr>
                                ))}

                                {personList.length === 0 && (
                                  <tr>
                                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                                      Tidak ada data peserta ditemukan.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Footer action button if collapsed and more than 5 */}
                          {!showAllPersonMatrix && personList.length > 5 && (
                            <div className="text-center pt-1">
                              <button
                                type="button"
                                onClick={() => setShowAllPersonMatrix(true)}
                                className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition shadow-2xs border border-slate-300 cursor-pointer inline-flex items-center gap-1.5"
                              >
                                <span>Lihat Seluruh ({personList.length}) Peserta Matrix</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Filter Bar & Export */}
                  <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-xs space-y-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          Riwayat Pekerja - Safety Mandatory Training
                        </h3>
                        <p className="text-xs text-gray-500">
                          Daftar riwayat pengerjaan 7 modul safety mandatory kerja beserta nilai post-test.
                        </p>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
                        <button
                          type="button"
                          onClick={() => {
                            if (isFilteringToday) {
                              setMtFilterStartDate('');
                              setMtFilterEndDate('');
                            } else {
                              setMtFilterStartDate(todayStrLocal);
                              setMtFilterEndDate(todayStrLocal);
                            }
                          }}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl transition border shadow-2xs cursor-pointer ${
                            isFilteringToday
                              ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                              : 'bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200'
                          }`}
                          title={isFilteringToday ? "Klik untuk membatalkan filter hari ini" : "Filter hanya riwayat training hari ini"}
                        >
                          <Calendar className="w-4 h-4" />
                          <span>Hari Ini ({todayPersonsCount} Orang)</span>
                        </button>
                        {(mtFilterModule !== 'all' || mtFilterStatus !== 'all' || mtFilterArea !== 'all' || mtFilterSearch || mtFilterStartDate || mtFilterEndDate) && (
                          <button
                            type="button"
                            onClick={clearMTFilters}
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold rounded-xl transition border border-red-200 cursor-pointer shadow-2xs"
                            title="Reset semua filter riwayat"
                          >
                            <XCircle className="w-4 h-4" />
                            <span>Reset Filter</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={exportMTToCSV}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition shadow-xs cursor-pointer"
                        >
                          <Download className="w-4 h-4" />
                          <span>Export CSV Laporan</span>
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 pt-2">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Modul Training</label>
                        <select
                          value={mtFilterModule}
                          onChange={(e) => setMtFilterModule(e.target.value)}
                          className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 bg-white outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer"
                        >
                          <option value="all">Semua 7 Modul Wajib</option>
                          {MANDATORY_MODULES.map(m => (
                            <option key={m.id} value={m.id}>{m.badge} - {m.title}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Status Kelulusan</label>
                        <select
                          value={mtFilterStatus}
                          onChange={(e) => setMtFilterStatus(e.target.value)}
                          className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 bg-white outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer"
                        >
                          <option value="all">Semua Status</option>
                          <option value="passed">LULUS (Passed)</option>
                          <option value="failed">BELUM LULUS (Failed)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-amber-600" />
                          <span>Area Kerja</span>
                        </label>
                        <select
                          value={mtFilterArea}
                          onChange={(e) => setMtFilterArea(e.target.value)}
                          className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 bg-white outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer"
                        >
                          <option value="all">Semua Area Kerja</option>
                          {mtAvailableAreas.map(area => (
                            <option key={area} value={area}>{area}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Cari Nama / NIK / Perusahaan / Area</label>
                        <div className="relative">
                          <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                          <input
                            type="text"
                            value={mtFilterSearch}
                            onChange={(e) => setMtFilterSearch(e.target.value)}
                            placeholder="Cari kata kunci..."
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl text-xs font-semibold text-gray-800 outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Tanggal Mulai</label>
                        <input
                          type="date"
                          value={mtFilterStartDate}
                          onChange={(e) => setMtFilterStartDate(e.target.value)}
                          className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Tanggal Sampai</label>
                        <input
                          type="date"
                          value={mtFilterEndDate}
                          onChange={(e) => setMtFilterEndDate(e.target.value)}
                          className="w-full border border-gray-300 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Data Table */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-6 py-3.5 text-left text-[10px] font-extrabold text-gray-500 uppercase tracking-wider">Tanggal</th>
                            <th className="px-6 py-3.5 text-left text-[10px] font-extrabold text-gray-500 uppercase tracking-wider">Nama & NRP/NIK</th>
                            <th className="px-6 py-3.5 text-left text-[10px] font-extrabold text-gray-500 uppercase tracking-wider">Posisi / Dept / Area</th>
                            <th className="px-6 py-3.5 text-left text-[10px] font-extrabold text-gray-500 uppercase tracking-wider">Modul Training</th>
                            <th className="px-6 py-3.5 text-center text-[10px] font-extrabold text-gray-500 uppercase tracking-wider">Skor Evaluasi</th>
                            <th className="px-6 py-3.5 text-center text-[10px] font-extrabold text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3.5 text-center text-[10px] font-extrabold text-gray-500 uppercase tracking-wider">Sertifikat</th>
                            {role === 'EDITOR' && (
                              <th className="px-6 py-3.5 text-center text-[10px] font-extrabold text-gray-500 uppercase tracking-wider">Aksi</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200 text-xs font-medium text-gray-800">
                          {filteredMT.map(rec => (
                            <tr key={rec.id} className="hover:bg-slate-50/80 transition">
                              <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                                {rec.completionDate}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="font-bold text-gray-900">{rec.fullName}</div>
                                <div className="text-[10px] text-gray-500">NRP/NIK: {rec.nik}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                                <div className="font-semibold text-gray-900">{rec.position || '-'}</div>
                                <div className="text-[10px] text-gray-500">{rec.company}</div>
                                <div className="text-[10px] text-amber-700 font-bold">{rec.workArea || 'Yard Sukapura'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-md border border-amber-200 text-[11px]">
                                  {rec.moduleTitle}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center font-extrabold text-sm text-gray-900">
                                {rec.score} / 100
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                {rec.passed ? (
                                  <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-full text-[10px] font-extrabold border border-emerald-200">
                                    <CheckCircle className="w-3 h-3" />
                                    <span>LULUS</span>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 px-2.5 py-1 rounded-full text-[10px] font-extrabold border border-red-200">
                                    <AlertTriangle className="w-3 h-3" />
                                    <span>BELUM LULUS</span>
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                {rec.passed ? (
                                  <div className="flex items-center justify-center gap-1.5">
                                    <button
                                      onClick={() => openCertificatePreview('MANDATORY', rec)}
                                      className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 rounded-lg text-xs font-bold transition cursor-pointer shadow-2xs"
                                      title="Preview sertifikat di modal"
                                    >
                                      <Eye size={13} />
                                      <span>Preview</span>
                                    </button>
                                    <button
                                      onClick={() => generateMandatoryTrainingPDF(rec)}
                                      className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg text-xs font-bold transition cursor-pointer shadow-2xs"
                                      title="Download PDF sertifikat langsung"
                                    >
                                      <Download size={13} />
                                      <span>Download</span>
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-gray-400 italic">Tidak ada</span>
                                )}
                              </td>
                              {role === 'EDITOR' && (
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                  <button
                                    onClick={() => handleDeleteMTRecord(rec.id, rec.fullName, rec.moduleTitle)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition"
                                    title="Hapus Record"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}

                          {filteredMT.length === 0 && (
                            <tr>
                              <td colSpan={8} className="px-6 py-8 text-center text-gray-500 font-medium">
                                Tidak ada data riwayat mandatory training yang sesuai filter.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                </div>
              );
            })()}

            {/* PENGATURAN SPECIAL SAFETY MANDATORY TAB */}
            {activeTab === 'mandatory_settings' && (
              <div className="space-y-6 animate-fade-in-up">
                {/* Header Banner */}
                <div className="bg-gradient-to-r from-amber-700 via-orange-800 to-slate-900 rounded-2xl p-6 text-white shadow-lg border border-amber-600/30">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-200 text-xs font-semibold">
                        <Sliders className="w-3.5 h-3.5" />
                        <span>Pengaturan Khusus Admin</span>
                      </div>
                      <h2 className="text-xl sm:text-2xl font-extrabold text-white">
                        Pengaturan Halaman Safety Mandatory Training (7 Modul)
                      </h2>
                      <p className="text-amber-100/90 text-xs sm:text-sm">
                        Atur passing grade kelulusan, daftar area kerja standar (Yard Sukapura, Jababeka, Cakung), penandatangan sertifikat, dan modul aktif.
                      </p>
                    </div>

                    <button
                      onClick={handleSaveMandatorySettings}
                      disabled={role !== 'EDITOR' || mandatorySettingsSaving}
                      className="shrink-0 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-sm shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {mandatorySettingsSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Menyimpan...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Simpan Pengaturan</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Notifications */}
                {mandatorySettingsSuccess && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 animate-fade-in">
                    <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                    <span>{mandatorySettingsSuccess}</span>
                  </div>
                )}

                {mandatorySettingsError && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 animate-fade-in">
                    <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
                    <span>{mandatorySettingsError}</span>
                  </div>
                )}

                {role === 'VIEWER' && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl text-xs sm:text-sm font-medium flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>Mode Viewer (Hanya Baca): Anda membutuhkan hak akses EDITOR untuk mengubah dan menyimpan pengaturan ini.</span>
                  </div>
                )}

                <form onSubmit={handleSaveMandatorySettings} className="space-y-6">
                  {/* Grid Layout Cards */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    
                    {/* Card 1: Passing Grade & Validity */}
                    <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs space-y-5">
                      <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                        <div className="p-2 bg-amber-100 text-amber-700 rounded-xl">
                          <Award className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-base">Aturan Kelulusan & Masa Berlaku</h3>
                          <p className="text-xs text-gray-500">Kriteria skor minimum evaluasi post-test dan sertifikat</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1.5">
                            Skor Minimal Kelulusan (Passing Score %)
                          </label>
                          <select
                            value={mandatorySettings.passScore}
                            disabled={role !== 'EDITOR'}
                            onChange={(e) => setMandatorySettings(prev => ({ ...prev, passScore: Number(e.target.value) || 0 }))}
                            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold bg-white focus:ring-2 focus:ring-amber-500 outline-none"
                          >
                            <option value={60}>60% (Cukup)</option>
                            <option value={70}>70% (Standar UT - Rekomendasi)</option>
                            <option value={75}>75% (Tinggi)</option>
                            <option value={80}>80% (Sangat Ketat)</option>
                            <option value={85}>85%</option>
                            <option value={90}>90%</option>
                            <option value={100}>100% (Sempurna)</option>
                          </select>
                          <p className="text-[11px] text-gray-500 mt-1">
                            Pekerja yang memperoleh skor di bawah nilai ini dinyatakan GAGAL dan tidak berhak mendapatkan sertifikat digital.
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1.5">
                            Masa Berlaku Sertifikat Digital (Tahun)
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={5}
                            disabled={role !== 'EDITOR'}
                            value={mandatorySettings.certificateValidityYears}
                            onChange={(e) => setMandatorySettings(prev => ({ ...prev, certificateValidityYears: Number(e.target.value) || 1 }))}
                            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold bg-white focus:ring-2 focus:ring-amber-500 outline-none"
                          />
                          <p className="text-[11px] text-gray-500 mt-1">
                            Masa berlaku yang akan dicantumkan secara otomatis pada Sertifikat PDF hasil training.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Card 2: EHS Certificate Signatory Info */}
                    <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs space-y-5">
                      <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                        <div className="p-2 bg-blue-100 text-blue-700 rounded-xl">
                          <User className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-base">Penandatangan Sertifikat EHS</h3>
                          <p className="text-xs text-gray-500">Informasi pejabat/penanggung jawab di sertifikat PDF</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1.5">
                            Jabatan / Nama Head of EHS Department
                          </label>
                          <input
                            type="text"
                            disabled={role !== 'EDITOR'}
                            value={mandatorySettings.ehsManagerName}
                            onChange={(e) => setMandatorySettings(prev => ({ ...prev, ehsManagerName: e.target.value }))}
                            placeholder="Contoh: EHS Department Head - PT United Tractors Tbk"
                            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold bg-white focus:ring-2 focus:ring-amber-500 outline-none"
                          />
                          <p className="text-[11px] text-gray-500 mt-1">
                            Nama/Jabatan yang tercetak di kolom tanda tangan manajer EHS pada sertifikat.
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-700 uppercase mb-1.5">
                            Tim Instruktur / Pemateri Training Default
                          </label>
                          <input
                            type="text"
                            disabled={role !== 'EDITOR'}
                            value={mandatorySettings.instructorName}
                            onChange={(e) => setMandatorySettings(prev => ({ ...prev, instructorName: e.target.value }))}
                            placeholder="Contoh: Tim EHS Training PT United Tractors Tbk"
                            className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold bg-white focus:ring-2 focus:ring-amber-500 outline-none"
                          />
                          <p className="text-[11px] text-gray-500 mt-1">
                            Nama pemateri yang dicantumkan pada riwayat laporan dan sertifikat training.
                          </p>
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Card 3: Work Areas Management (Yard Sukapura, Jababeka, Cakung, dll.) */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs space-y-4">
                    <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 text-emerald-700 rounded-xl">
                          <MapPin className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-base">Pengelolaan Area Kerja Operasional</h3>
                          <p className="text-xs text-gray-500">Daftar lokasi area kerja yang dapat dipilih oleh peserta</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2 pt-1">
                        {mandatorySettings.allowedWorkAreas.map((area) => (
                          <div
                            key={area}
                            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-100 border border-slate-200 text-slate-800 text-xs font-bold"
                          >
                            <MapPin className="w-3.5 h-3.5 text-amber-600" />
                            <span>{area}</span>
                            {role === 'EDITOR' && (
                              <button
                                type="button"
                                onClick={() => handleDeleteMTWorkArea(area)}
                                className="text-gray-400 hover:text-red-600 transition ml-1 cursor-pointer"
                                title="Hapus Area Kerja"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      {role === 'EDITOR' && (
                        <div className="flex items-center gap-2 pt-2 max-w-md">
                          <input
                            type="text"
                            value={newMTSettingWorkArea}
                            onChange={(e) => setNewMTSettingWorkArea(e.target.value)}
                            placeholder="Tambah Area Kerja Baru (mis. Yard Marunda)..."
                            className="flex-1 px-3.5 py-2 border border-gray-300 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-amber-500 outline-none"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddMTWorkArea();
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={handleAddMTWorkArea}
                            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold flex items-center gap-1 cursor-pointer transition"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Tambah</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card 4: Status Aktif / Non-Aktif 7 Modul Safety Mandatory */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs space-y-4">
                    <div className="border-b border-gray-100 pb-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 text-amber-700 rounded-xl">
                          <GraduationCap className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-base">Status Pengaktifan Modul Mandatory (7 Modul Utama)</h3>
                          <p className="text-xs text-gray-500">Aktifkan atau non-aktifkan modul yang tersedia untuk peserta</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {MANDATORY_MODULES.map((mod) => {
                        const isActive = mandatorySettings.activeModules[mod.id] !== false;
                        const hasPdf = !!(mandatorySettings.modulePdfs?.[mod.id]);
                        const customQs = mandatorySettings.customQuestions?.[mod.id];
                        const qCount = (customQs && customQs.length >= 10) ? customQs.length : mod.questions.length;

                        return (
                          <div
                            key={mod.id}
                            className={`p-4 rounded-2xl border transition-all flex flex-col justify-between ${
                              isActive
                                ? 'bg-amber-50/40 border-amber-200'
                                : 'bg-gray-50 border-gray-200 opacity-60'
                            }`}
                          >
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-extrabold uppercase">
                                  {mod.badge}
                                </span>
                                <button
                                  type="button"
                                  disabled={role !== 'EDITOR'}
                                  onClick={() => handleToggleModuleActive(mod.id)}
                                  className={`px-3 py-1 rounded-full text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                                    isActive
                                      ? 'bg-emerald-600 text-white shadow-xs'
                                      : 'bg-gray-300 text-gray-700'
                                  }`}
                                >
                                  {isActive ? (
                                    <>
                                      <CheckCircle className="w-3.5 h-3.5" />
                                      <span>Aktif</span>
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="w-3.5 h-3.5" />
                                      <span>Non-Aktif</span>
                                    </>
                                  )}
                                </button>
                              </div>

                              <h4 className="font-bold text-gray-900 text-sm leading-snug">
                                {mod.title}
                              </h4>
                              <p className="text-xs text-gray-600 line-clamp-2">
                                {mod.description}
                              </p>
                            </div>

                            <div className="mt-4 pt-3 border-t border-gray-200/60 flex items-center justify-between text-[11px] text-gray-600 font-semibold">
                              <span className="flex items-center gap-1">
                                <FileText className={`w-3.5 h-3.5 ${hasPdf ? 'text-emerald-600' : 'text-gray-400'}`} />
                                <span>{hasPdf ? 'PDF Terlampir' : 'Slide Bawaan'}</span>
                              </span>
                              <span className="flex items-center gap-1">
                                <HelpCircle className="w-3.5 h-3.5 text-amber-600" />
                                <span>{qCount} Soal Evaluasi</span>
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Card 5: UPLOAD & LINK FILE PDF MATERI SLIDE PER MODUL */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs space-y-6">
                    <div className="border-b border-gray-100 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 text-purple-700 rounded-xl">
                          <FileUp className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-base">Upload / Link File PDF Materi Slide per Modul</h3>
                          <p className="text-xs text-gray-500">
                            Lampirkan file PDF materi presentasi atau gunakan file PDF di folder <code className="bg-gray-100 px-1 py-0.5 rounded text-amber-800 font-mono">public/modules/</code> agar peserta dapat mempelajari dokumen PDF secara langsung.
                          </p>
                        </div>
                      </div>

                      {role === 'EDITOR' && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleSaveAllModulePdfs}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                            title="Simpan seluruh tautan dan berkas PDF modul ke database Firebase"
                          >
                            <Save className="w-3.5 h-3.5" />
                            <span>Simpan Semua PDF Modul</span>
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      {MANDATORY_MODULES.map((mod) => {
                        const pdfVal = mandatorySettings.modulePdfs?.[mod.id] || '';
                        const isBase64 = pdfVal.startsWith('data:application/pdf');

                        return (
                          <div
                            key={mod.id}
                            className="p-4 rounded-2xl border border-gray-200 bg-slate-50/70 hover:bg-slate-50 transition space-y-3"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200/80 pb-2">
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 text-[10px] font-extrabold uppercase">
                                  {mod.badge}
                                </span>
                                <h4 className="font-bold text-gray-900 text-sm">{mod.title}</h4>
                              </div>

                              <div className="flex items-center gap-2">
                                {pdfVal ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                                    <FileCheck className="w-3.5 h-3.5" />
                                    <span>
                                      {isBase64 
                                        ? 'File PDF Diupload' 
                                        : (pdfVal.includes('drive.google.com') || pdfVal.includes('docs.google.com'))
                                        ? 'Google Drive Link Terpasang'
                                        : 'Link URL Terpasang'}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-200 text-gray-700 text-[11px] font-medium">
                                    <span>Menggunakan Slide Standar</span>
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-center">
                              {/* Link URL Input */}
                              <div className="lg:col-span-7 flex flex-col gap-1.5">
                                <div className="flex items-center gap-2">
                                  <div className="relative flex-1">
                                    <Link className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                                    <input
                                      type="text"
                                      disabled={role !== 'EDITOR'}
                                      value={isBase64 ? '[ File PDF Ter-upload ]' : pdfVal}
                                      onChange={(e) => handlePdfUrlChange(mod.id, e.target.value)}
                                      onBlur={() => handlePdfUrlBlurOrApply(mod.id, pdfVal)}
                                      placeholder="Tempel link Google Drive / PDF URL (cth: https://drive.google.com/file/d/.../view)"
                                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl text-xs font-semibold bg-white focus:ring-2 focus:ring-amber-500 outline-none disabled:bg-gray-100"
                                    />
                                  </div>
                                  {role === 'EDITOR' && (
                                    <button
                                      type="button"
                                      onClick={() => handlePdfUrlBlurOrApply(mod.id, SAFETY_INDUCTION_PDF_URL)}
                                      className="px-2.5 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-xl text-[11px] font-bold transition flex items-center gap-1 cursor-pointer shrink-0 shadow-2xs"
                                      title="Terapkan Link Google Drive Resmi UT dan Simpan ke Firebase"
                                    >
                                      <Link className="w-3 h-3 text-emerald-600" />
                                      <span>Link Drive UT</span>
                                    </button>
                                  )}
                                </div>
                                {(pdfVal.includes('drive.google.com') || pdfVal.includes('docs.google.com')) && (
                                  <span className="text-[10px] text-emerald-700 font-bold flex items-center gap-1 pl-1">
                                    <CheckCircle className="w-3 h-3 text-emerald-600" />
                                    <span>Link Google Drive terdeteksi. Otomatis dikonversi menjadi Live Preview tanpa terblokir.</span>
                                  </span>
                                )}
                              </div>

                              {/* Upload File & Actions */}
                              <div className="lg:col-span-5 flex flex-wrap items-center gap-2 justify-end">
                                {role === 'EDITOR' && (
                                  <>
                                    <label className="px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shrink-0 shadow-xs">
                                      <Upload className="w-3.5 h-3.5 text-amber-400" />
                                      <span>Upload PDF</span>
                                      <input
                                        type="file"
                                        accept="application/pdf"
                                        onChange={(e) => handlePdfFileUpload(mod.id, e)}
                                        className="hidden"
                                      />
                                    </label>

                                    <button
                                      type="button"
                                      onClick={() => handleSaveSingleModulePdf(mod.id, mod.title)}
                                      className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shrink-0 shadow-xs"
                                      title={`Simpan materi PDF modul ${mod.title}`}
                                    >
                                      <Save className="w-3.5 h-3.5" />
                                      <span>Simpan</span>
                                    </button>
                                  </>
                                )}

                                {pdfVal && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setMtPdfPreviewModuleTitle(mod.title);
                                        setMtPdfPreviewUrl(pdfVal);
                                      }}
                                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition flex items-center gap-1 cursor-pointer shrink-0"
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                      <span>Preview</span>
                                    </button>

                                    {role === 'EDITOR' && (
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteModulePdf(mod.id)}
                                        className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl text-xs font-bold transition flex items-center gap-1 cursor-pointer shrink-0"
                                        title="Hapus PDF"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        <span>Hapus</span>
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Card 6: KELOLA POST-TEST & SOAL EVALUASI SAFETY MANDATORY */}
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-xs space-y-6">
                    <div className="border-b border-gray-100 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 text-amber-800 rounded-xl">
                          <HelpCircle className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-base">Kelola Soal Post-Test & Evaluasi per Modul</h3>
                          <p className="text-xs text-gray-500">
                            Tambah, edit, atau hapus pertanyaan evaluasi post-test untuk masing-masing dari 7 modul keselamatan.
                          </p>
                        </div>
                      </div>

                      {role === 'EDITOR' && (
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            disabled={isSyncingQuestions}
                            onClick={handleSyncAllQuestionsToFirebase}
                            className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs disabled:opacity-60"
                            title="Simpan seluruh soal pertanyaan (91 soal) secara langsung ke Firebase Firestore"
                          >
                            {isSyncingQuestions ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            <span>{isSyncingQuestions ? 'Menyimpan...' : 'Simpan Semua Soal ke Firebase'}</span>
                          </button>
                          <button
                            type="button"
                            onClick={handleOpenAddMTQuestionModal}
                            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                          >
                            <Plus className="w-4 h-4" />
                            <span>Tambah Soal Evaluasi</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {syncSuccessMessage && (
                      <div className="p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-bold flex items-center gap-2 animate-fade-in">
                        <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span>{syncSuccessMessage}</span>
                      </div>
                    )}

                    {/* Module Selection Tabs for Questions */}
                    <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-gray-100">
                      {MANDATORY_MODULES.map((mod) => {
                        const isSelected = mtSelectedQuestionModule === mod.id;
                        const isCustom = !!(mandatorySettings.customQuestions?.[mod.id] && mandatorySettings.customQuestions[mod.id].length >= 10);
                        const listCount = getEffectiveMTQuestions(mod.id).length;

                        return (
                          <button
                            key={mod.id}
                            type="button"
                            onClick={() => setMtSelectedQuestionModule(mod.id)}
                            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition shrink-0 flex items-center gap-2 cursor-pointer ${
                              isSelected
                                ? 'bg-amber-600 text-white shadow-xs'
                                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                            }`}
                          >
                            <span>{mod.title}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                              isSelected ? 'bg-amber-800 text-amber-100' : 'bg-slate-200 text-slate-800'
                            }`}>
                              {listCount} Soal {isCustom ? '(Custom)' : ''}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Questions Table / List */}
                    {(() => {
                      const questionsList = getEffectiveMTQuestions(mtSelectedQuestionModule);
                      const isCustom = !!(mandatorySettings.customQuestions?.[mtSelectedQuestionModule] && mandatorySettings.customQuestions[mtSelectedQuestionModule].length >= 10);
                      const activeModInfo = MANDATORY_MODULES.find(m => m.id === mtSelectedQuestionModule);

                      return (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between text-xs font-semibold text-gray-600 px-1">
                            <span>
                              Modul Aktif: <strong className="text-gray-900">{activeModInfo?.title}</strong>
                            </span>
                            <span className="flex items-center gap-1.5">
                              Status: 
                              <strong className={isCustom ? 'text-amber-700 font-bold' : 'text-gray-700 font-bold'}>
                                {isCustom ? 'Soal Khusus Admin (Customized)' : 'Soal Standar Sistem'}
                              </strong>
                            </span>
                          </div>

                          {questionsList.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-2xl border border-gray-200 text-gray-500 text-xs font-semibold">
                              Belum ada soal evaluasi untuk modul ini. Silakan klik tombol "Tambah Soal Evaluasi".
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {questionsList.map((q, qIdx) => (
                                <div
                                  key={q.id || qIdx}
                                  className="p-4 rounded-2xl border border-gray-200 bg-white hover:border-amber-300 transition shadow-2xs space-y-3"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                      <span className="w-6 h-6 rounded-full bg-amber-600 text-white font-extrabold text-xs flex items-center justify-center shrink-0 mt-0.5">
                                        {qIdx + 1}
                                      </span>
                                      <div className="space-y-1">
                                        <h5 className="font-bold text-gray-900 text-xs sm:text-sm leading-snug">
                                          {q.text}
                                        </h5>
                                        {q.textEn && (
                                          <p className="text-gray-500 text-xs italic">{q.textEn}</p>
                                        )}
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                      <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold">
                                        {q.points || 20} Poin
                                      </span>

                                      {role === 'EDITOR' && (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => handleOpenEditMTQuestionModal(qIdx, q)}
                                            className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition cursor-pointer"
                                            title="Edit Soal"
                                          >
                                            <Pencil className="w-4 h-4" />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleDeleteMTQuestion(qIdx)}
                                            className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition cursor-pointer"
                                            title="Hapus Soal"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Options List */}
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 pl-9">
                                    {q.options.map((opt, optIdx) => {
                                      const isCorrect = optIdx === q.correctOptionIndex;
                                      return (
                                        <div
                                          key={optIdx}
                                          className={`px-3 py-2 rounded-xl text-xs font-medium border flex items-center gap-2 ${
                                            isCorrect
                                              ? 'bg-emerald-50 border-emerald-300 text-emerald-900 font-bold'
                                              : 'bg-gray-50 border-gray-200 text-gray-700'
                                          }`}
                                        >
                                          <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${
                                            isCorrect ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-700'
                                          }`}>
                                            {String.fromCharCode(65 + optIdx)}
                                          </span>
                                          <span className="truncate">{opt}</span>
                                          {isCorrect && (
                                            <CheckCircle className="w-3.5 h-3.5 text-emerald-600 ml-auto shrink-0" />
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Bottom Save Bar */}
                  <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                      disabled={role !== 'EDITOR' || mandatorySettingsSaving}
                      className="px-6 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      {mandatorySettingsSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Menyimpan Pengaturan...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Simpan Seluruh Pengaturan Mandatory</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* QUESTIONS TAB */}
            {activeTab === 'questions' && (
              <div className="bg-white shadow rounded-lg p-6 animate-fade-in-up">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  Manage Questions
                  {role === 'VIEWER' && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-normal">Read Only</span>}
                </h3>
                
                {role === 'EDITOR' ? (
                  <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-1 gap-3 mb-3">
                      <input 
                        type="text" 
                        value={newQText}
                        onChange={(e) => setNewQText(e.target.value)}
                        placeholder={editingQ ? "Update question (Bahasa Indonesia)..." : "Type question (Bahasa Indonesia)..."}
                        className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input 
                        type="text" 
                        value={newQTextEn}
                        onChange={(e) => setNewQTextEn(e.target.value)}
                        placeholder={editingQ ? "Update question (English)..." : "Type question (English)..."}
                        className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 italic"
                      />
                    </div>
                    
                    <div className="flex justify-end">
                      {editingQ ? (
                        <div className="flex gap-2">
                          <button onClick={() => { setEditingQ(null); setNewQText(''); setNewQTextEn(''); }} className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400">Cancel</button>
                          <button onClick={handleUpdateQuestion} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Update</button>
                        </div>
                      ) : (
                        <button onClick={handleAddQuestion} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2">
                          <Plus size={18} /> Add Question
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded text-sm">
                    You are viewing in <strong>Read-Only</strong> mode. Log in as an Editor to add or modify questions.
                  </div>
                )}

                <ul className="space-y-3">
                  {questions.map((q, idx) => (
                    <li key={q.id} className="flex justify-between items-start bg-white p-4 rounded border border-gray-200 shadow-sm">
                      <div className="flex-1 mr-4">
                        <span className="text-gray-900 font-medium block">{idx + 1}. {q.text}</span>
                        {q.textEn && <span className="text-gray-500 italic text-sm block mt-1">{q.textEn}</span>}
                      </div>
                      {role === 'EDITOR' && (
                        <div className="flex gap-2 shrink-0">
                          <button 
                            onClick={() => handleEditQuestion(q)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                            title="Edit"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button 
                            onClick={() => handleDeleteQuestion(q.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* POST-TEST TAB */}
            {activeTab === 'posttest' && (
              <div className="bg-white shadow rounded-lg p-6 animate-fade-in-up">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                      Manage Post-Test Questions
                      {role === 'VIEWER' && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-normal">Read Only</span>}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Jumlah pertanyaan: <span className="font-semibold text-gray-800">{postTestQuestions.length}</span>. 
                      Total poin: <span className="font-semibold text-gray-800">{postTestQuestions.reduce((sum, q) => sum + q.points, 0)} pts</span>.
                      (Syarat kelulusan ≥ 70)
                    </p>
                  </div>
                </div>

                {role === 'EDITOR' ? (
                  <div id="posttest-form-container" className={`mb-8 p-6 rounded-lg border transition-all duration-350 ${editingPT ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-200 shadow-md' : 'bg-gray-50 border-gray-200'}`}>
                    <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-pulse"></span>
                      {editingPT ? "Edit Pertanyaan Post-Test (Sedang Diedit) / Editing Post-Test Question" : "Tambah Pertanyaan Post-Test Baru / Add New Post-Test Question"}
                    </h4>
                    
                    <div className="grid grid-cols-1 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pertanyaan (Bahasa Indonesia) / Question Text (ID):</label>
                        <input 
                          type="text" 
                          value={newPTText}
                          onChange={(e) => setNewPTText(e.target.value)}
                          placeholder="Contoh: Apa kepanjangan dari APD?"
                          className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 font-medium"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Terjemahan Bahasa Inggris / English Translation (EN):</label>
                        <input 
                          type="text" 
                          value={newPTTextEn}
                          onChange={(e) => setNewPTTextEn(e.target.value)}
                          placeholder="Example: What does PPE stand for?"
                          className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800"
                        />
                      </div>

                      {/* Question Image Input */}
                      <div className="bg-white p-4 rounded-md border border-gray-200">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Gambar Pertanyaan / Question Image (Opsional / Optional):</label>
                        <div className="flex flex-col sm:flex-row gap-3 items-center">
                          <input 
                            type="file" 
                            accept="image/*"
                            onChange={(e) => processImageFile(e, setNewPTImageUrl)}
                            className="block text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                          />
                          <span className="text-gray-400 text-xs font-medium">atau / or</span>
                          <input 
                            type="text" 
                            value={newPTImageUrl}
                            onChange={(e) => setNewPTImageUrl(e.target.value)}
                            placeholder="Tempel URL Gambar / Paste Image URL..."
                            className="flex-1 text-xs border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800"
                          />
                        </div>
                        {newPTImageUrl && (
                          <div className="mt-3 relative w-32 h-32 border rounded overflow-hidden bg-gray-50 flex items-center justify-center">
                            <img src={newPTImageUrl} alt="Question Preview" className="max-w-full max-h-full object-contain" />
                            <button 
                              type="button"
                              onClick={() => setNewPTImageUrl('')}
                              className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 transition shadow duration-150"
                              title="Hapus Gambar"
                            >
                              <XCircle size={14} />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Options Input with Individual Images */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {newPTOptions.map((opt, i) => (
                          <div key={i} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm space-y-3">
                            <label className="block text-sm font-semibold text-gray-700">Pilihan {String.fromCharCode(65 + i)} (Option):</label>
                            <input
                              type="text"
                              value={opt}
                              onChange={(e) => {
                                const copy = [...newPTOptions];
                                copy[i] = e.target.value;
                                setNewPTOptions(copy);
                              }}
                              placeholder={`Pilihan ${String.fromCharCode(65 + i)}`}
                              className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-gray-800"
                            />
                            <div>
                              <label className="block text-xs text-gray-500 font-medium mb-1">Terjemahan Inggris / English Translation:</label>
                              <input
                                type="text"
                                value={newPTOptionsEn[i] || ''}
                                onChange={(e) => {
                                  const copy = [...newPTOptionsEn];
                                  copy[i] = e.target.value;
                                  setNewPTOptionsEn(copy);
                                }}
                                placeholder={`Option ${String.fromCharCode(65 + i)} (English)`}
                                className="w-full border border-gray-200 rounded-md p-1.5 focus:ring-blue-400 focus:border-blue-400 text-xs text-gray-600 italic"
                              />
                            </div>
                            
                            <div className="pt-1 border-t border-gray-100 flex flex-col gap-1.5">
                              <span className="text-xs text-gray-500 font-medium">Gambar Pilihan / Option Image (Opsional):</span>
                              <div className="flex gap-2 items-center">
                                <input 
                                  type="file" 
                                  accept="image/*"
                                  onChange={(e) => processImageFile(e, (base64) => {
                                    const copy = [...newPTOptionImages];
                                    copy[i] = base64;
                                    setNewPTOptionImages(copy);
                                  })}
                                  className="block w-1/2 text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 cursor-pointer"
                                />
                                <input 
                                  type="text" 
                                  value={newPTOptionImages[i] || ''}
                                  onChange={(e) => {
                                    const copy = [...newPTOptionImages];
                                    copy[i] = e.target.value;
                                    setNewPTOptionImages(copy);
                                  }}
                                  placeholder="Atau Tempel URL..."
                                  className="w-1/2 text-xs border border-gray-200 rounded p-1.5 text-gray-700"
                                />
                              </div>
                              {newPTOptionImages[i] && (
                                <div className="mt-1.5 relative w-20 h-20 border rounded bg-gray-50 flex items-center justify-center">
                                  <img src={newPTOptionImages[i]} alt="Option Preview" className="max-w-full max-h-full object-contain" />
                                  <button 
                                    type="button"
                                    onClick={() => {
                                      const copy = [...newPTOptionImages];
                                      copy[i] = '';
                                      setNewPTOptionImages(copy);
                                    }}
                                    className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-0.5 hover:bg-red-700 transition shadow"
                                    title="Hapus Gambar"
                                  >
                                    <XCircle size={12} />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Kunci Jawaban (Correct Option):</label>
                          <select
                            value={newPTCorrectIndex}
                            onChange={(e) => setNewPTCorrectIndex(Number(e.target.value))}
                            className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800"
                          >
                            {newPTOptions.map((opt, i) => (
                              <option key={i} value={i}>
                                Pilihan {String.fromCharCode(65 + i)} {opt.trim() ? `(${opt.substring(0, 30)}...)` : ''}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nilai / Points:</label>
                          <input 
                            type="number" 
                            value={newPTPoints}
                            onChange={(e) => setNewPTPoints(Number(e.target.value))}
                            min="0"
                            max="100"
                            className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2">
                      {editingPT ? (
                        <>
                          <button 
                            onClick={() => { 
                              setEditingPT(null); 
                              setNewPTText(''); 
                              setNewPTOptions(['', '', '', '']);
                              setNewPTOptionsEn(['', '', '', '']);
                              setNewPTCorrectIndex(0);
                              setNewPTPoints(10);
                              setNewPTImageUrl('');
                              setNewPTOptionImages(['', '', '', '']);
                            }} 
                            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 text-sm font-medium"
                          >
                            Batal
                          </button>
                          <button onClick={handleUpdatePostTestQuestion} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-medium">
                            Simpan Perubahan
                          </button>
                        </>
                      ) : (
                        <button onClick={handleAddPostTestQuestion} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex items-center gap-2">
                          <Plus size={16} /> Tambah Pertanyaan
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded text-sm">
                    Anda berada dalam mode **Read-Only**. Masuk sebagai Editor untuk mengatur pertanyaan post-test.
                  </div>
                )}

                <div className="space-y-4">
                  {postTestQuestions.map((q, idx) => (
                    <div key={q.id} className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm relative">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 mr-4">
                          <span className="text-gray-900 font-semibold block text-base leading-relaxed">
                            {idx + 1}. {q.text}
                          </span>
                          {q.textEn && (
                            <span className="text-gray-500 italic block text-xs mt-1 pl-4">
                              {q.textEn}
                            </span>
                          )}

                          {/* Question Image Rendering directly in the list */}
                          {q.imageUrl && (
                            <div className="mt-3 max-w-sm rounded-lg overflow-hidden border border-gray-200 bg-gray-50 p-1 inline-block">
                              <img src={q.imageUrl} alt={`Visual helper for question ${idx + 1}`} className="max-h-48 rounded object-contain" />
                            </div>
                          )}
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                            {q.options.map((opt, i) => (
                              <div 
                                key={i} 
                                className={`p-3 rounded-lg border text-sm flex flex-col gap-2 ${
                                  i === q.correctOptionIndex 
                                    ? 'bg-green-50 border border-green-200 text-green-800 font-medium' 
                                    : 'bg-gray-50 border border-gray-100 text-gray-600'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span>{String.fromCharCode(65 + i)}. {opt}</span>
                                    {q.optionsEn?.[i] && (
                                      <span className="block text-xs text-gray-400 italic mt-0.5">{q.optionsEn[i]}</span>
                                    )}
                                  </div>
                                  {i === q.correctOptionIndex && (
                                    <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded font-semibold whitespace-nowrap">
                                      Kunci Jawaban
                                    </span>
                                  )}
                                </div>

                                {/* Option image rendering directly under option */}
                                {q.optionImages?.[i] && (
                                  <div className="mt-1 max-w-32 border bg-white rounded p-1 inline-block overflow-hidden">
                                    <img src={q.optionImages[i]} alt={`Visual option ${String.fromCharCode(65 + i)}`} className="max-h-20 max-w-full object-contain" />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                          
                          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500 font-medium font-mono">
                            <span>Points: {q.points} pt</span>
                          </div>
                        </div>

                        {role === 'EDITOR' && (
                          <div className="flex gap-2 shrink-0">
                            <button 
                              onClick={() => handleEditPostTestQuestion(q)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                              title="Edit"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button 
                              onClick={() => handleDeletePostTestQuestion(q.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {postTestQuestions.length === 0 && (
                    <p className="text-center py-6 text-gray-500">Tidak ada pertanyaan post-test saat ini. Silakan buat satu.</p>
                  )}
                </div>
              </div>
            )}

            {/* ADMINS TAB */}
            {activeTab === 'admins' && (
              <div className="bg-white shadow rounded-lg p-6 animate-fade-in-up">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      Manage Admin Accounts / Kelola Akun Admin
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Daftar pengguna yang memiliki akses masuk ke halaman dashboard admin ini.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Add Admin Form */}
                  <div className="lg:col-span-1">
                    {role === 'EDITOR' ? (
                      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                        <h4 className="font-semibold text-gray-850 mb-4 flex items-center gap-2">
                          <UserPlus className="w-4 h-4 text-green-600" />
                          Daftarkan Admin Baru / Register New Admin
                        </h4>

                        <form onSubmit={handleRegisterAdmin} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email:</label>
                            <input 
                              type="email"
                              required
                              value={newAdminEmail}
                              onChange={(e) => setNewAdminEmail(e.target.value)}
                              placeholder="nama@unitedtractors.com"
                              className="w-full border border-gray-300 rounded-md p-2 bg-white text-sm text-gray-800 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Password (Min. 6 Karakter):</label>
                            <input 
                              type="password"
                              required
                              value={newAdminPassword}
                              onChange={(e) => setNewAdminPassword(e.target.value)}
                              placeholder="******"
                              className="w-full border border-gray-300 rounded-md p-2 bg-white text-sm text-gray-800 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Hak Akses / Role:</label>
                            <select
                              value={newAdminRole}
                              onChange={(e) => setNewAdminRole(e.target.value as UserRole)}
                              className="w-full border border-gray-300 rounded-md p-2 bg-white text-sm focus:ring-blue-500 focus:border-blue-500 block text-gray-800"
                            >
                              <option value="EDITOR">EDITOR (Akses Penuh / Full Access)</option>
                              <option value="VIEWER">VIEWER (Hanya Lihat / Read Only)</option>
                            </select>
                          </div>

                          {adminError && (
                            <div className="text-xs bg-red-50 border border-red-200 p-2.5 rounded-md font-medium text-red-600">
                              {adminError}
                            </div>
                          )}

                          {adminSuccess && (
                            <div className="text-green-700 text-xs bg-green-50 border border-green-200 p-2.5 rounded-md font-medium">
                              {adminSuccess}
                            </div>
                          )}

                          <button
                            type="submit"
                            disabled={adminSaving}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-md transition text-sm flex justify-center items-center gap-2 disabled:opacity-50 cursor-pointer text-center"
                          >
                            {adminSaving ? 'Mendaftarkan...' : 'Daftarkan Akun'}
                          </button>
                        </form>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-100 text-blue-800 p-4 rounded-lg text-xs leading-relaxed">
                        <p className="font-semibold mb-1">Akses Terbatas (Read Only):</p>
                        Anda login dengan hak akses <strong>VIEWER</strong>. Anda dapat melihat daftar administrator terdaftar tetapi tidak dapat meregistrasi admin baru.
                      </div>
                    )}
                  </div>

                  {/* Admin List */}
                  <div className="lg:col-span-2">
                    <h4 className="font-semibold text-gray-850 mb-4 flex items-center gap-2">
                      <Users size={18} className="text-gray-600" />
                      Daftar Akun Admin Terdaftar ({adminsList.length})
                    </h4>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm bg-white">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Hak Akses / Role</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Terdaftar Tanggal</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">UID</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200 text-sm">
                          {adminsList.map((admin, idx) => (
                            <tr key={admin.id || admin.email} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap text-gray-400 font-mono text-xs">{idx + 1}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-900 font-medium">{admin.email}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                  admin.role === 'EDITOR'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-amber-100 text-amber-800'
                                }`}>
                                  {admin.role === 'EDITOR' ? (
                                    <><Shield size={10} /> EDITOR (Akses Penuh)</>
                                  ) : (
                                    <><Eye size={10} /> VIEWER (Read Only)</>
                                  )}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs font-mono">
                                {admin.createdAt ? new Date(admin.createdAt).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-400 text-[10px] font-mono max-w-[120px] truncate" title={admin.id}>
                                {admin.id ? admin.id.substring(0, 12) + '...' : '-'}
                              </td>
                            </tr>
                          ))}
                          {adminsList.length === 0 && (
                            <tr>
                              <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                                Belum ada akun admin yang tercatat di database.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* INSTRUCTORS MANAGEMENT TAB */}
            {activeTab === 'instructors' && (
              <div className="bg-white shadow rounded-lg p-6 animate-fade-in-up">
                <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-gray-100 pb-5 mb-6 gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-blue-600" />
                      Kelola Area & Pemberi Materi / Manage Areas & Instructors
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Konfigurasikan pembagian wilayah kerja (Area) beserta personel pemberi materi keselamatan (ESR Officer) di masing-masing area.
                    </p>
                  </div>

                  {role === 'EDITOR' && (
                    <button
                      onClick={handleSaveInstructorConfig}
                      disabled={instructorMapSaving}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-5 rounded-lg text-sm shadow-md transition-all flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                    >
                      {instructorMapSaving ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      <span>Simpan Semua Perubahan</span>
                    </button>
                  )}
                </div>

                {/* Notifications */}
                {instructorConfigSuccess && (
                  <div className="mb-5 bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg text-sm font-medium flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>{instructorConfigSuccess}</span>
                  </div>
                )}
                {instructorConfigError && (
                  <div className="mb-5 bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg text-sm font-medium flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span>{instructorConfigError}</span>
                  </div>
                )}

                {/* Info Disclaimer */}
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-100 rounded-lg text-xs leading-relaxed text-yellow-800">
                  <span className="font-bold uppercase tracking-tight block mb-1">💡 Petunjuk Penggunaan / Directions:</span>
                  Semua operasi penambahan, perubahan nama, maupun penghapusan wilayah/personel pemateri di bawah ini disimpan secara internal di halaman Anda terlebih dahulu. 
                  Anda <strong>wajib</strong> mengklik tombol <strong>"Simpan Semua Perubahan"</strong> di kanan atas agar pengaturan baru ini aktif dan dapat langsung diakses oleh peserta di halaman utama.
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Left Panel: Area List & Instructors list */}
                  <div className="lg:col-span-2 space-y-6">
                    <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                      <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700 mb-4">
                        Pilih & Tinjau Area Kerja (Selected Work Area)
                      </h4>

                      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                        <div className="flex-1">
                          <select
                            value={selectedConfigArea}
                            onChange={(e) => {
                              setSelectedConfigArea(e.target.value);
                              setEditingInstructorIndex(null);
                            }}
                            className="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-sm font-semibold text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
                          >
                            {areasList.length === 0 ? (
                              <option value="">-- Belum ada Area --</option>
                            ) : (
                              areasList.map((area) => (
                                <option key={area} value={area}>
                                  {area}
                                </option>
                              ))
                            )}
                          </select>
                        </div>

                        {selectedConfigArea && role === 'EDITOR' && (
                          <button
                            type="button"
                            onClick={() => handleDeleteArea(selectedConfigArea)}
                            className="bg-red-50 hover:bg-red-100 text-red-700 font-semibold border border-red-200 px-4 py-2.5 rounded-lg text-xs flex items-center justify-center gap-1.5 transition cursor-pointer"
                          >
                            <Trash2 size={14} /> Hapus Area ini
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Instructors list */}
                    {selectedConfigArea ? (
                      <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        <div className="bg-gray-100 px-5 py-3 border-b border-gray-200 flex justify-between items-center">
                          <span className="font-bold text-xs text-gray-650 uppercase tracking-wider">
                            Daftar Pemberi Materi di "{selectedConfigArea}"
                          </span>
                          <span className="bg-blue-100 text-blue-805 font-bold text-xs px-2.5 py-0.5 rounded-full">
                            {(instructorMap[selectedConfigArea] || []).length} Personel
                          </span>
                        </div>

                        <div className="divide-y divide-gray-150 bg-white">
                          {(instructorMap[selectedConfigArea] || []).map((name, idx) => (
                            <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                              {editingInstructorIndex === idx ? (
                                <div className="flex items-center gap-2 w-full">
                                  <input
                                    type="text"
                                    value={editingInstructorName}
                                    onChange={(e) => setEditingInstructorName(e.target.value)}
                                    className="flex-1 border border-gray-300 rounded-lg p-2 bg-white text-sm font-semibold outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Ubah nama pemateri..."
                                    autoFocus
                                  />
                                  <button
                                    onClick={handleUpdateInstructorName}
                                    className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-3 py-2 text-xs font-semibold cursor-pointer"
                                  >
                                    Update
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingInstructorIndex(null);
                                      setEditingInstructorName('');
                                    }}
                                    className="bg-gray-250 border border-gray-300 hover:bg-gray-300 text-gray-700 rounded-lg px-3 py-2 text-xs font-medium cursor-pointer"
                                  >
                                    Batal
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-2 text-gray-850 font-medium text-sm">
                                    <User size={15} className="text-gray-400" />
                                    <span>{name}</span>
                                  </div>

                                  {role === 'EDITOR' && (
                                    <div className="flex items-center gap-1.5">
                                      <button
                                        onClick={() => startEditingInstructor(idx, name)}
                                        className="text-gray-500 hover:text-blue-600 p-1.5 hover:bg-blue-50 rounded-lg transition"
                                        title="Ubah nama"
                                      >
                                        <Edit2 size={14} />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteInstructor(idx)}
                                        className="text-gray-505 hover:text-red-600 p-1.5 hover:bg-red-50 rounded-lg transition"
                                        title="Hapus"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ))}

                          {(instructorMap[selectedConfigArea] || []).length === 0 && (
                            <div className="p-8 text-center text-gray-400 text-sm">
                              Belum ada nama pemberi materi terdaftar di area ini. Silakan tambahkan satu di menu sebelah kanan.
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="p-10 border border-dashed border-gray-300 rounded-xl text-center text-gray-400 text-sm">
                        Belum ada wilayah kerja (Area) terpilih. Silakan buat wilayah kerja baru terlebih dahulu menggunakan panel di sebelah kanan.
                      </div>
                    )}
                  </div>

                  {/* Right Panel: Add Forms */}
                  <div className="space-y-6 lg:col-span-1">
                    {/* Form 1: Add New Area */}
                    <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                      <h4 className="font-bold text-gray-850 text-sm mb-4 flex items-center gap-1.5">
                        <Plus className="w-4 h-4 text-green-600" />
                        Tambah Area Baru / Add Area
                      </h4>

                      {role === 'EDITOR' ? (
                        <form onSubmit={handleAddArea} className="space-y-3.5">
                          <div>
                            <input
                              type="text"
                              value={newAreaInput}
                              onChange={(e) => setNewAreaInput(e.target.value)}
                              placeholder="Misal: Yard Balikpapan"
                              className="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-sm text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                          </div>

                          <button
                            type="submit"
                            disabled={!newAreaInput.trim()}
                            className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-xs tracking-wider transition-colors cursor-pointer text-center"
                          >
                            TAMBAH AREA / ADD
                          </button>
                        </form>
                      ) : (
                        <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 p-3 rounded-lg leading-relaxed">
                          Hanya akun administrator berhak-akses <strong>EDITOR</strong> yang diperbolehkan menambah area baru.
                        </p>
                      )}
                    </div>

                    {/* Form 2: Add Instructor to selected area */}
                    <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                      <h4 className="font-bold text-gray-850 text-sm mb-3.5 flex items-center gap-1.5">
                        <Plus className="w-4 h-4 text-green-600" />
                        Tambah Pemateri / Add Personnel
                      </h4>

                      {role === 'EDITOR' ? (
                        selectedConfigArea ? (
                          <form onSubmit={handleAddInstructor} className="space-y-3.5">
                            <div className="bg-blue-50 border border-blue-200 p-2.5 rounded-md text-xs leading-none">
                              <span className="text-gray-500 block mb-1 uppercase text-[10px] tracking-tight font-bold">Akan Ditambahkan ke Area:</span>
                              <span className="font-bold text-blue-900 text-sm">{selectedConfigArea}</span>
                            </div>

                            <div>
                              <input
                                type="text"
                                value={newInstructorInput}
                                onChange={(e) => setNewInstructorInput(e.target.value)}
                                placeholder="Nama Lengkap Personel (Misal: Sarno)"
                                className="w-full border border-gray-300 rounded-lg p-2.5 bg-white text-sm text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 outline-none"
                              />
                            </div>

                            <button
                              type="submit"
                              disabled={!newInstructorInput.trim()}
                              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg text-xs tracking-wider transition-colors cursor-pointer text-center"
                            >
                              TAMBAH PEMATERI / ADD
                            </button>
                          </form>
                        ) : (
                          <p className="text-xs text-gray-450 italic leading-normal">
                            Silakan buat atau pilih terlebih dahulu wilayah kerja (Area) di atas untuk menambahkan personel pemateri keselamatan yang relevan.
                          </p>
                        )
                      ) : (
                        <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 p-3 rounded-lg leading-relaxed">
                          Hanya akun administrator berhak-akses <strong>EDITOR</strong> yang diperbolehkan mendaftarkan nama pemberi materi keselamatan baru.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'expiring' && (
              <div className="space-y-6 animate-fade-in-up">
                {/* Header Section */}
                <div className="bg-white p-6 rounded-xl border border-gray-150 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      Status Masa Berlaku Safety Induction / Expiration Tracker
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Masa berlaku safety induction adalah <strong className="text-gray-800">1 tahun (365 hari)</strong> sejak tanggal induksi keselamatan dilaksanakan.
                    </p>
                  </div>
                </div>

                {/* Filter and Search controls */}
                <div className="bg-white p-5 rounded-xl border border-gray-150 shadow-sm space-y-4">
                  <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    {/* Segmented Filter Buttons */}
                    <div className="inline-flex rounded-lg shadow-sm w-full md:w-auto overflow-hidden border border-gray-200" role="group">
                      <button
                        type="button"
                        onClick={() => setExpirFilterSelect('soon')}
                        className={`flex-1 md:flex-none px-4 py-2.5 text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                          expirFilterSelect === 'soon'
                            ? 'bg-amber-500 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Akan Expired ({totalExpiringSoon})
                      </button>
                      <button
                        type="button"
                        onClick={() => setExpirFilterSelect('expired')}
                        className={`flex-1 md:flex-none px-4 py-2.5 text-xs font-bold border-l border-r border-gray-200 flex items-center justify-center gap-2 transition-all cursor-pointer ${
                          expirFilterSelect === 'expired'
                            ? 'bg-red-650 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Sudah Expired ({totalExpired})
                      </button>
                      <button
                        type="button"
                        onClick={() => setExpirFilterSelect('all')}
                        className={`flex-1 md:flex-none px-4 py-2.5 text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                          expirFilterSelect === 'all'
                            ? 'bg-gray-800 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Users className="w-3.5 h-3.5" />
                        Semua ({records.length})
                      </button>
                    </div>

                    {/* Search Field */}
                    <div className="relative w-full md:w-80">
                      <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={expirSearchQuery}
                        onChange={(e) => setExpirSearchQuery(e.target.value)}
                        placeholder="Cari nama, perusahaan, area..."
                        className="w-full border border-gray-300 rounded-lg pl-9 pr-4 py-2.5 bg-white text-xs text-gray-800 focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Expiry Data List Table */}
                <div className="bg-white rounded-xl border border-gray-150 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    {(() => {
                      // Filter the record dataset based on states
                      let listSource = processedRecordsWithExpiry;
                      if (expirFilterSelect === 'soon') {
                        listSource = expiringSoonRecords;
                      } else if (expirFilterSelect === 'expired') {
                        listSource = expiredRecords;
                      }

                      // Apply search query
                      const query = expirSearchQuery.trim().toLowerCase();
                      if (query) {
                        listSource = listSource.filter(item => 
                          item.fullName.toLowerCase().includes(query) ||
                          (item.company || '').toLowerCase().includes(query) ||
                          (item.jobTitle || '').toLowerCase().includes(query) ||
                          (item.area || '').toLowerCase().includes(query)
                        );
                      }

                      if (listSource.length === 0) {
                        return (
                          <div className="py-16 text-center">
                            <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3 animate-pulse" />
                            <h4 className="text-sm font-bold text-gray-700">Tidak ada data safety induction ditemukan</h4>
                            <p className="text-xs text-gray-400 mt-1">Coba sesuaikan kata kunci pencarian atau filter status Anda.</p>
                          </div>
                        );
                      }

                      return (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-4.5 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Peserta / Participant</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Perusahaan & Jabatan</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Area Kerja</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Tanggal Induksi</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Tanggal Expired</th>
                              <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">Sisa Waktu</th>
                              <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-widest">Aksi / Follow Up</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {listSource.map((item) => {
                              const exp = item.expiryInfo;
                              const isExp = exp.status === 'expired';
                              const isSoon = exp.status === 'expiring_soon';

                              // Generate WhatsApp dynamic reminder text message
                              const reminderMsg = isExp 
                                ? `Halo Pak/Bu, menginfokan bahwa masa berlaku Safety Induction untuk:\n- Nama: ${item.fullName}\n- Perusahaan: ${item.company || '-'}\n- Wilayah Kerja: ${item.area}\nyang terdaftar pada tanggal ${item.inductionDate} TELAH EXPIRED pada tanggal ${exp.expiryDateStr}. Silakan segera mendaftar ulang uji keselamatan agar tetap valid bekerja.`
                                : `Halo Pak/Bu, menginfokan bahwa masa berlaku Safety Induction untuk:\n- Nama: ${item.fullName}\n- Perusahaan: ${item.company || '-'}\n- Wilayah Kerja: ${item.area}\nyang terdaftar pada tanggal ${item.inductionDate} AKAN SEGERA EXPIRED pada tanggal ${exp.expiryDateStr} (${exp.daysLeft} hari lagi). Silakan dijadwalkan pendaftarannya Kembali.`;

                              const handleCopyReminder = () => {
                                navigator.clipboard.writeText(reminderMsg);
                                setCopyFeedbackId(item.id);
                                setTimeout(() => setCopyFeedbackId(''), 2500);
                              };

                              return (
                                <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                                  <td className="px-6 py-4.5 whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-700 font-bold text-xs flex items-center justify-center uppercase shadow-inner">
                                        {item.fullName.slice(0, 2)}
                                      </div>
                                      <div>
                                        <div className="text-sm font-bold text-gray-900">{item.fullName}</div>
                                        <div className="text-[10px] text-gray-400 font-mono">ID: {item.id.slice(0, 8)}...</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-xs font-semibold text-gray-700">{item.company || 'Pribadi / Umum'}</div>
                                    <div className="text-[11px] text-gray-400 mt-0.5">{item.jobTitle || 'N/A'}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-xs font-medium text-gray-850 bg-gray-100 px-2 py-1 rounded-md border border-gray-250">
                                      {item.area}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-650 font-medium">
                                    📅 {item.inductionDate}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-650 font-medium font-mono">
                                    📅 {exp.expiryDateStr}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {isExp ? (
                                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-red-700 bg-red-50 border border-red-150 px-2.5 py-1 rounded-full">
                                        <XCircle className="w-3.5 h-3.5 text-red-500" />
                                        Expired {Math.abs(exp.daysLeft)} Hari Lalu
                                      </span>
                                    ) : isSoon ? (
                                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-150 px-2.5 py-1 rounded-full">
                                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 animate-bounce" />
                                        {exp.daysLeft} Hari Lagi
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-green-700 bg-green-50 border border-green-150 px-2.5 py-1 rounded-full">
                                        <Shield className="w-3.5 h-3.5 text-green-500" />
                                        Valid ({exp.daysLeft} Hari)
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <div className="flex items-center justify-center gap-2">
                                      {/* Copy template notification text or WhatsApp link */}
                                      <button
                                        onClick={handleCopyReminder}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer ${
                                          copyFeedbackId === item.id
                                            ? 'bg-green-600 text-white'
                                            : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-150'
                                        }`}
                                      >
                                        <Save className="w-3.5 h-3.5" />
                                        {copyFeedbackId === item.id ? 'Tersalin!' : 'Salin Pesan'}
                                      </button>

                                      {/* WhatsApp Direct */}
                                      <a
                                        href={`https://wa.me/?text=${encodeURIComponent(reminderMsg)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-green-700 bg-green-50 hover:bg-green-100 border border-green-205 flex items-center gap-1"
                                        title="Kirim WhatsApp"
                                      >
                                        WhatsApp
                                      </a>

                                      {/* Certificate PDF download */}
                                      <button
                                        onClick={() => generateInductionPDF(item)}
                                        className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-gray-700 bg-gray-50 border border-gray-250 hover:bg-gray-100 flex items-center gap-1 cursor-pointer"
                                        title="Download PDF"
                                      >
                                        <Download className="w-3.5 h-3.5 text-gray-500" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                </div>

                {/* Info Advisory Panel */}
                <div className="bg-sky-50 border border-sky-150 p-4.5 rounded-xl flex gap-3 text-sky-900 leading-relaxed text-xs">
                  <div className="w-5 h-5 flex-shrink-0 bg-sky-500 text-white text-xs rounded-full flex items-center justify-center font-bold">i</div>
                  <div>
                    <h5 className="font-bold uppercase tracking-wider text-[10px]">Petunjuk Operator K3 / Safety Officer Guide</h5>
                    <p className="mt-1">
                      Kirimkan notifikasi draf pengingat keselamatan WhatsApp secara langsung dengan menekan tombol <strong>WhatsApp</strong> atau gunakan <strong>Salin Pesan</strong> untuk memodifikasi pesan pengingat pendaftaran ulang secara manual.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

      </main>

      {deleteConfirmRecordObj && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden transform transition-all scale-100">
            <div className="bg-red-50 border-b border-red-100 px-6 py-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-100 text-red-600">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-base">Konfirmasi Hapus Data</h3>
                <p className="text-red-500 text-xs font-semibold">Tindakan ini tidak dapat dibatalkan</p>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-slate-600 leading-relaxed">
                Apakah Anda benar-benar yakin ingin menghapus data hasil induksi keselamatan untuk partisipan berikut?
              </p>
              <div className="mt-4 bg-slate-50 border border-slate-150 rounded-lg p-3.5">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest leading-none">Nama Lengkap / Full Name</div>
                <div className="text-base font-bold text-slate-800 mt-1">{deleteConfirmRecordObj.fullName}</div>
                
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest leading-none mt-3.5">ID Transaksi / Record ID</div>
                <div className="text-[10px] font-mono text-slate-500 mt-1 select-all break-all">{deleteConfirmRecordObj.id}</div>
              </div>
            </div>

            <div className="bg-slate-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setDeleteConfirmRecordObj(null)}
                disabled={deleteConfirmLoading}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 bg-white hover:bg-slate-100 transition disabled:opacity-55"
              >
                Batal / Cancel
              </button>
              <button
                type="button"
                onClick={executeDeleteRecord}
                disabled={deleteConfirmLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition flex items-center gap-2 disabled:opacity-55"
              >
                {deleteConfirmLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Menghapus...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Hapus Permanen</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmMTObj && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden transform transition-all scale-100">
            <div className="bg-red-50 border-b border-red-100 px-6 py-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-100 text-red-600">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-base">Konfirmasi Hapus Mandatory Training</h3>
                <p className="text-red-500 text-xs font-semibold">Tindakan ini tidak dapat dibatalkan</p>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-slate-600 leading-relaxed">
                Apakah Anda benar-benar yakin ingin menghapus data hasil Mandatory Training berikut?
              </p>
              <div className="mt-4 bg-slate-50 border border-slate-150 rounded-lg p-3.5 space-y-2">
                <div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Nama Pekerja</div>
                  <div className="text-sm font-bold text-slate-800">{deleteConfirmMTObj.fullName}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Modul Wajib</div>
                  <div className="text-xs font-bold text-amber-700">{deleteConfirmMTObj.moduleTitle}</div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setDeleteConfirmMTObj(null)}
                disabled={deleteConfirmMTLoading}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 bg-white hover:bg-slate-100 transition disabled:opacity-55"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={executeDeleteMTRecord}
                disabled={deleteConfirmMTLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition flex items-center gap-2 disabled:opacity-55"
              >
                {deleteConfirmMTLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Menghapus...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Hapus Record</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedRecordForDetails && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full border border-slate-200 overflow-hidden transform transition-all scale-100 flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-sm flex items-center justify-center uppercase shadow-inner">
                  {selectedRecordForDetails.fullName.slice(0, 2)}
                </div>
                <div>
                  <h3 className="font-bold text-base md:text-lg leading-tight">Detail Hasil Induksi: {selectedRecordForDetails.fullName}</h3>
                  <p className="text-gray-400 text-xs mt-0.5 font-mono">ID: {selectedRecordForDetails.id}</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedRecordForDetails(null)}
                className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-gray-800 transition"
                aria-label="Tutup"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Content - Scrollable */}
            <div className="p-6 overflow-y-auto space-y-6 flex-grow bg-slate-50">
              {/* General Info Grid */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-150">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-2 mb-4">Informasi Peserta / Participant Info</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-xs text-gray-400 block font-medium">Tipe Pengunjung / Visitor Type</span>
                    <span className="font-bold text-gray-850">
                      {selectedRecordForDetails.visitorType === 'TAMU' ? 'Kunjungan Tamu / Guest' : 'Mekanik / Vendor / Pekerja Risiko Tinggi'}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block font-medium">Perusahaan / Vendor</span>
                    <span className="font-bold text-gray-850">{selectedRecordForDetails.company || 'Pribadi / Umum'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block font-medium">Jabatan / Job Title</span>
                    <span className="font-bold text-gray-855">{selectedRecordForDetails.jobTitle || '-'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block font-medium">Wilayah Kerja / Area</span>
                    <span className="font-bold text-gray-850">{selectedRecordForDetails.area}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block font-medium">Tanggal Induksi</span>
                    <span className="font-semibold text-gray-850">📅 {selectedRecordForDetails.inductionDate}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block font-medium">Masa Berlaku s/d</span>
                    <span className="font-semibold text-gray-855">📅 {getInductionExpiryStatus(selectedRecordForDetails.inductionDate).expiryDateStr}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block font-medium">Pemateri / Instruktur</span>
                    <span className="font-bold text-gray-850">{selectedRecordForDetails.instructor}</span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block font-medium">Nilai Post-Test</span>
                    <span className="font-bold text-gray-850">
                      {selectedRecordForDetails.visitorType === 'TAMU' 
                        ? 'Dilewati / Skipped' 
                        : selectedRecordForDetails.postTestScore !== undefined 
                          ? `${selectedRecordForDetails.postTestScore} / 100` 
                          : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block font-medium">Status Kelulusan</span>
                    <span className="mt-0.5 inline-block">
                      {selectedRecordForDetails.visitorType === 'TAMU' ? (
                        <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                          TAMU / GUEST
                        </span>
                      ) : selectedRecordForDetails.postTestPassed !== false ? (
                        <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-green-100 text-green-800 border border-green-200">
                          LULUS / PASSED
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-800 border border-red-200">
                          GAGAL / FAILED
                        </span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400 block font-medium">Aktivitas / Kegiatan</span>
                    <span className="font-semibold text-gray-850">{selectedRecordForDetails.activity || '-'}</span>
                  </div>
                </div>
              </div>

              {/* General safety comprehension checklist (Yes/No answers) */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-150">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-2 mb-4">
                  Bagian I: Checklist Pemahaman Umum Keselamatan (Induction Answers)
                </h4>
                {questions.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">Tidak ada daftar pertanyaan checklist pemahaman umum.</p>
                ) : (
                  <div className="space-y-3 mt-2">
                    {questions.map((q, idx) => {
                      const ans = selectedRecordForDetails.answers?.[q.id];
                      return (
                        <div key={q.id} className="flex items-start justify-between gap-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
                          <div className="text-xs md:text-sm text-gray-800 font-medium">
                            <span className="font-bold text-gray-900">{idx + 1}.</span> {q.text}
                            {q.textEn && <span className="block text-xs text-gray-400 italic font-normal mt-0.5">{q.textEn}</span>}
                          </div>
                          <div className="flex-shrink-0">
                            {ans === true ? (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 border border-green-150 px-2.5 py-1 rounded-full">
                                <CheckCircle size={12} className="text-green-600" />
                                YA / YES
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-50 border border-red-150 px-2.5 py-1 rounded-full">
                                <XCircle size={12} className="text-red-500" />
                                TIDAK / NO
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Post-Test Section */}
              <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-150">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 pb-2 mb-4">
                  Bagian II: Lembar Jawaban Post-Test ({
                    selectedRecordForDetails.visitorType === 'TAMU' 
                      ? 'Dilewati / Skipped (Tamu)' 
                      : selectedRecordForDetails.postTestScore !== undefined 
                        ? `Nilai: ${selectedRecordForDetails.postTestScore} / 100` 
                        : 'Belum melakukan Post-Test'
                  })
                </h4>
                {selectedRecordForDetails.visitorType === 'TAMU' ? (
                  <p className="text-xs text-gray-500 italic">Post-Test dilewati karena status pengunjung adalah Kunjungan Tamu.</p>
                ) : selectedRecordForDetails.postTestAnswers ? (
                  <div className="space-y-4.5 mt-2">
                    {postTestQuestions.map((q, idx) => {
                      const chosenIdx = selectedRecordForDetails.postTestAnswers?.[q.id];
                      const isCorrect = chosenIdx === q.correctOptionIndex;
                      
                      return (
                        <div key={q.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50/50 space-y-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="text-xs md:text-sm text-gray-800 font-bold">
                              <span>Pertanyaan {idx + 1}: </span>
                              <span className="font-semibold">{q.text}</span>
                              {q.textEn && <span className="block text-xs text-gray-400 italic font-normal mt-1">{q.textEn}</span>}
                            </div>
                            <div className="flex-shrink-0">
                              {isCorrect ? (
                                <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 border border-green-150 px-2.5 py-1 rounded-full">
                                  Benar (+{q.points} pt)
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700 bg-red-50 border border-red-150 px-2.5 py-1 rounded-full">
                                  Salah (0 pt)
                                </span>
                              )}
                            </div>
                          </div>

                          {q.imageUrl && (
                            <div className="mt-2 max-w-xs overflow-hidden rounded-lg border border-gray-200">
                              <img src={q.imageUrl} alt={`Pertanyaan ${idx + 1}`} className="w-full h-auto object-cover" referrerPolicy="no-referrer" />
                            </div>
                          )}

                          {/* Options list */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                            {q.options.map((opt, optIdx) => {
                              const isChosen = chosenIdx === optIdx;
                              const isCorrectOption = q.correctOptionIndex === optIdx;
                              
                              let optionStyle = "bg-white border-gray-200 text-gray-700";
                              let prefixIcon = null;

                              if (isChosen && isCorrectOption) {
                                optionStyle = "bg-green-50 border-green-300 text-green-900 font-semibold";
                                prefixIcon = <CheckCircle size={14} className="text-green-600 inline mr-1.5" />;
                              } else if (isChosen && !isCorrectOption) {
                                optionStyle = "bg-red-50 border-red-300 text-red-900 font-semibold";
                                prefixIcon = <XCircle size={14} className="text-red-500 inline mr-1.5" />;
                              } else if (isCorrectOption) {
                                optionStyle = "bg-green-50/50 border-green-200 text-green-800 italic";
                                prefixIcon = <CheckCircle size={14} className="text-green-500/70 inline mr-1.5" />;
                              }

                              return (
                                <div key={optIdx} className={`p-2.5 rounded-lg border text-xs flex items-center gap-1.5 ${optionStyle}`}>
                                  <div className="font-bold flex-shrink-0 uppercase bg-gray-100 text-gray-600 rounded-full w-5 h-5 flex items-center justify-center text-[10px] mr-1">
                                    {String.fromCharCode(65 + optIdx)}
                                  </div>
                                  <div className="flex-grow">
                                    {prefixIcon}
                                    <span>{opt}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 italic">Data jawaban post-test tidak tersedia untuk peserta ini.</p>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 px-6 py-4 flex items-center justify-between border-t border-slate-150 flex-shrink-0">
              <button
                type="button"
                onClick={() => setSelectedRecordForDetails(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 bg-white hover:bg-slate-100 transition"
              >
                Tutup / Close
              </button>

              {(selectedRecordForDetails.visitorType === 'TAMU' || selectedRecordForDetails.postTestPassed !== false) && (
                <button
                  type="button"
                  onClick={() => generateInductionPDF(selectedRecordForDetails)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition flex items-center gap-2 cursor-pointer"
                >
                  <Download size={16} />
                  <span>Lihat Sertifikat</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDIT / TAMBAH SOAL EVALUASI MANDATORY */}
      {mtQuestionModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-xl w-full border border-slate-200 overflow-hidden my-8">
            <div className="bg-amber-700 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <HelpCircle className="w-5 h-5 text-amber-300" />
                <h3 className="font-extrabold text-lg">
                  {mtEditingQuestionIndex !== null ? "Edit Soal Evaluasi Mandatory" : "Tambah Soal Evaluasi Baru"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setMtQuestionModalOpen(false)}
                className="text-amber-200 hover:text-white p-1 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Modul Safety
                </label>
                <input
                  type="text"
                  disabled
                  value={MANDATORY_MODULES.find(m => m.id === mtSelectedQuestionModule)?.title || mtSelectedQuestionModule}
                  className="w-full px-3.5 py-2 border border-gray-200 bg-gray-100 rounded-xl text-xs font-bold text-gray-700"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Pertanyaan (Bahasa Indonesia) *
                </label>
                <textarea
                  rows={3}
                  value={mtQuestionForm.text}
                  onChange={(e) => setMtQuestionForm(prev => ({ ...prev, text: e.target.value }))}
                  placeholder="Ketikkan isi pertanyaan evaluasi post-test..."
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Terjemahan Pertanyaan (English - Opsional)
                </label>
                <textarea
                  rows={2}
                  value={mtQuestionForm.textEn}
                  onChange={(e) => setMtQuestionForm(prev => ({ ...prev, textEn: e.target.value }))}
                  placeholder="Optional English translation for the question..."
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-xs font-medium text-gray-600 focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-2">
                  Pilihan Jawaban (Tandai Radio untuk Jawaban Benar) *
                </label>
                <div className="space-y-2.5">
                  {mtQuestionForm.options.map((opt, optIdx) => {
                    const isSelected = mtQuestionForm.correctOptionIndex === optIdx;
                    return (
                      <div
                        key={optIdx}
                        className={`flex items-center gap-2 p-2 rounded-xl border transition ${
                          isSelected ? 'border-emerald-500 bg-emerald-50/50' : 'border-gray-200'
                        }`}
                      >
                        <input
                          type="radio"
                          name="mtCorrectOption"
                          id={`mtOptionRadio_${optIdx}`}
                          checked={isSelected}
                          onChange={() => setMtQuestionForm(prev => ({ ...prev, correctOptionIndex: optIdx }))}
                          className="w-4 h-4 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                        />
                        <label
                          htmlFor={`mtOptionRadio_${optIdx}`}
                          className="w-6 h-6 rounded-full bg-slate-200 text-slate-800 text-xs font-extrabold flex items-center justify-center shrink-0 cursor-pointer"
                        >
                          {String.fromCharCode(65 + optIdx)}
                        </label>
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) => {
                            const newOpts = [...mtQuestionForm.options];
                            newOpts[optIdx] = e.target.value;
                            setMtQuestionForm(prev => ({ ...prev, options: newOpts }));
                          }}
                          placeholder={`Pilihan ${String.fromCharCode(65 + optIdx)}...`}
                          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-semibold focus:ring-2 focus:ring-amber-500 outline-none"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Bobot Nilai Poin
                </label>
                <input
                  type="number"
                  min={5}
                  max={100}
                  value={mtQuestionForm.points}
                  onChange={(e) => setMtQuestionForm(prev => ({ ...prev, points: Number(e.target.value) || 20 }))}
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-xs font-bold focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>
            </div>

            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setMtQuestionModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-bold text-gray-700 bg-white hover:bg-gray-100 transition"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleSaveMTQuestion}
                className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shadow-md transition flex items-center gap-1.5 cursor-pointer"
              >
                <Save className="w-4 h-4" />
                <span>Simpan Soal Evaluasi</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL PREVIEW PDF MATERI SLIDE */}
      {mtPdfPreviewUrl && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-fade-in">
          <div className="relative bg-slate-900 rounded-2xl shadow-2xl max-w-5xl w-full border border-slate-800 overflow-hidden flex flex-col h-[90vh]">
            {/* Modal Header */}
            <div className="bg-slate-900 text-white px-6 py-4 flex flex-wrap items-center justify-between gap-3 shrink-0 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-extrabold text-base text-white">Preview Dokumen PDF Materi Slide</h3>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-blue-500/20 text-blue-300 border border-blue-500/30 uppercase">
                      PDF Viewer
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {mtPdfPreviewModuleTitle
                      ? `Modul: ${mtPdfPreviewModuleTitle}`
                      : 'Melihat dokumen PDF materi slide sebelum diunduh'}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setMtPdfPreviewUrl(null)}
                className="text-slate-400 hover:text-white p-1.5 rounded-xl hover:bg-slate-800 transition cursor-pointer"
                title="Tutup Modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 bg-slate-950 p-2 overflow-hidden flex flex-col items-center justify-center relative">
              <PdfViewer
                url={mtPdfPreviewUrl}
                title="Dokumen PDF Materi Slide"
                height="100%"
                className="w-full h-full border-none shadow-none"
              />
            </div>

            {/* Modal Footer Controls */}
            <div className="bg-slate-900 border-t border-slate-800 px-6 py-3.5 flex flex-wrap items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-400">
                  Format: <strong className="text-white">Dokumen PDF Materi</strong>
                </span>
              </div>

              <div className="flex items-center gap-2.5">
                <a
                  href={mtPdfPreviewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold transition flex items-center gap-1.5 border border-slate-700 shadow-2xs"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Buka Tab Baru</span>
                </a>

                <a
                  href={mtPdfPreviewUrl}
                  download
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-extrabold transition flex items-center gap-1.5 shadow-md cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  <span>Download File PDF</span>
                </a>

                <button
                  type="button"
                  onClick={() => setMtPdfPreviewUrl(null)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL PREVIEW FORMAT SERTIFIKAT PDF */}
      {certPreviewModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-fade-in">
          <div className="relative bg-slate-900 rounded-2xl shadow-2xl max-w-5xl w-full border border-slate-800 overflow-hidden flex flex-col h-[90vh]">
            {/* Modal Header */}
            <div className="bg-slate-900 text-white px-6 py-4 flex flex-wrap items-center justify-between gap-3 shrink-0 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
                  <Award className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-extrabold text-base text-white">Preview Format Sertifikat Kelulusan</h3>
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-blue-500/20 text-blue-300 border border-blue-500/30 uppercase">
                      PDF Universal Canvas Preview
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {certPreviewRecord
                      ? `Peserta: ${certPreviewRecord.fullName} (${'nik' in certPreviewRecord ? `NIK: ${certPreviewRecord.nik}` : `Perusahaan: ${certPreviewRecord.company}`})`
                      : 'Melihat tampilan template sertifikat standar sebelum diunduh'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Type Selector Toggle Buttons */}
                <div className="inline-flex rounded-xl bg-slate-800 p-1 border border-slate-700">
                  <button
                    type="button"
                    onClick={() => openCertificatePreview('INDUCTION', certPreviewRecord && 'area' in certPreviewRecord ? certPreviewRecord : undefined)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                      certPreviewType === 'INDUCTION'
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Safety Induction
                  </button>
                  <button
                    type="button"
                    onClick={() => openCertificatePreview('MANDATORY', certPreviewRecord && 'moduleId' in certPreviewRecord ? certPreviewRecord : undefined)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                      certPreviewType === 'MANDATORY'
                        ? 'bg-amber-600 text-white shadow-xs'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Mandatory Training
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setCertPreviewModalOpen(false)}
                  className="text-slate-400 hover:text-white p-1.5 rounded-xl hover:bg-slate-800 transition cursor-pointer"
                  title="Tutup Modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Sub-Header Toolbar for Mandatory Modules if Mandatory preview */}
            {certPreviewType === 'MANDATORY' && (
              <div className="bg-slate-800 border-b border-slate-700 px-6 py-2.5 flex items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-amber-400 uppercase">Ganti Modul Mandatory:</span>
                  <select
                    value={certPreviewRecord && 'moduleId' in certPreviewRecord ? certPreviewRecord.moduleId : 'jsa'}
                    onChange={async (e) => {
                      const modId = e.target.value;
                      const foundMod = MANDATORY_MODULES.find(m => m.id === modId);
                      const modTitle = foundMod ? foundMod.title : 'Job Safety Analysis (JSA)';
                      const updatedRecord: MandatoryTrainingRecord = {
                        id: certPreviewRecord && 'id' in certPreviewRecord ? certPreviewRecord.id : 'SAMPLE-MT-2026',
                        fullName: certPreviewRecord?.fullName || 'Eko Prasetyo (Sample)',
                        nik: certPreviewRecord && 'nik' in certPreviewRecord ? certPreviewRecord.nik : '317101987654321',
                        company: certPreviewRecord?.company || 'PT United Tractors Tbk',
                        position: certPreviewRecord && 'position' in certPreviewRecord ? certPreviewRecord.position : 'Technician Leader',
                        workArea: certPreviewRecord && 'workArea' in certPreviewRecord ? certPreviewRecord.workArea : 'Yard Sukapura',
                        moduleId: modId,
                        moduleTitle: modTitle,
                        score: certPreviewRecord && 'score' in certPreviewRecord ? certPreviewRecord.score : 100,
                        passed: true,
                        completionDate: certPreviewRecord && 'completionDate' in certPreviewRecord ? certPreviewRecord.completionDate : new Date().toISOString().split('T')[0],
                        answers: {}
                      };
                      openCertificatePreview('MANDATORY', updatedRecord);
                    }}
                    className="border border-slate-600 rounded-lg px-3 py-1 bg-slate-900 text-xs font-bold text-white outline-none focus:ring-2 focus:ring-amber-500 cursor-pointer"
                  >
                    {MANDATORY_MODULES.map(m => (
                      <option key={m.id} value={m.id}>{m.badge} - {m.title}</option>
                    ))}
                  </select>
                </div>

                <div className="text-[11px] font-semibold text-slate-400">
                  Format A4 • PT United Tractors Tbk EHS Marketing Division
                </div>
              </div>
            )}

            {/* Modal Body - Live PDF Viewer */}
            <div className="flex-1 bg-slate-950 p-2 overflow-hidden flex flex-col items-center justify-center relative">
              {certPreviewLoading ? (
                <div className="flex flex-col items-center justify-center p-8 bg-slate-900 rounded-2xl border border-slate-800 shadow-lg space-y-3">
                  <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
                  <p className="text-xs font-bold text-slate-200">Memproses & Mengenerate Dokumentasi PDF Sertifikat...</p>
                </div>
              ) : certPreviewDataUri ? (
                <PdfViewer
                  url={certPreviewDataUri}
                  title={`Sertifikat Kelulusan - ${certPreviewRecord?.fullName || ''}`}
                  height="100%"
                  className="w-full h-full border-none shadow-none"
                />
              ) : (
                <div className="text-center p-6 text-slate-400">
                  <p className="text-sm font-semibold">Gagal memuat preview sertifikat.</p>
                </div>
              )}
            </div>

            {/* Modal Footer Controls */}
            <div className="bg-slate-900 border-t border-slate-800 px-6 py-3.5 flex flex-wrap items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-400">
                  Tipe Format: <strong className="text-white">{certPreviewType === 'INDUCTION' ? 'Safety Induction Certificate' : 'Mandatory Training Certificate'}</strong>
                </span>
              </div>

              <div className="flex items-center gap-2.5">
                {certPreviewDataUri && (
                  <a
                    href={certPreviewDataUri}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold transition flex items-center gap-1.5 border border-slate-700 shadow-2xs"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Buka Tab Baru</span>
                  </a>
                )}

                <button
                  type="button"
                  onClick={() => {
                    if (!certPreviewRecord) return;
                    if (certPreviewType === 'INDUCTION') {
                      generateInductionPDF(certPreviewRecord as InductionRecord);
                    } else {
                      generateMandatoryTrainingPDF(certPreviewRecord as MandatoryTrainingRecord);
                    }
                  }}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-extrabold transition flex items-center gap-1.5 shadow-md cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  <span>Download File PDF Sertifikat</span>
                </button>

                <button
                  type="button"
                  onClick={() => setCertPreviewModalOpen(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL BREAKDOWN PESERTA PER MODUL */}
      {moduleBreakdownModal && (() => {
        const passedRecs = mandatoryRecords
          .filter(r => r.moduleId === moduleBreakdownModal.moduleId && r.passed)
          .sort((a, b) => a.fullName.localeCompare(b.fullName));
        const failedRecs = mandatoryRecords
          .filter(r => r.moduleId === moduleBreakdownModal.moduleId && !r.passed)
          .sort((a, b) => a.fullName.localeCompare(b.fullName));

        return (
          <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-fade-in">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full border border-gray-200 overflow-hidden flex flex-col max-h-[85vh]">
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white px-6 py-4 flex items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl border border-white/30">
                    <BookOpen className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-extrabold text-base">Detail Pengerjaan Modul</h3>
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-white/20 text-white border border-white/30 uppercase">
                        {moduleBreakdownModal.moduleBadge}
                      </span>
                    </div>
                    <p className="text-xs text-amber-100 mt-0.5">{moduleBreakdownModal.moduleTitle}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setModuleBreakdownModal(null)}
                  className="text-amber-100 hover:text-white p-1.5 rounded-xl hover:bg-white/10 transition cursor-pointer"
                  title="Tutup"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Summary Stats */}
              <div className="bg-slate-50 border-b border-gray-200 px-6 py-3 flex items-center gap-4 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-xs font-extrabold border border-emerald-300">
                    <CheckCircle className="w-4 h-4" />
                  </span>
                  <span className="text-xs font-bold text-gray-700">
                    Lulus: <strong className="text-emerald-700">{passedRecs.length}</strong> peserta
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-100 text-red-700 text-xs font-extrabold border border-red-300">
                    <AlertTriangle className="w-4 h-4" />
                  </span>
                  <span className="text-xs font-bold text-gray-700">
                    Tidak Lulus: <strong className="text-red-700">{failedRecs.length}</strong> peserta
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-xs font-bold text-gray-500">
                    Total: <strong className="text-gray-800">{passedRecs.length + failedRecs.length}</strong> peserta
                  </span>
                </div>
              </div>

              {/* Body - Lists */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {/* Passed List */}
                <div>
                  <h4 className="text-xs font-extrabold text-emerald-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4" />
                    Daftar Peserta Lulus ({passedRecs.length})
                  </h4>
                  {passedRecs.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {passedRecs.map((rec, idx) => (
                        <div key={rec.id} className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                          <span className="text-[10px] font-extrabold text-emerald-600 w-5 text-center">{idx + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-gray-900 truncate">{rec.fullName}</p>
                            <p className="text-[10px] text-gray-500 truncate">
                              {rec.nik} • {rec.company} • {rec.workArea || 'Yard Sukapura'} • Skor: {rec.score}
                            </p>
                          </div>
                          <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-md shrink-0">LULUS</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">Belum ada peserta yang lulus modul ini.</p>
                  )}
                </div>

                {/* Failed List */}
                <div>
                  <h4 className="text-xs font-extrabold text-red-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4" />
                    Daftar Peserta Tidak Lulus ({failedRecs.length})
                  </h4>
                  {failedRecs.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {failedRecs.map((rec, idx) => (
                        <div key={rec.id} className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                          <span className="text-[10px] font-extrabold text-red-600 w-5 text-center">{idx + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-gray-900 truncate">{rec.fullName}</p>
                            <p className="text-[10px] text-gray-500 truncate">
                              {rec.nik} • {rec.company} • {rec.workArea || 'Yard Sukapura'} • Skor: {rec.score}
                            </p>
                          </div>
                          <span className="text-[10px] font-extrabold text-red-700 bg-red-100 px-1.5 py-0.5 rounded-md shrink-0">TIDAK LULUS</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">Tidak ada peserta yang tidak lulus modul ini.</p>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setModuleBreakdownModal(null)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pop-up Modal Status Simpan (Berhasil / Error dengan Kode Error) */}
      {saveStatusModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-gray-100 flex flex-col items-center text-center space-y-4 animate-in zoom-in-95 duration-200">
            {saveStatusModal.type === 'success' ? (
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shadow-inner">
                <CheckCircle className="w-9 h-9" />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-red-100 text-red-600 flex items-center justify-center shadow-inner">
                <AlertTriangle className="w-9 h-9" />
              </div>
            )}

            <div className="space-y-1.5 w-full">
              <h3 className={`text-lg font-black ${saveStatusModal.type === 'success' ? 'text-emerald-900' : 'text-red-900'}`}>
                {saveStatusModal.title}
              </h3>
              <p className="text-xs text-gray-600 font-medium px-2 leading-relaxed">
                {saveStatusModal.message}
              </p>
            </div>

            {saveStatusModal.type === 'error' && saveStatusModal.errorCode && (
              <div className="w-full bg-red-50 border border-red-200 rounded-xl p-3 text-left">
                <span className="text-[10px] font-black uppercase text-red-600 block mb-0.5">Kode Error / Detail:</span>
                <code className="text-xs font-mono font-bold text-red-800 break-all select-all">
                  {saveStatusModal.errorCode}
                </code>
              </div>
            )}

            <div className="w-full pt-2">
              <button
                type="button"
                onClick={() => setSaveStatusModal(prev => ({ ...prev, isOpen: false }))}
                className={`w-full py-2.5 rounded-xl text-xs font-bold text-white transition shadow-sm cursor-pointer ${
                  saveStatusModal.type === 'success'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="w-full text-center py-4 bg-gray-800 text-gray-400 text-xs border-t border-gray-750 mt-auto shrink-0 select-none font-medium">
        Aplikasi By IRL92
      </footer>
    </div>
  );
};